import "../debug/logBuffer.ts";
import * as THREE from "three/webgpu";
import { create } from "zustand";
import { parseBlendFileAsync } from "../blend-reader/parseBlendFileAsync.ts";
import type { BlendModifier } from "../blend-reader/types/BlendModifier.ts";
import { MODIFIER_DEFAULTS } from "../blend-reader/types/BlendModifier.ts";
import { setEnabled as setDebugLoggerEnabled } from "../debug/debugLogger.ts";
import { entityIdToStoreKey } from "../ecs/EntityId.ts";
import { computeSelectedCentroid, getRegistry } from "../ecs/queryStore.ts";
import { getBone, getBoneMap, getRestPose } from "../mannequin/boneRegistry.ts";
import { applyConstraint } from "../mannequin/constraints.ts";
import { captureChainState } from "../mannequin/ikChainState.ts";
import { findChainForBone } from "../mannequin/ikChains.ts";
import { setIKLogEnabled } from "../mannequin/ikDebugLog.ts";
import { solveJacobianIK } from "../mannequin/jacobianIK.ts";
import { MANNEQUIN_MODELS } from "../mannequin/registry.ts";
import { applySecondaryMotion } from "../mannequin/secondaryMotion.ts";
import { buildClothTopology } from "../modeling/clothTopology.ts";
import {
  cursorSpawnPosition,
  DEFAULT_CURSOR_3D,
} from "../modeling/cursor3d.ts";
import {
  editFaceSelectionWorldMedian,
  editSelectionWorldMedian,
  medianPoint,
  snapCursorToGridPoint,
  snapSelectionMedianToCursor,
  snapVerticesToCursor,
} from "../modeling/cursorOps.ts";
import { generateCurvePolylineGeometry } from "../modeling/curveGeometry.ts";
import { enforceHandleConstraintsBatch } from "../modeling/curvemath.ts";
import { buildCurveEditVertices } from "../modeling/curveOps.ts";
import {
  captureEditSelectionForRecall,
  checkerDeselectEditSelection,
  growEditSelection,
  hasAnySelection,
  hasVertexSelection,
  shrinkEditSelection,
} from "../modeling/editSelection.ts";
import { isKnownGeneratorId } from "../modeling/generators/registry.ts";
import { loadLibrary } from "../modeling/objectLibrary.ts";
import {
  DEFAULT_SETTLE_PARAMS,
  GravitySimCancelledError,
  type SettleOutput,
  settleParticlesAsync,
} from "../modeling/physicsSim.ts";
import {
  createDefaultCurveData,
  generatePrimitive,
} from "../modeling/primitives.ts";
import { createEmptySceneSketchState } from "../modeling/sketch.ts";
import {
  buildCollisionMeshes,
  disposeCollisionMeshes,
  raycastDownZ,
} from "../modeling/surfaceRaycast.ts";
import { topologyFaceCount, topologyFaceVerts } from "../modeling/topology.ts";
import type {
  CurveData,
  CurvePoint,
  MaterialDataBlock,
  PivotReference,
  SceneMaterial,
  SceneObject,
} from "../modeling/types.ts";
import {
  asLoopNormals,
  createDefaultMaterialSlots,
  createMaterialId,
  createSceneObjectId,
  DEFAULT_MATERIAL,
  EDGE_WIRE,
  EMPTY_RENDER_TRIANGLE_INDICES,
  isCurveObject,
  isLightObject,
} from "../modeling/types.ts";
import { DriverGraph } from "../scene-graph/DriverGraph.ts";
import { createSculptOps } from "../sculpt/sculptOps.ts";
import { pruneSceneSketch } from "../sketch/sceneSketch.ts";
import { createSceneSketchOps } from "../sketch/sceneSketchOps.ts";
import { showToast } from "../ui/toastBus.ts";
import { flushCameraSync } from "../viewport/cameraSync.ts";
import { startCameraTransition } from "../viewport/cameraTransition.ts";
import {
  pngToRaster,
  rasterToPng,
  rasterToThumbnailPng,
} from "../viewport/inking/canvas/rasterPng.ts";
import { clampRenderDim } from "../viewport/renderPresets.ts";
import {
  trySurfacePolyRedo,
  trySurfacePolyUndo,
} from "../viewport/surfacePolyUndoBridge.ts";
import {
  lockedOrthographicHalfHeight,
  viewLockBreakReason,
} from "../viewport/viewLock.ts";
import {
  recomputeLoopNormals,
  resolveAutoSmoothAngle,
} from "../wasm/normals.ts";
import {
  allBlendObjects,
  allShapeKeySets,
  findEntryByObjectName,
  generateBlendFileId,
} from "./blendFileQueries.ts";
import {
  buildObjectCollectionIndex,
  type CollectionMemberRejection,
  describeCollectionMemberRejection,
  getCollectionForObject,
  rejectCollectionMemberIfInvalid,
  removeCollectionMembers,
} from "./collectionGraph.ts";
import { createCollectionId } from "./collectionIds.ts";
import { regenerateCurveGeometryInMap } from "./curveRegen.ts";
import { computeFileHash } from "./fileHash.ts";
import {
  cloneGroupTransform,
  composeGroupTransform,
  decomposeWorldToLocalTransform,
  type EulerTransform,
  isIdentityGroupTransform,
} from "./groupTransform.ts";
import {
  DEFAULT_INKING_CANVAS_SETTINGS,
  DEFAULT_INKING_MODE_SETTINGS,
  DEFAULT_INKING_SETTINGS,
} from "./inkingDefaults.ts";
import { useLibraryStore } from "./libraryStore.ts";
import { clearLiveEditPreview } from "./liveEditPreview.ts";
import { getCachedLibraryMaterial } from "./materialLibraryCache.ts";
import { pruneUnusedMaterials } from "./materialOps.ts";
import {
  clampMemoryLimit,
  MEMORY_LIMIT_DEFAULT_BYTES,
  setMemoryLimit as setMemoryGovernorLimit,
} from "./memoryGovernor.ts";
import {
  filterExistingSelectionTargets,
  getSingleSelectionTarget,
  type ObjectSelectionTarget,
  objectSelectionTargetKey,
  resolveStoreKeySelectionTarget,
  selectionToLeafObjectIds,
} from "./objectSelection.ts";
import { saveScene, saveSessionState } from "./persistence.ts";
import {
  clampExportOverride,
  clampRenderSetting,
  clampViewportQuality,
  DEFAULT_EXPORT_QUALITY_OVERRIDES,
  DEFAULT_RENDER_RENDER_CONFIG,
  DEFAULT_VIEWPORT_QUALITY,
  DEFAULT_VIEWPORT_RENDER_CONFIG,
  type RenderConfig,
  type RenderScope,
  renderScopeFor,
  VIEW_MODES_BY_SCOPE,
} from "./renderConfig.ts";
import { exportMaterialToCloudLibrary } from "./saveMaterialToLibrary.ts";
import { getCurrentLibraryItemId } from "./saveToLibrary.ts";
import type { SceneObjectMap } from "./sceneObjectMap.ts";
import {
  addSceneObject,
  patchSceneObject,
  removeFromSceneMap,
} from "./sceneObjectMap.ts";
import { createCommandOps } from "./storeCommandOps/index.ts";
import { isDirtyTrackingSuppressed } from "./storeDirtyTracking.ts";
import { createEditOps } from "./storeEditOps/index.ts";
import { createGeneratorOps } from "./storeGeneratorOps.ts";
import {
  bakeTransformIntoVerticesPatch,
  createObjectOps,
  originToPositionPatch,
} from "./storeObjectOps.ts";
import { createSceneFileOps, generateSceneName } from "./storeSceneFileOps.ts";
import { createSurfaceOps } from "./storeSurfaceOps.ts";
import type {
  AppState,
  BlendFileEntry,
  CameraBookmark,
  Collection,
  DebugPanelId,
  GravityBakeResult,
  GroupTransform,
  InteractionMode,
  ReferenceLayer,
  SavedInkingView,
} from "./storeTypes.ts";
import { IDENTITY_GROUP_TRANSFORM } from "./storeTypes.ts";
import {
  applyObjectGeometrySnapshotRedo,
  applyObjectGeometrySnapshotUndo,
  applySculptDeltaRedo,
  applySculptDeltaUndo,
  applySelectionDeltaState,
  applySnapshot,
  applyVertexPositionDeltaRedo,
  applyVertexPositionDeltaUndo,
  captureSelectionDeltaState,
  captureSnapshot,
  isObjectGeometrySnapshotDelta,
  isSculptDelta,
  isSelectionDelta,
  isVertexPositionDelta,
  persistUndoBudget,
  pushSelectionUndo,
  UNDO_BUDGET_DEFAULT_BYTES,
  UNDO_BUDGET_MAX_BYTES,
  UNDO_BUDGET_MIN_BYTES,
  undoManager,
} from "./storeUndo.ts";

// Re-export types and undo utilities for backwards compatibility
export type {
  AppState,
  AxisConstraint,
  CameraBookmark,
  CameraProjection,
  CommandBuffer,
  CommandType,
  InteractionMode,
  ModifierOverride,
  ReferenceLayer,
  SavedInkingView,
  SculptBrushType,
  SculptModeState,
  TransformOrientation,
  UndoableSnapshot,
  ViewMode,
  WorkspaceMode,
} from "./storeTypes.ts";
export { getUndoManager } from "./storeUndo.ts";

/** Bit 0 of Blender's Object.restrictflag — hidden in viewport */
const OB_RESTRICT_VIEWPORT = 1;

/**
 * Undo/redo may restore a sketch mode whose `activeLayerId` was deleted or
 * never existed in the restored state. Pick the top layer (or empty
 * string) in that case so the layer panel and stroke capture paths find a
 * valid target.
 */
function healSketchModeActiveLayer(
  get: () => AppState,
  set: (partial: Partial<AppState>) => void,
): void {
  const pm = get().sketchMode;
  if (!pm) return;
  const layers = get().sketch.layers;
  if (layers.length === 0) return;
  if (layers.some((l) => l.id === pm.activeLayerId)) return;
  set({
    sketchMode: { ...pm, activeLayerId: layers[layers.length - 1].id },
  });
}

function sceneObjectEulerTransform(obj: SceneObject): EulerTransform {
  return {
    position: [...obj.position],
    rotation: [...obj.rotation],
    scale: [...obj.scale],
  };
}

function patchSceneObjectEulerTransform(
  sceneObjects: SceneObjectMap,
  objectId: string,
  transform: EulerTransform,
): SceneObjectMap {
  return patchSceneObject(sceneObjects, objectId, {
    position: transform.position,
    rotation: transform.rotation,
    scale: transform.scale,
  });
}

function worldTransformForCollectionMember(
  obj: SceneObject,
  collectionTransform: GroupTransform,
): EulerTransform {
  return composeGroupTransform(
    collectionTransform,
    sceneObjectEulerTransform(obj),
  );
}

function sceneObjectWorldTransform(
  state: Pick<AppState, "collections">,
  obj: SceneObject,
): EulerTransform {
  const collection = getCollectionForObject(state.collections, obj.id);
  return collection
    ? worldTransformForCollectionMember(obj, collection.transform)
    : sceneObjectEulerTransform(obj);
}

function patchSceneObjectWorldTransform(
  state: Pick<AppState, "sceneObjects" | "collections">,
  objectId: string,
  transform: EulerTransform,
): SceneObjectMap {
  const collection = getCollectionForObject(state.collections, objectId);
  return patchSceneObjectEulerTransform(
    state.sceneObjects,
    objectId,
    collection
      ? decomposeWorldToLocalTransform(collection.transform, transform)
      : transform,
  );
}

// ---------------------------------------------------------------------------
// Gravity bake helpers
// ---------------------------------------------------------------------------

/**
 * Hard upper bound on the number of frames captured per bake. Keeps the
 * memory footprint predictable for large cloths: at e.g. 10k verts and
 * 200 frames you land around 24 MB per object, which is roomy but not
 * unreasonable for a modelling-grade tool. The menu picks its stride so
 * a typical 400-substep run lands well under this cap.
 */
const GRAVITY_BAKE_MAX_FRAMES = 200;

/**
 * Substeps the solver continues running after settle detection, when
 * baking. Gives the Time slider a bit of post-settle motion to scrub —
 * cloth sway, rope pendulum overshoot — so the final third of the scrub
 * range doesn't look visually frozen. Pure non-bake calls still use 0.
 */
const GRAVITY_BAKE_SETTLE_EXTENSION = 30;

/**
 * Convert the solver's world-space Float64 trajectory into the object-
 * local Float32 frames the scrub path needs. Pinned verts snap back to
 * their baseline values so frame 0 matches the original pose exactly
 * and the Time slider returning to 0 doesn't drift through float-
 * roundtrip noise.
 */
function bakeFramesToLocalSpace(
  objectId: string,
  worldFrames: Float64Array,
  frameCount: number,
  vertexCount: number,
  pinned: Uint8Array,
  sourceVertices: Float32Array,
  inverseWorld: THREE.Matrix4,
): GravityBakeResult {
  const frameStride = vertexCount * 3;
  const out = new Float32Array(frameCount * frameStride);
  const tmp = new THREE.Vector3();
  let anyFree = false;
  for (let i = 0; i < vertexCount; i++) {
    if (!pinned[i]) {
      anyFree = true;
      break;
    }
  }
  for (let f = 0; f < frameCount; f++) {
    const fOff = f * frameStride;
    for (let i = 0; i < vertexCount; i++) {
      const vOff = i * 3;
      if (pinned[i]) {
        out[fOff + vOff] = sourceVertices[vOff];
        out[fOff + vOff + 1] = sourceVertices[vOff + 1];
        out[fOff + vOff + 2] = sourceVertices[vOff + 2];
      } else {
        tmp
          .set(
            worldFrames[fOff + vOff],
            worldFrames[fOff + vOff + 1],
            worldFrames[fOff + vOff + 2],
          )
          .applyMatrix4(inverseWorld);
        out[fOff + vOff] = tmp.x;
        out[fOff + vOff + 1] = tmp.y;
        out[fOff + vOff + 2] = tmp.z;
      }
    }
  }
  return { objectId, frames: out, frameCount, vertexCount, empty: !anyFree };
}

/**
 * Gather world-space sample points representing the surface of `obj` —
 * bone world positions for mannequins, transformed mesh vertices otherwise.
 * Returns an empty array for objects that don't drop (lights, curves, or
 * meshes with no vertex data). Used by Drop to pick the bottom-region
 * points that get raycast downward.
 */
function collectDropSamplePoints(
  state: Pick<AppState, "collections">,
  obj: SceneObject,
): THREE.Vector3[] {
  if (isLightObject(obj) || isCurveObject(obj)) return [];
  const points: THREE.Vector3[] = [];

  if (obj.objectKind === "mannequin") {
    const boneMap = getBoneMap(obj.id);
    if (!boneMap) return [];
    for (const bone of boneMap.values()) {
      const p = new THREE.Vector3();
      bone.getWorldPosition(p);
      points.push(p);
    }
    return points;
  }

  const verts = obj.vertices;
  if (!verts || verts.length === 0) return [];

  const worldTransform = sceneObjectWorldTransform(state, obj);
  const [rx, ry, rz] = worldTransform.rotation;
  const [sx, sy, sz] = worldTransform.scale;
  const mat = new THREE.Matrix4()
    .makeRotationFromEuler(new THREE.Euler(rx, ry, rz))
    .scale(new THREE.Vector3(sx, sy, sz));
  if (obj.originOffset) {
    const [ox, oy, oz] = obj.originOffset;
    mat.setPosition(ox, oy, oz);
  }
  const [px, py, pz] = worldTransform.position;
  for (let i = 0; i < verts.length; i += 3) {
    const v = new THREE.Vector3(verts[i], verts[i + 1], verts[i + 2]);
    v.applyMatrix4(mat);
    v.x += px;
    v.y += py;
    v.z += pz;
    points.push(v);
  }
  return points;
}

/**
 * Given world-space sample points, compute the surface Z the object
 * should land on (and the minZ it's falling from) so a caller can apply
 * the `surfaceZ - minZ` delta. The 5%-of-height threshold matches
 * Blender's "drop to ground" behaviour: only points near the bottom of
 * the object actually contact a surface.
 */
function computeDropDelta(
  points: THREE.Vector3[],
  excludeIds: ReadonlySet<string>,
  sceneObjects: ReadonlyMap<string, SceneObject>,
  collections: ReadonlyMap<string, Collection>,
): { minZ: number; surfaceZ: number } | null {
  if (points.length === 0) return null;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of points) {
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }
  if (!Number.isFinite(minZ)) return null;
  const threshold = minZ + Math.max((maxZ - minZ) * 0.05, 0.001);
  const bottomPoints = points.filter((p) => p.z <= threshold);
  const surfaceZ =
    raycastDownZ(bottomPoints, excludeIds, sceneObjects, collections) ?? 0;
  return { minZ, surfaceZ };
}

/**
 * Filter a list of candidate ids down to the ones that are legal
 * collection members, surfacing each rejection via toast. In dev builds
 * the first rejection additionally throws so programmer errors fail
 * loudly in tests.
 *
 * Rejected reasons (see `rejectCollectionMemberIfInvalid`):
 *  - `not-scene-object` / `blend-entry` / `nested-collection` — wrong
 *    namespace. Almost always a programmer bug: the UI should never hand
 *    these ids to collection actions.
 *  - `follow-constraint` — user-reachable. Surface a toast so the user
 *    understands why their drag-drop / context-menu action was rejected.
 */
function filterValidCollectionMembers(
  state: AppState,
  objectIds: readonly string[],
  opName: string,
): string[] {
  const valid: string[] = [];
  const rejections: CollectionMemberRejection[] = [];
  for (const id of objectIds) {
    const reason = rejectCollectionMemberIfInvalid(state, id);
    if (reason) {
      rejections.push(reason);
      continue;
    }
    valid.push(id);
  }
  if (rejections.length > 0) {
    const first = rejections[0];
    if (import.meta.env?.DEV && first.kind !== "follow-constraint") {
      throw new Error(
        `${opName}: ${describeCollectionMemberRejection(first)} ` +
          `(${rejections.length} of ${objectIds.length} rejected)`,
      );
    }
    for (const rejection of rejections) {
      showToast(describeCollectionMemberRejection(rejection), {
        duration: 4000,
      });
    }
  }
  return valid;
}

function withSelectedTargets(
  targets: readonly ObjectSelectionTarget[],
): Pick<AppState, "selectedObjectTargets"> {
  return { selectedObjectTargets: [...targets] };
}

function selectionTargetWorldPosition(
  state: Pick<AppState, "sceneObjects" | "collections" | "blendFiles">,
  target: ObjectSelectionTarget,
): [number, number, number] | null {
  switch (target.kind) {
    case "sceneObject": {
      const obj = state.sceneObjects.get(target.id);
      return obj ? sceneObjectWorldTransform(state, obj).position : null;
    }
    case "collection":
      return state.collections.get(target.id)?.transform.position ?? null;
    case "blendEntry":
      return state.blendFiles.get(target.id)?.groupTransform.position ?? null;
  }
}

/**
 * World-space center of the current selection, resolving the active edit
 * context in the same priority order as the 3D-cursor "snap to selection"
 * operator:
 *
 *  1. Pose mode with an active bone  → that bone's world head.
 *  2. Edit mode with a selection     → median of selected verts (world space).
 *  3. Object mode with a selection   → centroid of selected targets.
 *  4. Nothing selected               → null.
 *
 * Shared by the cursor snap path and the axis-view snap path, so that both
 * "C → Selection" and ` → Front frame the same point.
 */
export function getSelectionWorldCenter(
  state: AppState,
): [number, number, number] | null {
  // Pose mode: active bone world head.
  const pm = state.poseMode;
  if (pm?.activeBone) {
    const bone = getBone(pm.objectId, pm.activeBone);
    if (bone) {
      const world = new THREE.Vector3();
      bone.getWorldPosition(world);
      return [world.x, world.y, world.z];
    }
  }
  // Edit mode: median of selected vertices in world space.
  const em = state.editMode;
  if (em) {
    const obj = state.sceneObjects.get(em.objectId);
    if (obj) {
      const center = editSelectionWorldMedian(
        obj,
        em,
        getCollectionForObject(state.collections, em.objectId)?.transform,
      );
      if (center) return center;
    }
  }
  // Object mode: centroid of selected object targets.
  if (state.selectedObjectTargets.length > 0) {
    const positions = state.selectedObjectTargets
      .map((target) => selectionTargetWorldPosition(state, target))
      .filter((pos): pos is [number, number, number] => pos != null);
    if (positions.length > 0) return medianPoint(positions);
  }
  return null;
}

/**
 * Snapshot the active reference layer's `above` and `opacity`, then return
 * a partial state that forces both to "above + fully opaque" so the image
 * doesn't visually conflict with the 3D scene during alignment. The
 * snapshot is restored on exit via `exitAlignModeOverrides`.
 *
 * No-ops gracefully when there is no active layer.
 */
function enterAlignModeOverrides(state: AppState): Partial<AppState> {
  const layer = state.referenceLayers.find(
    (l) => l.id === state.activeReferenceLayerId,
  );
  if (!layer) return {};
  return {
    alignModeSavedLayerState: {
      layerId: layer.id,
      above: layer.above,
      opacity: layer.opacity,
    },
    referenceLayers: state.referenceLayers.map((l) =>
      l.id === layer.id ? { ...l, above: true, opacity: 1 } : l,
    ),
  };
}

/**
 * Restore the active reference layer's `above` and `opacity` from the
 * snapshot taken on entry to align mode, and clear the snapshot.
 */
function exitAlignModeOverrides(state: AppState): Partial<AppState> {
  const saved = state.alignModeSavedLayerState;
  if (!saved) return { alignModeSavedLayerState: null };
  return {
    alignModeSavedLayerState: null,
    referenceLayers: state.referenceLayers.map((l) =>
      l.id === saved.layerId
        ? { ...l, above: saved.above, opacity: saved.opacity }
        : l,
    ),
  };
}

let bookmarkCounter = 0;
let savedInkingCounter = 0;

/**
 * Pick the first unused "Inking N" label for a new saved view. Uses the lowest
 * free integer so that deleting #2 from a list of three makes the next save
 * take the #2 slot back instead of jumping to #4.
 */
function nextUniqueInkingName(existing: { name: string }[]): string {
  const used = new Set<number>();
  for (const v of existing) {
    const m = /^Inking\s+(\d+)$/.exec(v.name);
    if (m) used.add(Number(m[1]));
  }
  let n = 1;
  while (used.has(n)) n++;
  return `Inking ${n}`;
}

/** Auto-generate material names following Blender's .001 convention. */
function nextMaterialName(
  materials: Map<string, MaterialDataBlock>,
  base = "Material",
): string {
  const names = new Set([...materials.values()].map((m) => m.name));
  if (!names.has(base)) return base;
  for (let i = 1; ; i++) {
    const c = `${base}.${String(i).padStart(3, "0")}`;
    if (!names.has(c)) return c;
  }
}

/**
 * Apply a material property patch with linked-material propagation.
 *
 * Updates the source scene block, then — if the block carries a
 * libraryId — fans the same patch out to every other scene block that
 * references the same library item, updates the in-memory
 * SceneMaterial cache for that library id, and queues a debounced R2
 * upload of the new JSON. All linked users in the current session stay
 * field-level in sync; cross-device sync happens through the cloud
 * library on the next load.
 *
 * Callers: updateSceneObjectMaterial, updateMaterial. Never call this
 * from library-side edits — those propagate in the opposite direction
 * and avoid re-entering this helper.
 */
function propagateMaterialEdit(
  state: { materials: Map<string, MaterialDataBlock> },
  sourceBlock: MaterialDataBlock,
  patch: Partial<SceneMaterial>,
): { materials: Map<string, MaterialDataBlock> } {
  const mats = new Map(state.materials);
  mats.set(sourceBlock.id, {
    ...sourceBlock,
    properties: { ...sourceBlock.properties, ...patch },
  });
  return { materials: mats };
}

export const useStore = create<AppState>((set, get) => ({
  blendFiles: new Map(),
  driverGraph: null,
  selectedObjectTargets: [],
  collections: new Map(),
  visibility: new Map(),
  renderVisibility: new Map(),
  shadingOverrides: new Map(),
  shapeKeyWeights: new Map(),
  focalLength: 50,
  cameraPosition: [0, -3, 1] as [number, number, number],
  cameraTarget: [0, 0, 0.5] as [number, number, number],
  cameraUp: null as [number, number, number] | null,
  referenceLayers: [],
  activeReferenceLayerId: null,
  alignCameraDrawAxis: null,
  selectedVanishingLineId: null,
  alignModeSavedLayerState: null,
  mode: "navigate",
  orbitEnabled: true,
  singleDragPanEnabled: (() => {
    try {
      return localStorage.getItem("poseref-single-drag-pan-enabled") === "true";
    } catch {
      return false;
    }
  })(),
  spaceOrbitActive: false,
  boxSelectActive: false,
  brushSelectActive: false,
  surfaceTool: null,
  eyedropperSession: null,
  eyedropperHoveredId: null,
  transformMode: "translate",
  workspaceMode: "object",
  renderConfigs: {
    viewport: { ...DEFAULT_VIEWPORT_RENDER_CONFIG },
    render: { ...DEFAULT_RENDER_RENDER_CONFIG },
  },
  viewportQuality: { ...DEFAULT_VIEWPORT_QUALITY },
  exportQualityOverrides: { ...DEFAULT_EXPORT_QUALITY_OVERRIDES },
  showTextures: true,
  textureMapsEnabled: true,
  settingsOpen: false,
  settingsInitialTab: null,
  renderMethodModalOpen: false,
  generatorAdjusting: false,
  objectXray: false,
  gridLogDist: 1.0,
  showGrid: true,
  showAxisIndicator: true,
  showFrustum: false,
  renderWidth: 1920,
  renderHeight: 1080,
  renderTransparentBackground: true,
  inkingEnabled: false,
  inkingThickness: DEFAULT_INKING_SETTINGS.thickness,
  inkingThicknessMin: DEFAULT_INKING_SETTINGS.thicknessMin,
  inkingShadowResponse: DEFAULT_INKING_SETTINGS.shadowResponse,
  inkingShadowExposure: DEFAULT_INKING_SETTINGS.shadowExposure,
  inkingOpacity: DEFAULT_INKING_SETTINGS.opacity,
  inkingColor: "#000000",
  inkingIncludePrimitives: true,
  inkingIncludeMannequins: false,
  inkingIncludeBlendObjects: false,
  inkingLineWidthVariation: DEFAULT_INKING_SETTINGS.lineWidthVariation,
  inkingDetail: DEFAULT_INKING_SETTINGS.detail,
  inkingHatchingEnabled: false,
  inkingHatchingDirection: DEFAULT_INKING_SETTINGS.hatchingDirection,
  inkingHatchingExposure: DEFAULT_INKING_SETTINGS.hatchingExposure,
  inkingHatchingDensity: DEFAULT_INKING_SETTINGS.hatchingDensity,
  inkingHatchingJitter: DEFAULT_INKING_SETTINGS.hatchingJitter,
  inkingDistanceNear: DEFAULT_INKING_SETTINGS.distanceNear,
  inkingDistanceFar: DEFAULT_INKING_SETTINGS.distanceFar,
  inkingAutoGenerate: true,
  renderExportMode: "full" as import("./storeTypes.ts").RenderExportMode,
  renderGridOverride: "defer" as import("./storeTypes.ts").RenderGridOverride,
  cameraProjection: "perspective" as import("./storeTypes.ts").CameraProjection,
  viewLock: null as import("./storeTypes.ts").ViewLock | null,
  orthographicHalfHeight: 1,
  flyMode: false,
  uiScale: (() => {
    try {
      const v = Number(localStorage.getItem("poseref-ui-scale"));
      return v > 0 ? v : 1;
    } catch {
      return 1;
    }
  })(),
  orbitSensitivity: (() => {
    try {
      const v = Number(localStorage.getItem("poseref-orbit-sensitivity"));
      return v > 0 ? v : 1;
    } catch {
      return 1;
    }
  })(),
  panSensitivity: (() => {
    try {
      const v = Number(localStorage.getItem("poseref-pan-sensitivity"));
      return v > 0 ? v : 1.5;
    } catch {
      return 1.5;
    }
  })(),
  dollySensitivity: (() => {
    try {
      const v = Number(localStorage.getItem("poseref-dolly-sensitivity"));
      return v > 0 ? v : 1;
    } catch {
      return 1;
    }
  })(),
  invertOrbit: (() => {
    try {
      return localStorage.getItem("poseref-invert-orbit") === "true";
    } catch {
      return false;
    }
  })(),
  invertPan: (() => {
    try {
      return localStorage.getItem("poseref-invert-pan") === "true";
    } catch {
      return false;
    }
  })(),
  invertZoom: (() => {
    try {
      return localStorage.getItem("poseref-invert-zoom") === "true";
    } catch {
      return false;
    }
  })(),
  precisionLevel: 0,
  undoMemoryBudgetBytes: (() => {
    try {
      const raw = Number(localStorage.getItem("poseref-undo-budget-bytes"));
      if (
        Number.isFinite(raw) &&
        raw >= UNDO_BUDGET_MIN_BYTES &&
        raw <= UNDO_BUDGET_MAX_BYTES
      ) {
        return raw;
      }
    } catch {
      /* ignore */
    }
    return UNDO_BUDGET_DEFAULT_BYTES;
  })(),
  memoryLimitBytes: (() => {
    try {
      const raw = localStorage.getItem("poseref-memory-limit-bytes");
      if (raw !== null) {
        const n = Number(raw);
        // 0 is the legitimate "Off" value; preserve it explicitly.
        if (n === 0) return 0;
        if (Number.isFinite(n) && n > 0) return clampMemoryLimit(n);
      }
    } catch {
      /* ignore */
    }
    return MEMORY_LIMIT_DEFAULT_BYTES;
  })(),
  debugEnabled: (() => {
    try {
      const on = localStorage.getItem("poseref-debug-enabled") === "true";
      if (on) {
        setIKLogEnabled(true);
        setDebugLoggerEnabled(true);
      }
      return on;
    } catch {
      return false;
    }
  })(),
  debugMaterial: null,
  debugPanelOpen: (() => {
    const defaults: Record<DebugPanelId, boolean> = {
      debugOverlay: true,
      debugMaterial: true,
      bevelDebug: false,
      boneInfo: true,
      jointAngles: true,
      shapeKeyLayers: true,
      correctiveShapeKeys: false,
      poseCapture: false,
      topologyViz: true,
      shadingDebug: false,
      console: false,
      recorder: false,
      memoryProbe: false,
    };
    try {
      const raw = localStorage.getItem("poseref-debug-panel-open");
      if (raw) return { ...defaults, ...JSON.parse(raw) };
    } catch {
      /* ignore */
    }
    return defaults;
  })(),
  activeTopologyViz: null,
  primitiveSymmetryDebugAxis: null,
  debugDisabledShapeKeyLayers: new Set(),
  shadingDebugFlags: {
    screenSpaceNormals: false,
    blendedNormals: false,
    disableNormalMap: false,
    flatShading: false,
    normalDirectionViz: false,
    gpuNormalBufferViz: false,
    morphInfluenceHeatmap: false,
    normalMapViz: false,
    dqsSkinning: false,
    correctiveSmooth: false,
    boneInfluenceViz: false,
    uvIslandViz: false,
  },
  loading: false,
  error: null,
  cameraBookmarks: [],
  savedInkingViews: [],
  canUndo: false,
  canRedo: false,
  favoriteShapeKeys: new Set(),
  favoriteOperators: new Set(
    (() => {
      try {
        const raw = localStorage.getItem("poseref-favorite-operators");
        return raw ? JSON.parse(raw) : [];
      } catch {
        return [];
      }
    })(),
  ),
  operatorSearchOpen: false,
  activeOperator: null,
  chromeHidden: false,
  addObjectOpen: false,
  addObjectFocusSearch: false,
  deleteMenuOpen: false,
  renderMethodPieOpen: false,
  snapPieOpen: false,
  viewPieOpen: false,
  extrudePieOpen: false,
  mergeMenuOpen: false,
  relaxMenuOpen: false,
  gravityMenuOpen: false,
  smoothMenuOpen: false,
  objectDeleteConfirmOpen: false,
  meshMenuOpen: false,
  meshMenuSection: "all" as const,
  operatorAnchorPoint: null,
  panelLastSelected: {},
  relaxFalloff: 0,
  modifierOverrides: new Map(),
  modifierUICollapsed: new Map(),
  sceneObjects: new Map(),
  materials: new Map(),
  mannequinMorphs: new Map(),
  mannequinBonePoses: new Map(),
  lockedBones: new Map(),
  pinnedBones: new Map(),
  boneVolumes: new Map(),
  constraintWeights: new Map(),
  poseMode: null,
  ikDragState: null,
  editMode: null,
  activeEditSession: null,
  activeMaterialSlotId: null,
  editSessionCache: new Map(),
  lastObjectModeSelection: null,
  lastEditModeSelection: null,
  sculptMode: null,
  sculptStrokeSeq: 0,
  sketchMode: null,
  sketch: createEmptySceneSketchState(),
  sketchStrokeSeq: 0,
  inkingMode: null,
  allowNonPenDrawingInput: false,
  pivotReference: "median" as PivotReference,
  transformOrientation:
    "global" as import("./storeTypes.ts").TransformOrientation,
  proportionalEnabled: false,
  proportionalRadius: 100,
  proportionalFalloff:
    "smooth" as import("../modeling/proportional.ts").ProportionalFalloff,
  brushSelectRadius: 25,
  snapEnabled: false,
  snapTarget: "vertex" as import("./storeTypes.ts").SnapTarget,
  snapWithRotation: false,
  surfaceProjectIndividual: true,
  cursor3D: { ...DEFAULT_CURSOR_3D },
  pendingAxis: null,
  commandBuffer: null,
  commandPreSnapshot: null,
  commandCancelSnapshot: null,
  lastOperation: null,
  objectLibrary: loadLibrary(),
  currentSceneFileName: `${generateSceneName()}.pr`,
  sceneDirty: false,
  sceneSaving: false,
  sceneCreatedAt: null,
  loadFile: async (file, name, buffer, _skipPersistence) => {
    // Initialize visibility — respect Blender's viewport restriction flag,
    // collection visibility, AND parent hierarchy propagation.
    const directlyHidden = new Set<string>();
    for (const obj of file.objects) {
      const hiddenByRestrictFlag =
        (obj.restrictFlag & OB_RESTRICT_VIEWPORT) !== 0;
      if (hiddenByRestrictFlag || obj.hiddenByCollection) {
        directlyHidden.add(obj.name);
      }
    }

    const childrenOf = new Map<string, string[]>();
    for (const obj of file.objects) {
      if (obj.parentName) {
        let list = childrenOf.get(obj.parentName);
        if (!list) {
          list = [];
          childrenOf.set(obj.parentName, list);
        }
        list.push(obj.name);
      }
    }

    const allHidden = new Set(directlyHidden);
    const propagate = (parentName: string) => {
      const children = childrenOf.get(parentName);
      if (!children) return;
      for (const child of children) {
        allHidden.add(child);
        propagate(child);
      }
    };
    for (const hiddenName of directlyHidden) {
      propagate(hiddenName);
    }

    const vis = new Map<string, boolean>();
    for (const obj of file.objects) {
      vis.set(obj.name, !allHidden.has(obj.name));
    }

    // Initialize shape key weights from defaults
    const weights = new Map<string, Map<string, number>>();
    for (const [setName, skSet] of file.shapeKeySets) {
      const keyWeights = new Map<string, number>();
      for (const key of skSet.keys) {
        keyWeights.set(key.name, key.defaultValue);
      }
      weights.set(setName, keyWeights);
    }

    // Build driver graph
    const driverGraph = new DriverGraph();
    driverGraph.build(file.shapeKeySets);
    driverGraph.evaluate(weights);

    undoManager.clear();

    // Build blend file entry
    const entryId = generateBlendFileId();
    const entryHash = buffer ? await computeFileHash(buffer) : "";
    const entry: BlendFileEntry = {
      id: entryId,
      file,
      fileName: name,
      hash: entryHash,
      originalBuffer: buffer ?? new ArrayBuffer(0),
      groupTransform: { ...IDENTITY_GROUP_TRANSFORM },
    };

    const patch: Partial<AppState> = {
      blendFiles: new Map([[entryId, entry]]),
      selectedObjectTargets: [],
      visibility: vis,
      shapeKeyWeights: weights,
      modifierOverrides: new Map(),
      driverGraph,
      loading: false,
      error: null,
      canUndo: false,
      canRedo: false,
      workspaceMode: "object",
    };
    set(patch);

    // localStorage persistence is kept as a backup but not auto-restored.
    // The cloud library is the source of truth.
  },

  removeBlendFile: (id) => {
    const state = get();
    const entry = state.blendFiles.get(id);
    if (!entry) return;

    undoManager.push(captureSnapshot(state));

    // Collect all object names owned by this entry
    const removedNames = new Set(entry.file.objects.map((o) => o.name));

    // Remove the entry from the map
    const newFiles = new Map(state.blendFiles);
    newFiles.delete(id);

    // Clean up name-keyed state
    const vis = new Map(state.visibility);
    const rv = new Map(state.renderVisibility);
    const so = new Map(state.shadingOverrides);
    const skw = new Map(state.shapeKeyWeights);
    const mo = new Map(state.modifierOverrides);

    for (const name of removedNames) {
      vis.delete(name);
      rv.delete(name);
      so.delete(name);
      mo.delete(name);
    }
    // Clean up shape key weights for removed entry's shape key sets
    for (const setName of entry.file.shapeKeySets.keys()) {
      skw.delete(setName);
    }

    // Rebuild driver graph from remaining entries
    const driverGraph = new DriverGraph();
    const remainingSKS = allShapeKeySets(newFiles);
    if (remainingSKS.size > 0) {
      driverGraph.build(remainingSKS);
      driverGraph.evaluate(skw);
    }

    // Clear editMode if targeting a removed object
    if (state.editMode && removedNames.has(state.editMode.objectId)) {
      clearLiveEditPreview(state.editMode.objectId);
    }
    const editMode =
      state.editMode && removedNames.has(state.editMode.objectId)
        ? null
        : state.editMode;
    const selectedObjectTargets = state.selectedObjectTargets.filter(
      (target) => target.kind !== "blendEntry" || newFiles.has(target.id),
    );

    set({
      blendFiles: newFiles,
      visibility: vis,
      renderVisibility: rv,
      shadingOverrides: so,
      ...withSelectedTargets(selectedObjectTargets),
      shapeKeyWeights: skw,
      modifierOverrides: mo,
      driverGraph: remainingSKS.size > 0 ? driverGraph : null,
      editMode,
      canUndo: true,
    });
  },

  resetScene: async () => {
    const { blendFiles } = get();
    const firstEntry = blendFiles.size > 0 ? [...blendFiles.values()][0] : null;
    if (firstEntry) {
      // Blend file loaded — re-parse and reload
      set({ loading: true });
      const bufferCopy = firstEntry.originalBuffer.slice(0);
      const file = await parseBlendFileAsync(firstEntry.originalBuffer);
      await get().loadFile(file, firstEntry.fileName, bufferCopy, true);
    } else {
      // Blank scene — clear all scene objects and reset to defaults.
      // Don't route through cancelCommand: we're wiping the scene, so
      // restoring from the command snapshot would fight against that.
      // Clear commandBuffer + snapshot fields directly, matching the shape
      // cancelCommand uses when no restore is needed.
      undoManager.clear();
      clearLiveEditPreview();
      set({
        sceneObjects: new Map(),
        materials: new Map(),
        selectedObjectTargets: [],
        collections: new Map(),
        editMode: null,
        activeEditSession: null,
        editSessionCache: new Map(),
        commandBuffer: null,
        commandPreSnapshot: null,
        commandCancelSnapshot: null,
        ikDragState: null,
        canUndo: false,
        canRedo: false,
      });
    }
  },

  selectObject: (name, additive = false) => {
    const state = get();
    const target = name
      ? resolveStoreKeySelectionTarget(
          state.blendFiles,
          state.collections,
          name,
        )
      : null;
    state.selectObjectTarget(target, additive);
  },

  selectObjectTarget: (target, additive = false) => {
    const state = get();
    const prevTargets = state.selectedObjectTargets;
    const currentMode = state.mode;
    const wm = state.workspaceMode;

    if (wm === "pose") {
      if (!target || target.kind !== "sceneObject") return;
      const obj = state.sceneObjects.get(target.id);
      if (obj?.objectKind !== "mannequin") return;
      const pm = state.poseMode;
      if (pm && pm.objectId !== target.id) {
        state.enterPoseMode(target.id);
      }
      return;
    }

    if ((wm === "sketch" || wm === "sculpt") && !target) return;
    if (wm === "edit") return;

    let nextTargets: ObjectSelectionTarget[] = [];
    if (target) {
      if (additive) {
        nextTargets = [...prevTargets];
        const existingIndex = nextTargets.findIndex(
          (candidate) =>
            objectSelectionTargetKey(candidate) ===
            objectSelectionTargetKey(target),
        );
        if (existingIndex >= 0) {
          nextTargets.splice(existingIndex, 1);
        } else {
          nextTargets.push(target);
        }
      } else {
        nextTargets = [target];
      }
    }

    const patch: Partial<AppState> = withSelectedTargets(nextTargets);
    if (state.cursor3D.selected)
      patch.cursor3D = { ...state.cursor3D, selected: false };
    // Save the previous target list for recall when a non-additive click replaces it.
    if (!additive && prevTargets.length > 0) {
      patch.lastObjectModeSelection = selectionToLeafObjectIds(
        state.blendFiles,
        state.collections,
        prevTargets,
      );
    }

    // Sculpt mode targets a single object. If the new selection no longer
    // contains that target, eject so the workspace reverts cleanly.
    const newLeafIds = selectionToLeafObjectIds(
      state.blendFiles,
      state.collections,
      nextTargets,
    );
    const sm = state.sculptMode;
    const ejectsSculpt = !!(sm && !newLeafIds.has(sm.objectId));
    if (ejectsSculpt) {
      patch.sculptMode = null;
      patch.workspaceMode = "object";
    }
    // Sketch mode is no longer bound to a selected object — selection changes
    // never eject it.

    const isRefImage =
      currentMode === "transform" && state.transformMode === "refImage";
    if (nextTargets.length > 0 && !isRefImage) {
      patch.mode = "transform" as InteractionMode;
    } else if (nextTargets.length === 0 && currentMode === "transform") {
      patch.mode = "navigate" as InteractionMode;
    }

    // Pure selection change → lightweight delta. Sculpt ejection
    // mutates modal state that SelectionDelta doesn't carry, so fall
    // back to a full snapshot on that branch.
    if (ejectsSculpt) {
      get().pushUndo();
    } else {
      const before = captureSelectionDeltaState(state);
      const after = captureSelectionDeltaState({
        ...state,
        ...patch,
      } as AppState);
      pushSelectionUndo(before, after);
      set({ canUndo: undoManager.canUndo, canRedo: undoManager.canRedo });
    }
    set(patch);
  },

  boxSelectObjects: (objectIds, additive) => {
    const state = get();
    if (state.workspaceMode !== "object" || state.editMode) return;
    if (additive && objectIds.size === 0) return;

    const prevTargets = state.selectedObjectTargets;
    const nextTargets: ObjectSelectionTarget[] = additive
      ? [...prevTargets]
      : [];
    const seen = new Set(nextTargets.map(objectSelectionTargetKey));

    for (const objectId of objectIds) {
      const target = resolveStoreKeySelectionTarget(
        state.blendFiles,
        state.collections,
        objectId,
      );
      const targetKey = objectSelectionTargetKey(target);
      if (seen.has(targetKey)) continue;
      seen.add(targetKey);
      nextTargets.push(target);
    }

    const patch: Partial<AppState> = withSelectedTargets(nextTargets);
    if (nextTargets.length > 0 && state.cursor3D.selected) {
      patch.cursor3D = { ...state.cursor3D, selected: false };
    }
    if (!additive && prevTargets.length > 0) {
      patch.lastObjectModeSelection = selectionToLeafObjectIds(
        state.blendFiles,
        state.collections,
        prevTargets,
      );
    }

    if (nextTargets.length > 0) {
      patch.mode = "transform" as InteractionMode;
    } else if (state.mode === "transform") {
      patch.mode = "navigate" as InteractionMode;
    }

    const before = captureSelectionDeltaState(state);
    const after = captureSelectionDeltaState({
      ...state,
      ...patch,
    } as AppState);
    pushSelectionUndo(before, after);
    set({ canUndo: undoManager.canUndo, canRedo: undoManager.canRedo });

    set(patch);
  },

  brushSelectObjects: (objectIds, mode) => {
    const state = get();
    if (state.workspaceMode !== "object" || state.editMode) return;
    if (mode !== "set" && objectIds.size === 0) return;

    const prevTargets = state.selectedObjectTargets;
    let nextTargets: ObjectSelectionTarget[];

    if (mode === "set") {
      nextTargets = [];
      const seen = new Set<string>();
      for (const objectId of objectIds) {
        const target = resolveStoreKeySelectionTarget(
          state.blendFiles,
          state.collections,
          objectId,
        );
        const targetKey = objectSelectionTargetKey(target);
        if (seen.has(targetKey)) continue;
        seen.add(targetKey);
        nextTargets.push(target);
      }
    } else if (mode === "add") {
      nextTargets = [...prevTargets];
      const seen = new Set(nextTargets.map(objectSelectionTargetKey));
      for (const objectId of objectIds) {
        const target = resolveStoreKeySelectionTarget(
          state.blendFiles,
          state.collections,
          objectId,
        );
        const targetKey = objectSelectionTargetKey(target);
        if (seen.has(targetKey)) continue;
        seen.add(targetKey);
        nextTargets.push(target);
      }
    } else {
      const removeKeys = new Set<string>();
      for (const objectId of objectIds) {
        const target = resolveStoreKeySelectionTarget(
          state.blendFiles,
          state.collections,
          objectId,
        );
        removeKeys.add(objectSelectionTargetKey(target));
      }
      if (removeKeys.size === 0) return;
      nextTargets = prevTargets.filter(
        (target) => !removeKeys.has(objectSelectionTargetKey(target)),
      );
    }

    const patch: Partial<AppState> = withSelectedTargets(nextTargets);
    if (nextTargets.length > 0 && state.cursor3D.selected) {
      patch.cursor3D = { ...state.cursor3D, selected: false };
    }
    if (mode === "set" && prevTargets.length > 0) {
      patch.lastObjectModeSelection = selectionToLeafObjectIds(
        state.blendFiles,
        state.collections,
        prevTargets,
      );
    }

    if (nextTargets.length > 0) {
      patch.mode = "transform" as InteractionMode;
    } else if (state.mode === "transform") {
      patch.mode = "navigate" as InteractionMode;
    }

    set(patch);
  },

  beginEyedropperSession: (onPick, onCancel, canPick) => {
    set({ eyedropperSession: { onPick, onCancel, canPick } });
  },

  endEyedropperSession: () => {
    set({ eyedropperSession: null, eyedropperHoveredId: null });
  },

  setEyedropperHoveredId: (id) => {
    set({ eyedropperHoveredId: id });
  },

  // ---------------------------------------------------------------------------
  // Collection actions
  // ---------------------------------------------------------------------------

  createCollection: (name, objectIds) => {
    const state = get();
    const id = createCollectionId();
    const collections = new Map(state.collections);
    const target: Collection = {
      id,
      name,
      transform: cloneGroupTransform(IDENTITY_GROUP_TRANSFORM),
      objectIds: new Set(),
    };
    collections.set(id, target);

    let sceneObjects = state.sceneObjects;
    const membership = buildObjectCollectionIndex(collections);
    const validIds = filterValidCollectionMembers(
      state,
      objectIds ?? [],
      "createCollection",
    );
    for (const objectId of validIds) {
      const obj = sceneObjects.get(objectId);
      if (!obj) continue;
      const sourceCollectionId = membership.get(objectId) ?? null;
      const worldTransform = sourceCollectionId
        ? worldTransformForCollectionMember(
            obj,
            collections.get(sourceCollectionId)?.transform ??
              IDENTITY_GROUP_TRANSFORM,
          )
        : sceneObjectEulerTransform(obj);
      sceneObjects = patchSceneObjectEulerTransform(
        sceneObjects,
        objectId,
        decomposeWorldToLocalTransform(target.transform, worldTransform),
      );
      if (sourceCollectionId && sourceCollectionId !== id) {
        removeCollectionMembers(collections, sourceCollectionId, [objectId]);
      }
      target.objectIds.add(objectId);
    }

    collections.set(id, target);
    set({
      collections,
      sceneObjects,
      selectedObjectTargets: filterExistingSelectionTargets(
        sceneObjects,
        state.blendFiles,
        collections,
        state.selectedObjectTargets,
      ),
    });
    return id;
  },

  deleteCollection: (id) => {
    get().pushUndo();
    const collections = new Map(get().collections);
    const collection = collections.get(id);
    if (!collection) return;
    let sceneObjects = get().sceneObjects;
    for (const objectId of collection.objectIds) {
      const obj = sceneObjects.get(objectId);
      if (!obj) continue;
      sceneObjects = patchSceneObjectEulerTransform(
        sceneObjects,
        objectId,
        worldTransformForCollectionMember(obj, collection.transform),
      );
    }
    collections.delete(id);
    const patch: Partial<AppState> = { collections, sceneObjects };
    patch.selectedObjectTargets = filterExistingSelectionTargets(
      sceneObjects,
      get().blendFiles,
      collections,
      get().selectedObjectTargets,
    );
    set(patch);
  },

  renameCollection: (id, name) => {
    const collections = new Map(get().collections);
    const coll = collections.get(id);
    if (!coll) return;
    collections.set(id, { ...coll, name });
    set({ collections });
  },

  applyCollectionTransform: (id) => {
    const current = get();
    const collection = current.collections.get(id);
    if (!collection) return;
    if (isIdentityGroupTransform(collection.transform)) return;
    current.pushUndo();

    // For each member: compose its world transform, bake that into its
    // vertex data, and reset its local TRS to identity. Members with no
    // geometry to bake (mannequins, empty verts) still get their TRS
    // normalized so the whole collection lands at a consistent identity.
    let sceneObjects = current.sceneObjects;
    const bakedTransforms = new Map<string, THREE.Matrix4>();
    for (const objectId of collection.objectIds) {
      const obj = sceneObjects.get(objectId);
      if (!obj) continue;
      const worldTransform = worldTransformForCollectionMember(
        obj,
        collection.transform,
      );
      const result = bakeTransformIntoVerticesPatch(obj, worldTransform);
      if (result) {
        sceneObjects = patchSceneObject(sceneObjects, objectId, result.patch);
        if (result.bakeMatrix) bakedTransforms.set(objectId, result.bakeMatrix);
      }
    }

    const collections = new Map(current.collections);
    collections.set(id, {
      ...collection,
      transform: cloneGroupTransform(IDENTITY_GROUP_TRANSFORM),
      objectIds: new Set(collection.objectIds),
    });
    const sketchPatch =
      bakedTransforms.size > 0
        ? { sketch: pruneSceneSketch(current.sketch, { bakedTransforms }) }
        : {};
    set({ collections, sceneObjects, ...sketchPatch });
  },

  toggleCollectionVisibility: (id) => {
    get().pushUndo();
    const coll = get().collections.get(id);
    if (!coll) return;
    const vis = new Map(get().visibility);
    const shouldShow = [...coll.objectIds].some(
      (objId) => (vis.get(objId) ?? true) === false,
    );
    for (const objId of coll.objectIds) {
      vis.set(objId, shouldShow);
    }
    set({ visibility: vis });
  },

  addToCollection: (collectionId, objectIds) => {
    const state = get();
    const collections = new Map(state.collections);
    const target = collections.get(collectionId);
    if (!target) return;
    let sceneObjects = state.sceneObjects;
    const membership = buildObjectCollectionIndex(collections);
    const validIds = filterValidCollectionMembers(
      state,
      objectIds,
      "addToCollection",
    );
    for (const objectId of validIds) {
      const obj = sceneObjects.get(objectId);
      if (!obj) continue;
      const sourceCollectionId = membership.get(objectId) ?? null;
      if (sourceCollectionId === collectionId) continue;
      const worldTransform = sourceCollectionId
        ? worldTransformForCollectionMember(
            obj,
            collections.get(sourceCollectionId)?.transform ??
              IDENTITY_GROUP_TRANSFORM,
          )
        : sceneObjectEulerTransform(obj);
      sceneObjects = patchSceneObjectEulerTransform(
        sceneObjects,
        objectId,
        decomposeWorldToLocalTransform(target.transform, worldTransform),
      );
      if (sourceCollectionId) {
        removeCollectionMembers(collections, sourceCollectionId, [objectId]);
      }
      target.objectIds.add(objectId);
      membership.set(objectId, collectionId);
    }
    collections.set(collectionId, {
      ...target,
      objectIds: new Set(target.objectIds),
    });
    set({
      collections,
      sceneObjects,
      selectedObjectTargets: filterExistingSelectionTargets(
        sceneObjects,
        state.blendFiles,
        collections,
        state.selectedObjectTargets,
      ),
    });
  },

  removeFromCollection: (collectionId, objectIds) => {
    const state = get();
    const collections = new Map(state.collections);
    const coll = collections.get(collectionId);
    if (!coll) return;
    let sceneObjects = state.sceneObjects;
    const removed = new Set<string>();
    for (const objectId of objectIds) {
      if (!coll.objectIds.has(objectId)) continue;
      const obj = sceneObjects.get(objectId);
      if (!obj) continue;
      sceneObjects = patchSceneObjectEulerTransform(
        sceneObjects,
        objectId,
        worldTransformForCollectionMember(obj, coll.transform),
      );
      removed.add(objectId);
    }
    removeCollectionMembers(collections, collectionId, removed);
    set({
      collections,
      sceneObjects,
      selectedObjectTargets: filterExistingSelectionTargets(
        sceneObjects,
        state.blendFiles,
        collections,
        state.selectedObjectTargets,
      ),
    });
  },

  moveToCollection: (targetCollectionId, objectIds) => {
    const state = get();
    const collections = new Map(state.collections);
    const target = collections.get(targetCollectionId);
    if (!target) return;
    let sceneObjects = state.sceneObjects;
    const membership = buildObjectCollectionIndex(collections);
    const validIds = filterValidCollectionMembers(
      state,
      objectIds,
      "moveToCollection",
    );
    for (const objectId of validIds) {
      const obj = sceneObjects.get(objectId);
      if (!obj) continue;
      const sourceCollectionId = membership.get(objectId) ?? null;
      if (sourceCollectionId === targetCollectionId) continue;
      const worldTransform = sourceCollectionId
        ? worldTransformForCollectionMember(
            obj,
            collections.get(sourceCollectionId)?.transform ??
              IDENTITY_GROUP_TRANSFORM,
          )
        : sceneObjectEulerTransform(obj);
      sceneObjects = patchSceneObjectEulerTransform(
        sceneObjects,
        objectId,
        decomposeWorldToLocalTransform(target.transform, worldTransform),
      );
      if (sourceCollectionId) {
        removeCollectionMembers(collections, sourceCollectionId, [objectId]);
      }
      target.objectIds.add(objectId);
      membership.set(objectId, targetCollectionId);
    }
    collections.set(targetCollectionId, {
      ...target,
      objectIds: new Set(target.objectIds),
    });
    set({
      collections,
      sceneObjects,
      selectedObjectTargets: filterExistingSelectionTargets(
        sceneObjects,
        state.blendFiles,
        collections,
        state.selectedObjectTargets,
      ),
    });
  },

  selectCollection: (id) => {
    get().selectObjectTarget(id ? { kind: "collection", id } : null, false);
  },

  selectGroup: (objectNames) => {
    if (objectNames.length === 0) return;
    const targets: ObjectSelectionTarget[] = [];
    const seen = new Set<string>();
    const state = get();
    for (const key of objectNames) {
      const target = resolveStoreKeySelectionTarget(
        state.blendFiles,
        state.collections,
        key,
      );
      const targetKey = objectSelectionTargetKey(target);
      if (seen.has(targetKey)) continue;
      seen.add(targetKey);
      targets.push(target);
    }
    state.pushUndo();
    const patch: Partial<AppState> = withSelectedTargets(targets);
    if (state.mode !== "edit") {
      patch.mode = "transform" as InteractionMode;
    }
    set(patch);
  },

  selectBlendEntry: (entryId) => {
    const entry = get().blendFiles.get(entryId);
    if (!entry) return;
    get().selectObjectTarget({ kind: "blendEntry", id: entryId }, false);
  },

  toggleVisibility: (name) => {
    get().pushUndo();
    const vis = new Map(get().visibility);
    const newState = !vis.get(name);
    vis.set(name, newState);

    // Propagate to all descendants
    const { blendFiles } = get();
    if (blendFiles.size > 0) {
      const propagate = (parentName: string) => {
        for (const obj of allBlendObjects(blendFiles)) {
          if (obj.parentName === parentName) {
            vis.set(obj.name, newState);
            propagate(obj.name);
          }
        }
      };
      propagate(name);
    }

    set({ visibility: vis });
  },

  setObjectsVisibility: (ids, visible) => {
    if (ids.length === 0) return;
    get().pushUndo();
    const vis = new Map(get().visibility);
    for (const id of ids) vis.set(id, visible);
    set({ visibility: vis });
  },

  hideSelection: () => {
    const s = get();
    const inEditMode = s.mode === "edit" && s.editMode != null;

    if (inEditMode) {
      // Delegate to edit mode hide (implemented in storeEditOps)
      s.hideEditSelection();
    } else {
      // Object mode: hide all selected objects (deselect after hiding)
      if (s.selectedObjectTargets.length === 0) return;
      const selected = selectionToLeafObjectIds(
        s.blendFiles,
        s.collections,
        s.selectedObjectTargets,
      );
      if (selected.size === 0) return;
      s.pushUndo();
      const vis = new Map(s.visibility);
      for (const id of selected) vis.set(id, false);
      set({ visibility: vis, selectedObjectTargets: [] });
    }
  },

  unhideAll: () => {
    const s = get();
    const inEditMode = s.mode === "edit" && s.editMode != null;

    if (inEditMode) {
      s.unhideEditAll();
    } else {
      // Object mode: unhide everything
      s.pushUndo();
      set({ visibility: new Map() });
    }
  },

  hideUnselected: () => {
    const s = get();
    const inEditMode = s.mode === "edit" && s.editMode != null;

    if (inEditMode) {
      s.hideEditUnselected();
    } else {
      // Object mode: hide all objects NOT currently selected
      const selected = selectionToLeafObjectIds(
        s.blendFiles,
        s.collections,
        s.selectedObjectTargets,
      );
      s.pushUndo();
      const vis = new Map(s.visibility);
      for (const id of s.sceneObjects.keys()) {
        if (!selected.has(id)) vis.set(id, false);
      }
      set({ visibility: vis });
    }
  },

  setRenderVisible: (name, visible) => {
    const rv = new Map(get().renderVisibility);
    rv.set(name, visible);
    set({ renderVisibility: rv });
  },

  setShadingOverride: (name, shading) => {
    const so = new Map(get().shadingOverrides);
    so.set(name, shading);
    set({ shadingOverrides: so });
  },

  setShapeKeyWeight: (setName, keyName, weight) => {
    // Deep-clone all inner maps so driver evaluation can safely mutate
    // target sets and Zustand subscribers detect changes via new references.
    const prev = get().shapeKeyWeights;
    const weights = new Map<string, Map<string, number>>();
    for (const [k, v] of prev) {
      weights.set(k, new Map(v));
    }

    if (!weights.has(setName)) {
      weights.set(setName, new Map());
    }
    weights.get(setName)!.set(keyName, weight);

    const { driverGraph } = get();
    if (driverGraph) {
      driverGraph.evaluate(weights);
    }

    set({ shapeKeyWeights: weights });
  },

  setFocalLength: (mm) => set({ focalLength: mm }),
  setCameraOrbit: (position, target) => {
    const state = get();
    const breakReason = viewLockBreakReason(state.viewLock, position, target);
    if (breakReason) {
      const lock = state.viewLock;
      if (lock) {
        window.__poseref_cameraController?.applyState({
          projection: lock.prevProjection,
          position,
          target,
          up: lock.prevProjection === "perspective" ? null : state.cameraUp,
          focalLength: lock.prevFocalLength,
          orthographicHalfHeight:
            lock.prevOrthographicHalfHeight ?? state.orthographicHalfHeight,
        });
      } else {
        window.__poseref_cameraController?.applyState({ position, target });
      }
      state.exitViewLock(breakReason);
    } else {
      window.__poseref_cameraController?.applyState({ position, target });
    }
    set({ cameraPosition: position, cameraTarget: target });
  },
  addReferenceLayer: (imageKey, imageUrl, name) => {
    const id = `ref_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    const layers = get().referenceLayers;
    const layerName = name ?? `Reference ${layers.length + 1}`;
    const newLayer: ReferenceLayer = {
      id,
      name: layerName,
      imageKey,
      imageUrl,
      offset: [0, 0],
      scale: 1,
      rotation: 0,
      opacity: 0.3,
      above: false,
      visible: true,
    };
    const patch: Record<string, unknown> = {
      referenceLayers: [...layers, newLayer],
      activeReferenceLayerId: id,
    };
    if (get().workspaceMode === "reference") {
      patch.transformMode = "refImage";
      patch.mode = "transform";
    }
    set(patch as any);
  },
  deleteReferenceLayer: (layerId) => {
    const layers = get().referenceLayers;
    const idx = layers.findIndex((l) => l.id === layerId);
    if (idx < 0) return;
    // Revoke blob URL to free memory
    const removed = layers[idx];
    if (removed.imageUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(removed.imageUrl);
    }
    const remaining = layers.filter((l) => l.id !== layerId);
    const patch: Record<string, unknown> = { referenceLayers: remaining };
    if (get().activeReferenceLayerId === layerId) {
      if (remaining.length > 0) {
        // Select adjacent layer: prefer the one below, else above
        const nextIdx = Math.min(idx, remaining.length - 1);
        patch.activeReferenceLayerId = remaining[nextIdx].id;
      } else {
        patch.activeReferenceLayerId = null;
      }
    }
    if (remaining.length === 0 && get().workspaceMode === "reference") {
      patch.workspaceMode = "object";
      patch.mode = "navigate";
      patch.transformMode = "translate";
    }
    set(patch as any);
  },
  setActiveReferenceLayer: (layerId) =>
    set({ activeReferenceLayerId: layerId }),
  setRefLayerOffset: (layerId, offset) => {
    set({
      referenceLayers: get().referenceLayers.map((l) =>
        l.id === layerId ? { ...l, offset } : l,
      ),
    });
  },
  setRefLayerScale: (layerId, scale) => {
    set({
      referenceLayers: get().referenceLayers.map((l) =>
        l.id === layerId
          ? { ...l, scale: Math.max(0.05, Math.min(10, scale)) }
          : l,
      ),
    });
  },
  setRefLayerRotation: (layerId, degrees) => {
    set({
      referenceLayers: get().referenceLayers.map((l) =>
        l.id === layerId ? { ...l, rotation: degrees } : l,
      ),
    });
  },
  setRefLayerOpacity: (layerId, opacity) => {
    set({
      referenceLayers: get().referenceLayers.map((l) =>
        l.id === layerId
          ? { ...l, opacity: Math.max(0, Math.min(1, opacity)) }
          : l,
      ),
    });
  },
  setRefLayerAbove: (layerId, above) => {
    set({
      referenceLayers: get().referenceLayers.map((l) =>
        l.id === layerId ? { ...l, above } : l,
      ),
    });
  },
  setRefLayerVisible: (layerId, visible) => {
    set({
      referenceLayers: get().referenceLayers.map((l) =>
        l.id === layerId ? { ...l, visible } : l,
      ),
    });
  },
  setRefLayerName: (layerId, name) => {
    set({
      referenceLayers: get().referenceLayers.map((l) =>
        l.id === layerId ? { ...l, name } : l,
      ),
    });
  },
  setRefLayerImage: (layerId, imageKey, imageUrl) => {
    set({
      referenceLayers: get().referenceLayers.map((l) =>
        l.id === layerId ? { ...l, imageKey, imageUrl } : l,
      ),
    });
  },
  refreshReferenceImageUrls: async () => {
    const layers = get().referenceLayers;
    if (layers.length === 0) return;
    const { getReferenceImageUrl } = await import(
      "./persistence/referenceImageDB.ts"
    );

    // Revoke previous blob URLs to avoid memory leaks
    for (const l of layers) {
      if (l.imageUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(l.imageUrl);
      }
    }

    const updatedLayers = await Promise.all(
      layers.map(async (l) => {
        if (!l.imageKey) return l;

        // Legacy R2 key migration: keys containing "/" are old cloud paths
        if (l.imageKey.includes("/")) {
          try {
            const { migrateCloudReferenceImage } = await import(
              "./referenceImageMigration.ts"
            );
            const result = await migrateCloudReferenceImage(l.imageKey);
            if (result) {
              return { ...l, imageKey: result.hash, imageUrl: result.imageUrl };
            }
          } catch {
            // Migration failed — show placeholder
          }
          return { ...l, imageUrl: "" };
        }

        // Normal local lookup by content hash
        const imageUrl = await getReferenceImageUrl(l.imageKey);
        return { ...l, imageUrl: imageUrl ?? "" };
      }),
    );

    // Preserve all layers — never remove missing images
    set({ referenceLayers: updatedLayers } as any);
  },
  moveReferenceLayer: (layerId, direction) => {
    const layers = [...get().referenceLayers];
    const idx = layers.findIndex((l) => l.id === layerId);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx + 1 : idx - 1;
    if (swapIdx < 0 || swapIdx >= layers.length) return;
    [layers[idx], layers[swapIdx]] = [layers[swapIdx], layers[idx]];
    set({ referenceLayers: layers });
  },
  setAlignCameraDrawAxis: (axis) => set({ alignCameraDrawAxis: axis }),
  setSelectedVanishingLine: (lineId) =>
    set({ selectedVanishingLineId: lineId }),
  addVanishingLine: (layerId, line) => {
    set({
      referenceLayers: get().referenceLayers.map((l) =>
        l.id === layerId
          ? { ...l, vanishingLines: [...(l.vanishingLines ?? []), line] }
          : l,
      ),
    });
  },
  updateVanishingLineEndpoint: (layerId, lineId, which, pos) => {
    set({
      referenceLayers: get().referenceLayers.map((l) =>
        l.id === layerId
          ? {
              ...l,
              vanishingLines: l.vanishingLines?.map((vl) =>
                vl.id === lineId ? { ...vl, [which]: pos } : vl,
              ),
            }
          : l,
      ),
    });
  },
  deleteVanishingLine: (layerId, lineId) => {
    set({
      referenceLayers: get().referenceLayers.map((l) =>
        l.id === layerId
          ? {
              ...l,
              vanishingLines: (l.vanishingLines ?? []).filter(
                (vl) => vl.id !== lineId,
              ),
            }
          : l,
      ),
      selectedVanishingLineId:
        get().selectedVanishingLineId === lineId
          ? null
          : get().selectedVanishingLineId,
    });
  },
  clearVanishingLines: (layerId) => {
    set({
      referenceLayers: get().referenceLayers.map((l) =>
        l.id === layerId ? { ...l, vanishingLines: [] } : l,
      ),
      selectedVanishingLineId: null,
    });
  },
  setGroundPlaneZ: (layerId, z) => {
    set({
      referenceLayers: get().referenceLayers.map((l) =>
        l.id === layerId ? { ...l, groundPlaneZ: z } : l,
      ),
    });
  },
  setLiveSolve: (layerId, enabled) => {
    set({
      referenceLayers: get().referenceLayers.map((l) =>
        l.id === layerId ? { ...l, liveSolve: enabled } : l,
      ),
    });
  },
  setReferenceDistance: (layerId, ref) => {
    set({
      referenceLayers: get().referenceLayers.map((l) =>
        l.id === layerId ? { ...l, referenceDistance: ref } : l,
      ),
    });
  },
  setMode: (mode) => {
    // If entering transform mode with refImage but no ref image, fall back
    if (
      mode === "transform" &&
      get().transformMode === "refImage" &&
      get().referenceLayers.length === 0
    ) {
      set({ mode, transformMode: "translate" });
    } else {
      set({ mode });
    }
  },
  setOrbitEnabled: (enabled, options) => {
    if (get().orbitEnabled === enabled) return;
    set({ orbitEnabled: enabled });
    if (options?.toast !== false) {
      showToast(
        enabled
          ? "Viewport navigation enabled"
          : "Viewport navigation disabled",
        { duration: 2000 },
      );
    }
  },
  setSingleDragPanEnabled: (enabled, options) => {
    if (get().singleDragPanEnabled === enabled) return;
    set({ singleDragPanEnabled: enabled });
    try {
      localStorage.setItem("poseref-single-drag-pan-enabled", String(enabled));
    } catch {
      /* ignore */
    }
    if (options?.toast !== false) {
      showToast(
        enabled ? "Single-drag pan enabled" : "Single-drag pan disabled",
        { duration: 2000 },
      );
    }
  },
  setSpaceOrbitActive: (active) => set({ spaceOrbitActive: active }),
  setTransformMode: (mode) => {
    if (mode === "pose") {
      // Enter pose mode — requires a single poseable object selected
      const target = getSingleSelectionTarget(get().selectedObjectTargets);
      if (target?.kind === "sceneObject") {
        get().enterPoseMode(target.id);
      }
      return;
    }
    if (mode === "refImage") {
      // Leaving alignCamera → restore image-display overrides.
      const exitingAlign = get().transformMode === "alignCamera";
      set({
        transformMode: mode,
        mode: "transform" as InteractionMode,
        ...(exitingAlign ? exitAlignModeOverrides(get()) : {}),
      });
    } else if (mode === "alignCamera") {
      // Entering alignCamera → snapshot and force above=true / opacity=1.0
      // on the active layer so the reference image is unmissable while
      // drawing lines.
      set({
        transformMode: mode,
        mode: "transform" as InteractionMode,
        alignCameraDrawAxis: "x",
        ...enterAlignModeOverrides(get()),
      });
    } else if (get().poseMode && mode === "rotate") {
      // Within pose mode, only rotation is available via the gizmo.
      // Translation is handled by direct joint dragging (BoneOverlay).
      set({ transformMode: mode });
    } else if (get().poseMode && mode === "translate") {
      // Translate mode is not available in pose mode — ignore silently
      return;
    } else {
      // Clean up align camera state when leaving that mode
      const exitingAlign = get().transformMode === "alignCamera";
      const alignCleanup = exitingAlign
        ? {
            alignCameraDrawAxis: null,
            selectedVanishingLineId: null,
            ...exitAlignModeOverrides(get()),
          }
        : {};
      // Switching to scale/other exits pose mode
      if (get().poseMode) {
        set({ transformMode: mode, poseMode: null, ...alignCleanup });
      } else {
        set({ transformMode: mode, ...alignCleanup });
      }
    }
  },
  setRenderSetting: (scope, key, value) => {
    const current = get().renderConfigs;
    const clamped = clampRenderSetting(key, value);
    const nextScopeCfg: RenderConfig = { ...current[scope], [key]: clamped };
    const nextConfigs: Record<RenderScope, RenderConfig> = {
      ...current,
      [scope]: nextScopeCfg,
    };
    const patch: Partial<ReturnType<typeof get>> = {
      renderConfigs: nextConfigs,
    };
    set(patch);
  },
  setActiveRenderSetting: (key, value) => {
    const scope = renderScopeFor(get().workspaceMode);
    get().setRenderSetting(scope, key, value);
  },
  cycleRenderMethod: () => {
    const state = get();
    const scope = renderScopeFor(state.workspaceMode);
    const modes = VIEW_MODES_BY_SCOPE[scope];
    const current = state.renderConfigs[scope].viewMode;
    const idx = modes.indexOf(current);
    const next = modes[(idx + 1) % modes.length];
    state.setRenderSetting(scope, "viewMode", next);
  },
  setViewportQuality: (key, value) => {
    const clamped = clampViewportQuality(key, value);
    set({ viewportQuality: { ...get().viewportQuality, [key]: clamped } });
  },
  setExportQualityOverride: (key, value) => {
    const clamped = clampExportOverride(key, value);
    set({
      exportQualityOverrides: {
        ...get().exportQualityOverrides,
        [key]: clamped,
      },
    });
  },

  setWorkspaceMode: (target) => {
    const state = get();
    if (target === state.workspaceMode) return;

    // Commit any pending live → shadow camera sync before mode-switch side
    // effects read cameraPosition/cameraTarget/cameraUp. Without this, an
    // orbit or fly-mode gesture that ends within the sync debounce window
    // leaves the shadow stale, and downstream reads (exit handlers,
    // viewport policy, etc.) would see a pose the user already moved past.
    flushCameraSync();

    // ── Clean exit from current workspace ──
    if (state.workspaceMode === "edit" && state.editMode) {
      get().exitEditMode();
    }
    if (state.workspaceMode === "pose" && state.poseMode) {
      set({ poseMode: null, transformMode: "rotate" });
    }
    if (
      state.workspaceMode === "reference" &&
      (get().transformMode === "refImage" ||
        get().transformMode === "alignCamera")
    ) {
      const exitingAlign = get().transformMode === "alignCamera";
      set({
        transformMode: "translate",
        alignCameraDrawAxis: null,
        selectedVanishingLineId: null,
        ...(exitingAlign ? { ...exitAlignModeOverrides(get()) } : {}),
      });
    }
    if (state.workspaceMode === "sculpt" && state.sculptMode) {
      get().exitSculptMode();
    }
    if (state.workspaceMode === "sketch" && state.sketchMode) {
      get().exitSketchMode();
    }
    if (state.workspaceMode === "render") {
      set({ showFrustum: false });
    }
    if (state.workspaceMode === "inking") {
      set({ inkingMode: null, showFrustum: false });
    }

    // ── Enter target workspace ──
    switch (target) {
      case "object":
        set({ workspaceMode: "object", mode: "transform" as InteractionMode });
        break;

      case "reference": {
        const patch: Partial<AppState> = {
          workspaceMode: "reference",
        };
        // Only enter refImage transform if a reference layer exists
        if (get().referenceLayers.length > 0) {
          patch.transformMode = "refImage";
          patch.mode = "transform" as InteractionMode;
        }
        set(patch);
        break;
      }

      case "pose": {
        const s = get();
        const mannequins = [...s.sceneObjects.values()].filter(
          (o) => o.objectKind === "mannequin",
        );
        if (mannequins.length === 0) return;
        const activeTarget = getSingleSelectionTarget(s.selectedObjectTargets);
        const activeMannequinId =
          activeTarget?.kind === "sceneObject" &&
          s.sceneObjects.get(activeTarget.id)?.objectKind === "mannequin"
            ? activeTarget.id
            : null;
        const targetId = activeMannequinId ?? mannequins[0].id;
        // enterPoseMode handles poseMode, mode, transformMode, selection, editMode
        get().enterPoseMode(targetId);
        // enterPoseMode already sets workspaceMode via sync (step 3), but set explicitly in case guard fails
        if (get().poseMode) set({ workspaceMode: "pose" });
        break;
      }

      case "edit": {
        const s = get();
        const target = getSingleSelectionTarget(s.selectedObjectTargets);
        if (!target || target.kind !== "sceneObject") return;
        const id = target.id;
        const obj = s.sceneObjects.get(id);
        const collection = getCollectionForObject(s.collections, id);
        if (
          !obj ||
          obj.objectKind === "mannequin" ||
          obj.objectKind === "image_plane" ||
          obj.light ||
          obj.generator ||
          collection?.generatorRef
        )
          return;
        // enterEditMode handles editMode, mode, selection
        get().enterEditMode(id);
        // enterEditMode already sets workspaceMode via sync (step 3), but set explicitly in case guard fails
        if (get().editMode) set({ workspaceMode: "edit" });
        break;
      }

      case "sculpt": {
        const s = get();
        const target = getSingleSelectionTarget(s.selectedObjectTargets);
        if (!target || target.kind !== "sceneObject") return;
        const id = target.id;
        const obj = s.sceneObjects.get(id);
        const collection = getCollectionForObject(s.collections, id);
        if (
          !obj ||
          obj.objectKind === "mannequin" ||
          obj.objectKind === "image_plane" ||
          obj.light ||
          obj.generator ||
          collection?.generatorRef
        )
          return;
        get().enterSculptMode(id);
        if (get().sculptMode) set({ workspaceMode: "sculpt" });
        break;
      }

      case "sketch": {
        // Sketch mode no longer requires a selection — projected points attach
        // to whichever object the cursor hits, or float free in screenspace.
        get().enterSketchMode();
        if (get().sketchMode) set({ workspaceMode: "sketch" });
        break;
      }

      case "render": {
        set({
          workspaceMode: "render",
          showFrustum: true,
          mode: "navigate" as InteractionMode,
        });
        break;
      }

      case "inking":
        set({
          workspaceMode: "inking",
          mode: "navigate" as InteractionMode,
          showFrustum: true,
          inkingMode: {
            baked: false,
            baking: false,
            bakeProgress: 0,
            bakePhase: "init",
            features: { ...DEFAULT_INKING_MODE_SETTINGS.features },
            creaseAngle: DEFAULT_INKING_MODE_SETTINGS.creaseAngle,
            taperStrength: DEFAULT_INKING_MODE_SETTINGS.taperStrength,
            simplificationTolerance:
              DEFAULT_INKING_MODE_SETTINGS.simplificationTolerance,
            cornerAngle: DEFAULT_INKING_MODE_SETTINGS.cornerAngle,
            editSubMode: DEFAULT_INKING_CANVAS_SETTINGS.editSubMode,
            tool: DEFAULT_INKING_CANVAS_SETTINGS.tool,
            size: DEFAULT_INKING_CANVAS_SETTINGS.size,
            opacity: DEFAULT_INKING_CANVAS_SETTINGS.opacity,
            hardness: DEFAULT_INKING_CANVAS_SETTINGS.hardness,
            color: DEFAULT_INKING_CANVAS_SETTINGS.color,
            pressureMode: DEFAULT_INKING_CANVAS_SETTINGS.pressureMode,
            showReference: DEFAULT_INKING_CANVAS_SETTINGS.showReference,
            pan: [...DEFAULT_INKING_CANVAS_SETTINGS.pan] as [number, number],
            zoom: DEFAULT_INKING_CANVAS_SETTINGS.zoom,
            rotation: DEFAULT_INKING_CANVAS_SETTINGS.rotation,
            rasterBuffer: null,
            rasterWidth: 0,
            rasterHeight: 0,
            rasterSeq: 0,
            rasterDirtyFromBake: false,
          },
        });
        break;
    }
  },

  addLight: (kind) => {
    // Switch to object mode if not already there
    if (get().workspaceMode !== "object") {
      get().setWorkspaceMode("object");
    }
    const kindNames: Record<string, string> = {
      point: "Point Light",
      directional: "Directional Light",
    };
    const objectKinds: Record<string, string> = {
      point: "point_light",
      directional: "directional_light",
    };
    const kindName = kindNames[kind] ?? "Light";
    const objectKind = objectKinds[kind] as SceneObject["objectKind"];
    // Count existing lights of this kind to generate a unique name
    const existing = [...get().sceneObjects.values()].filter(
      (o) => o.objectKind === objectKind,
    );
    const name =
      existing.length === 0
        ? kindName
        : `${kindName}.${String(existing.length + 1).padStart(3, "0")}`;
    const lightSpawn = cursorSpawnPosition(get().cursor3D);
    const lightMatId = get().createMaterial();
    const lightObj: SceneObject = {
      id: createSceneObjectId(),
      name,
      objectKind,
      position: lightSpawn,
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      vertices: new Float32Array(0),
      indices: EMPTY_RENDER_TRIANGLE_INDICES,
      loopNormals: asLoopNormals(new Float32Array(0)),
      topology: {
        offsets: new Uint32Array([0]),
        corners: new Uint32Array(0),
        edges: new Uint32Array(0),
        cornerEdges: new Uint32Array(0),
        edgeFlags: new Uint8Array(0),
      },
      materialSlots: createDefaultMaterialSlots(lightMatId),
      shading: "smooth",
      light: {
        color: "#ffffff",
        intensity: kind === "directional" ? 0.8 : 10,
      },
    };
    const lightAdd = addSceneObject(get().sceneObjects, lightObj);
    if (!lightAdd.admitted) return;
    set({
      sceneObjects: lightAdd.map,
      selectedObjectTargets: [{ kind: "sceneObject", id: lightObj.id }],
      mode: "transform" as InteractionMode,
    });
  },

  updateSceneObjectLight: (id, patch) => {
    const obj = get().sceneObjects.get(id);
    if (!obj || !obj.light) return;
    set({
      sceneObjects: patchSceneObject(get().sceneObjects, id, {
        light: { ...obj.light, ...patch },
      }),
    });
  },

  addImagePlane: (imageKey, intrinsicWidth, intrinsicHeight, name) => {
    if (get().workspaceMode !== "object") {
      get().setWorkspaceMode("object");
    }

    // View-aligned, axis-snapped orientation. We pick the world axis nearest
    // to the camera's forward direction and build a rotation that maps:
    //   - local +Z (the PlaneGeometry normal) onto the world axis pointing
    //     back at the camera, so the image faces the viewer; and
    //   - local +Y (the image's "up" edge) onto world +Z whenever possible,
    //     so the image is never rendered upside-down.
    // Single-axis Euler rotations satisfy the first constraint but not the
    // second when the plane stands upright (normal along ±X or ±Y) — local
    // +Y ends up along world ±Y or ±Z arbitrarily, manifesting as a vertical
    // flip in side and front views. Building the basis explicitly via
    // `Matrix4.lookAt` keeps the image upright in every camera-axis case.
    const s = get();
    const fx = s.cameraTarget[0] - s.cameraPosition[0];
    const fy = s.cameraTarget[1] - s.cameraPosition[1];
    const fz = s.cameraTarget[2] - s.cameraPosition[2];
    const ax = Math.abs(fx);
    const ay = Math.abs(fy);
    const az = Math.abs(fz);
    const normalDir = new THREE.Vector3();
    if (ay >= ax && ay >= az) {
      normalDir.set(0, fy > 0 ? -1 : 1, 0);
    } else if (ax >= az) {
      normalDir.set(fx > 0 ? -1 : 1, 0, 0);
    } else {
      normalDir.set(0, 0, fz > 0 ? -1 : 1);
    }
    // World "up" hint for the lookAt basis. When the plane normal is itself
    // along world ±Z (top/bottom view), fall back to world +Y so the up
    // vector isn't degenerate with the look direction.
    const upHint =
      Math.abs(normalDir.z) > 0.5
        ? new THREE.Vector3(0, 1, 0)
        : new THREE.Vector3(0, 0, 1);
    const lookMat = new THREE.Matrix4().lookAt(
      normalDir,
      new THREE.Vector3(0, 0, 0),
      upHint,
    );
    const eul = new THREE.Euler().setFromRotationMatrix(lookMat);
    const rotation: [number, number, number] = [eul.x, eul.y, eul.z];

    // Aspect is baked into the renderer (an inner group sized to the image's
    // intrinsic aspect, with the largest side = 1 world unit). The
    // user-facing `scale` field stays at [1, 1, 1] so the Transform panel
    // shows clean defaults — Blender-style "applied" scale, where the
    // resting 1.0 already encodes the image proportions.
    const w = Math.max(1, intrinsicWidth);
    const h = Math.max(1, intrinsicHeight);

    const existing = [...s.sceneObjects.values()].filter(
      (o) => o.objectKind === "image_plane",
    );
    const baseName = name && name.length > 0 ? name : "Image";
    const displayName =
      existing.length === 0
        ? baseName
        : `${baseName}.${String(existing.length + 1).padStart(3, "0")}`;
    const matId = get().createMaterial();
    const obj: SceneObject = {
      id: createSceneObjectId(),
      name: displayName,
      objectKind: "image_plane",
      position: cursorSpawnPosition(s.cursor3D),
      rotation,
      scale: [1, 1, 1],
      vertices: new Float32Array(0),
      indices: EMPTY_RENDER_TRIANGLE_INDICES,
      loopNormals: asLoopNormals(new Float32Array(0)),
      topology: {
        offsets: new Uint32Array([0]),
        corners: new Uint32Array(0),
        edges: new Uint32Array(0),
        cornerEdges: new Uint32Array(0),
        edgeFlags: new Uint8Array(0),
      },
      materialSlots: createDefaultMaterialSlots(matId),
      shading: "smooth",
      imagePlane: {
        imageKey,
        intrinsicWidth: w,
        intrinsicHeight: h,
      },
    };
    const added = addSceneObject(get().sceneObjects, obj);
    if (!added.admitted) return;
    set({
      sceneObjects: added.map,
      selectedObjectTargets: [{ kind: "sceneObject", id: obj.id }],
      mode: "transform" as InteractionMode,
    });
  },

  addMannequin: (modelId) => {
    // Switch to object mode if not already there
    if (get().workspaceMode !== "object") {
      get().setWorkspaceMode("object");
    }
    const model = MANNEQUIN_MODELS[modelId];
    if (!model) return;
    const existing = [...get().sceneObjects.values()].filter(
      (o) => o.objectKind === "mannequin",
    );
    const name =
      existing.length === 0
        ? "Mannequin"
        : `Mannequin.${String(existing.length + 1).padStart(3, "0")}`;
    const mannequinMatId = get().createMaterial();
    const obj: SceneObject = {
      id: createSceneObjectId(),
      name,
      objectKind: "mannequin",
      position: cursorSpawnPosition(get().cursor3D),
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      vertices: new Float32Array(0),
      indices: EMPTY_RENDER_TRIANGLE_INDICES,
      loopNormals: asLoopNormals(new Float32Array(0)),
      topology: {
        offsets: new Uint32Array([0]),
        corners: new Uint32Array(0),
        edges: new Uint32Array(0),
        cornerEdges: new Uint32Array(0),
        edgeFlags: new Uint8Array(0),
      },
      materialSlots: createDefaultMaterialSlots(mannequinMatId),
      shading: "smooth",
      mannequinModelId: modelId,
    };
    const morphs = new Map(get().mannequinMorphs);
    const weights = new Map<string, number>();
    for (const m of model.morphs) {
      weights.set(m.name, m.default);
    }
    morphs.set(obj.id, weights);
    const mannequinAdd = addSceneObject(get().sceneObjects, obj);
    if (!mannequinAdd.admitted) return;
    set({
      sceneObjects: mannequinAdd.map,
      mannequinMorphs: morphs,
      selectedObjectTargets: [{ kind: "sceneObject", id: obj.id }],
      mode: "transform" as InteractionMode,
    });
  },

  setMannequinModelId: (objectId, modelId) => {
    const objects = get().sceneObjects;
    const obj = objects.get(objectId);
    if (!obj || obj.objectKind !== "mannequin") return;
    set({
      sceneObjects: patchSceneObject(objects, objectId, {
        mannequinModelId: modelId,
      }),
    });
  },

  setMannequinMorph: (objectId, morphName, weight) => {
    const morphs = new Map(get().mannequinMorphs);
    const weights = new Map(morphs.get(objectId) ?? new Map());
    weights.set(morphName, weight);
    morphs.set(objectId, weights);
    set({ mannequinMorphs: morphs });
  },

  enterPoseMode: (objectId) => {
    if (!objectId) return;

    const obj = get().sceneObjects.get(objectId);
    if (obj?.objectKind === "mannequin") {
      // Mirror exitEditMode: cancel any in-flight command before a mode
      // transition so a stale commandBuffer/snapshot can't leak across.
      if (get().commandBuffer) get().cancelCommand();
      clearLiveEditPreview();
      set({
        poseMode: { objectId, selectedBones: new Set(), activeBone: null },
        mode: "transform" as InteractionMode,
        transformMode: "rotate",
        selectedObjectTargets: [{ kind: "sceneObject", id: objectId }],
        editMode: null,
        activeEditSession: null,
        workspaceMode: "pose",
      });
    }
  },

  exitPoseMode: () => {
    set({
      poseMode: null,
      transformMode: "rotate",
      workspaceMode: "object",
    });
  },

  selectBone: (boneName, objectId, additive) => {
    const pm = get().poseMode;
    if (!pm) return;

    let selectedBones: Set<string>;
    let activeBone: string | null;

    if (boneName === null) {
      // Deselect all
      selectedBones = new Set();
      activeBone = null;
    } else if (additive) {
      // Shift+click: toggle bone in selection
      selectedBones = new Set(pm.selectedBones);
      if (selectedBones.has(boneName)) {
        selectedBones.delete(boneName);
        activeBone = selectedBones.size > 0 ? [...selectedBones].at(-1)! : null;
      } else {
        selectedBones.add(boneName);
        activeBone = boneName;
      }
    } else {
      // Normal click: exclusive select
      selectedBones = new Set([boneName]);
      activeBone = boneName;
    }

    const resolvedId = objectId ?? pm.objectId;
    const patch: Partial<AppState> = {
      poseMode: { ...pm, selectedBones, activeBone, objectId: resolvedId },
    };
    // Keep selection in sync with the posed mannequin
    const currentTargets = get().selectedObjectTargets;
    const alreadyTargeted =
      currentTargets.length === 1 &&
      currentTargets[0].kind === "sceneObject" &&
      currentTargets[0].id === resolvedId;
    if (!alreadyTargeted) {
      patch.selectedObjectTargets = [{ kind: "sceneObject", id: resolvedId }];
    }
    // Cursor selection is mutually exclusive with bone selection.
    if (activeBone !== null && get().cursor3D.selected) {
      patch.cursor3D = { ...get().cursor3D, selected: false };
    }
    set(patch);
  },

  setBonePose: (objectId, boneName, quaternion) => {
    const poses = new Map(get().mannequinBonePoses);
    const bones = new Map(poses.get(objectId) ?? new Map());
    bones.set(boneName, quaternion);
    poses.set(objectId, bones);
    set({ mannequinBonePoses: poses });
  },

  setBonePoses: (objectId, newPoses) => {
    const poses = new Map(get().mannequinBonePoses);
    const bones = new Map(poses.get(objectId) ?? new Map());
    for (const [name, q] of newPoses) bones.set(name, q);
    poses.set(objectId, bones);
    set({ mannequinBonePoses: poses });
  },

  mirrorBonePose: (objectId, boneName) => {
    // Swap L↔R in bone name to find sibling
    const mirrorName = (name: string): string | null => {
      // Try replacing L→R or R→L at common positions
      // Bone names: shoulderL, thighR, finger_indexL1, etc.
      if (name.includes("L")) return name.replace(/L/g, "R");
      if (name.includes("R")) return name.replace(/R/g, "L");
      return null;
    };

    const targetName = mirrorName(boneName);
    if (!targetName) return;

    get().pushUndo();
    const poses = new Map(get().mannequinBonePoses);
    const bonePoses = new Map(poses.get(objectId) ?? new Map());

    // Mirror the quaternion: negate Y and Z to reflect across the sagittal plane
    const mirrorQuat = (
      q: [number, number, number, number],
    ): [number, number, number, number] => [q[0], -q[1], -q[2], q[3]];

    // Mirror the selected bone
    const srcQ = bonePoses.get(boneName);
    if (srcQ) bonePoses.set(targetName, mirrorQuat(srcQ));

    // Mirror children: find all bones that start with the same prefix
    // e.g., selecting shoulderL mirrors upper_armL, forearmL, handL, finger_*L*
    const srcSide = boneName.includes("L") ? "L" : "R";
    for (const [name, q] of bonePoses) {
      if (name === boneName) continue;
      if (!name.includes(srcSide)) continue;
      const mirrored = mirrorName(name);
      if (mirrored && mirrored !== name) {
        bonePoses.set(mirrored, mirrorQuat(q));
      }
    }

    poses.set(objectId, bonePoses);
    set({ mannequinBonePoses: poses });
  },

  drop: (objectId) => {
    const state = get();
    const obj = state.sceneObjects.get(objectId);
    if (!obj) return;

    const pm = state.poseMode;

    // ====================================================================
    // Object mode — drop the object onto the nearest surface below it
    // (or Z = 0 if none is found).
    // ====================================================================
    if (!pm) {
      const worldPoints = collectDropSamplePoints(state, obj);
      const delta = computeDropDelta(
        worldPoints,
        new Set([objectId]),
        state.sceneObjects,
        state.collections,
      );
      if (!delta) return;

      const worldTransform = sceneObjectWorldTransform(state, obj);
      state.pushUndo();
      const newPosition: [number, number, number] = [
        worldTransform.position[0],
        worldTransform.position[1],
        worldTransform.position[2] - delta.minZ + delta.surfaceZ,
      ];
      set({
        sceneObjects: patchSceneObjectWorldTransform(state, objectId, {
          position: newPosition,
          rotation: worldTransform.rotation,
          scale: worldTransform.scale,
        }),
      });
      return;
    }

    // ====================================================================
    // Pose mode — bone-level surface drop / IK.
    // ====================================================================
    const boneMap = getBoneMap(objectId);
    if (!boneMap) return;

    const selectedBones = pm.selectedBones;
    if (selectedBones.size === 0) return;

    const hasRoot = selectedBones.has("root");

    state.pushUndo();

    if (hasRoot) {
      // ------------------------------------------------------------------
      // Root bone selected: translate the scene object so its lowest joint
      // sits on the nearest surface below it (or Z = 0).
      // ------------------------------------------------------------------
      let minZ = Infinity;
      const bonePositions: THREE.Vector3[] = [];
      for (const bone of boneMap.values()) {
        const pos = new THREE.Vector3();
        bone.getWorldPosition(pos);
        if (pos.z < minZ) minZ = pos.z;
        bonePositions.push(pos);
      }

      if (!Number.isFinite(minZ)) return;

      // Keep only bones in the bottom region for ray sampling.
      const maxBoneZ = bonePositions.reduce(
        (m, p) => Math.max(m, p.z),
        -Infinity,
      );
      const boneHeight = maxBoneZ - minZ;
      const threshold = minZ + Math.max(boneHeight * 0.05, 0.001);
      const bottomBones = bonePositions.filter((p) => p.z <= threshold);

      const surfaceZ =
        raycastDownZ(
          bottomBones,
          new Set([objectId]),
          state.sceneObjects,
          state.collections,
        ) ?? 0;

      const worldTransform = sceneObjectWorldTransform(state, obj);
      const newPosition: [number, number, number] = [
        worldTransform.position[0],
        worldTransform.position[1],
        worldTransform.position[2] - minZ + surfaceZ,
      ];
      set({
        sceneObjects: patchSceneObjectWorldTransform(state, objectId, {
          position: newPosition,
          rotation: worldTransform.rotation,
          scale: worldTransform.scale,
        }),
      });
    } else {
      // ------------------------------------------------------------------
      // Non-root bones: use IK to move each selected bone toward the
      // nearest surface below it (or Z = 0).
      // ------------------------------------------------------------------
      const poses = new Map(state.mannequinBonePoses);
      const bonePoses = new Map(poses.get(objectId) ?? new Map());
      const lockedSet = state.lockedBones.get(objectId);

      for (const boneName of selectedBones) {
        if (lockedSet?.has(boneName)) continue;

        const chainDef = findChainForBone(boneName);
        if (!chainDef) continue;

        const chainState = captureChainState(boneMap, chainDef.chain);
        if (!chainState) continue;

        const origPositions = chainState.positions.map((p) => p.clone());

        const effectorIdx = chainDef.effectorIndex;
        const effectorPos = origPositions[effectorIdx];

        // Single effector — one ray is sufficient for a bone tip.
        const surfaceZ =
          raycastDownZ(
            [effectorPos],
            new Set([objectId]),
            state.sceneObjects,
            state.collections,
          ) ?? 0;

        if (effectorPos.z <= surfaceZ) continue;

        const target = effectorPos.clone();
        target.z = surfaceZ;

        const solvedQuats = solveJacobianIK(chainState, target, {
          objectId,
          excludeLastBone: true,
        });
        solvedQuats.delete(boneName);

        for (const [name, q] of solvedQuats) {
          if (lockedSet?.has(name)) continue;
          let quat = new THREE.Quaternion(q[0], q[1], q[2], q[3]);
          const boneRestQ = getRestPose(objectId, name);
          quat = applyConstraint(name, quat, boneRestQ);
          bonePoses.set(name, [quat.x, quat.y, quat.z, quat.w]);
        }

        const directBones = new Set(solvedQuats.keys());
        const secondary = applySecondaryMotion(
          objectId,
          directBones,
          bonePoses,
        );
        for (const [name, q] of secondary) bonePoses.set(name, q);
      }

      poses.set(objectId, bonePoses);
      set({ mannequinBonePoses: poses });
    }
  },

  dropCollection: (collectionId) => {
    const state = get();
    const collection = state.collections.get(collectionId);
    if (!collection || collection.objectIds.size === 0) return;

    // Aggregate world-space sample points across every member so the
    // group lands as a single rigid unit — one child's bottom sets the
    // contact point, others come along via the shared group transform.
    const worldPoints: THREE.Vector3[] = [];
    const memberIds = new Set(collection.objectIds);
    for (const memberId of collection.objectIds) {
      const memberObj = state.sceneObjects.get(memberId);
      if (!memberObj) continue;
      for (const p of collectDropSamplePoints(state, memberObj)) {
        worldPoints.push(p);
      }
    }

    const delta = computeDropDelta(
      worldPoints,
      memberIds,
      state.sceneObjects,
      state.collections,
    );
    if (!delta) return;

    const dz = delta.surfaceZ - delta.minZ;
    if (dz === 0) return;

    state.pushUndo();
    const nextCollections = new Map(state.collections);
    const nextTransform = cloneGroupTransform(collection.transform);
    nextTransform.position = [
      nextTransform.position[0],
      nextTransform.position[1],
      nextTransform.position[2] + dz,
    ];
    nextCollections.set(collectionId, {
      ...collection,
      transform: nextTransform,
    });
    set({ collections: nextCollections });
  },

  simulateGravity: async (objectId, options) => {
    const state = get();
    const obj = state.sceneObjects.get(objectId);
    if (!obj) return;

    const compliance = options?.compliance ?? 0;
    const tautness = options?.tautness ?? 1;
    const pinGroupId = options?.pinGroupId;
    const skipUndo = options?.skipUndo ?? false;
    // `substepFraction` clamps the solver's substep budget so the preview
    // slider can scrub a mid-drape timeline. `1` = fully settled drape,
    // `0` = zero substeps (= baseline), intermediate = partial settle.
    // `maxSubsteps` (when set) overrides — used by the menu's opt-in
    // time-range bake to define an explicit end-substep.
    const substepFraction = Math.max(
      0,
      Math.min(1, options?.substepFraction ?? 1),
    );
    const maxSubsteps =
      options?.maxSubsteps != null
        ? Math.max(0, Math.floor(options.maxSubsteps))
        : Math.round(DEFAULT_SETTLE_PARAMS.maxSubsteps * substepFraction);
    // Trajectory recorder inputs. The menu passes `recordStride > 0` once
    // per bake (on settings change); subsequent Time-slider scrubs go
    // through `applyGravityFrame` instead and never re-enter this path.
    const recordStride = Math.max(0, options?.recordStride ?? 0);
    const onBakeComplete = options?.onBakeComplete;
    const shouldBake = recordStride > 0 && onBakeComplete != null;
    // When a baseline is supplied, the simulation runs against it rather
    // than against the current (possibly already-simulated) object state.
    // This keeps the live-preview slider from compounding each drag frame
    // on top of the previous result.
    const baselineCurveData = options?.baseline?.curveData;
    const baselineVertices = options?.baseline?.vertices;
    // Worker plumbing: every solve runs in `physics.worker.ts` so the
    // main thread doesn't block. Cancellation + progress flow through.
    const signal = options?.signal;
    const onProgress = options?.onProgress;
    const recordStartSubstep = Math.max(
      0,
      Math.floor(options?.recordStartSubstep ?? 0),
    );
    // Progress-callback cadence is inversely interesting: the trajectory
    // bake drives a determinate ProgressBar and wants a tight stride, the
    // endpoint bake drives an indeterminate bar and only needs callbacks
    // for cancellation. Widening the stride for the endpoint path cuts
    // per-solve postMessage overhead enough to matter on short solves.
    const progressStride = shouldBake ? 4 : 30;

    // Lights and mannequins are not draped — they don't have a vertex chain
    // semantically meaningful to gravity simulation.
    if (isLightObject(obj) || obj.objectKind === "mannequin") return;

    // Object's full world transform — same composition Drop and the raycaster
    // use. position × rotationFromEuler × scale × translate(originOffset).
    const worldTransform = sceneObjectWorldTransform(state, obj);
    const [rx, ry, rz] = worldTransform.rotation;
    const [sx, sy, sz] = worldTransform.scale;
    const worldMatrix = new THREE.Matrix4().compose(
      new THREE.Vector3(...worldTransform.position),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz)),
      new THREE.Vector3(sx, sy, sz),
    );
    if (obj.originOffset) {
      worldMatrix.multiply(
        new THREE.Matrix4().makeTranslation(...obj.originOffset),
      );
    }
    if (Math.abs(sx) < 1e-12 || Math.abs(sy) < 1e-12 || Math.abs(sz) < 1e-12) {
      console.warn(
        "simulateGravity: object has zero scale on one or more axes, skipping",
      );
      return;
    }
    const inverseWorld = new THREE.Matrix4().copy(worldMatrix).invert();

    const em = state.editMode?.objectId === objectId ? state.editMode : null;

    // ====================================================================
    // Curve dispatch — operate on curveData.points
    // ====================================================================
    if (
      isCurveObject(obj) &&
      obj.curveData &&
      obj.curveData.points.length >= 2
    ) {
      const curveData = baselineCurveData ?? obj.curveData;
      const n = curveData.points.length;

      // Build particles in world space.
      const positions = new Float64Array(n * 3);
      const tmp = new THREE.Vector3();
      for (let i = 0; i < n; i++) {
        const co = curveData.points[i].co;
        tmp.set(co[0], co[1], co[2]).applyMatrix4(worldMatrix);
        positions[i * 3] = tmp.x;
        positions[i * 3 + 1] = tmp.y;
        positions[i * 3 + 2] = tmp.z;
      }

      // Edges = consecutive pairs, plus closing pair if cyclic.
      const edgeCount = curveData.cyclic ? n : n - 1;
      const edges = new Uint32Array(edgeCount * 2);
      for (let i = 0; i < n - 1; i++) {
        edges[i * 2] = i;
        edges[i * 2 + 1] = i + 1;
      }
      if (curveData.cyclic) {
        edges[(n - 1) * 2] = n - 1;
        edges[(n - 1) * 2 + 1] = 0;
      }

      // Pinning rules (see storeTypes.ts comment).
      const pinned = new Uint8Array(n);
      if (em?.isCurve) {
        // Edit mode: pin user-selected control points (vi % 3 === 0 are the
        // co indices in the flat curveEditVertices layout).
        let anyPinned = false;
        for (const vi of em.selectedVertices) {
          if (vi % 3 === 0) {
            const pi = vi / 3;
            if (pi >= 0 && pi < n) {
              pinned[pi] = 1;
              anyPinned = true;
            }
          }
        }
        if (!anyPinned) {
          // Default: pin first control point so the rope hangs from there.
          pinned[0] = 1;
        }
      } else if (!curveData.cyclic) {
        // Object mode, open curve: pin both endpoints.
        pinned[0] = 1;
        pinned[n - 1] = 1;
      }
      // Cyclic curves in object mode get no pins (let the loop fall and coil).

      // Bail if everything is pinned — no work to do, no undo push.
      let anyFree = false;
      for (let i = 0; i < n; i++) {
        if (!pinned[i]) {
          anyFree = true;
          break;
        }
      }
      if (!anyFree) return;

      // Build collision meshes once and run the simulation in the worker.
      const collisionMeshes = buildCollisionMeshes(
        new Set([objectId]),
        state.sceneObjects,
        state.collections,
      );
      // Snapshot pre-sim state for undo BEFORE awaiting the worker — the
      // user's history target is "back to before the drape", not "back
      // to whatever happened to be in flight".
      if (!skipUndo) state.pushUndo();
      let result: SettleOutput | undefined;
      try {
        result = await settleParticlesAsync(
          {
            positions,
            edges,
            pinned,
            collisionMeshes,
            params: { compliance, tautness, maxSubsteps },
          },
          { signal, onProgress, recordStartSubstep, progressStride },
        );
      } catch (err) {
        disposeCollisionMeshes(collisionMeshes);
        if (err instanceof GravitySimCancelledError) return;
        throw err;
      }
      disposeCollisionMeshes(collisionMeshes);
      const resolvedPositions = result.positions;

      if (process.env.NODE_ENV !== "production") {
        console.log(
          `simulateGravity (curve): ${result.substepsRun} substeps, max edge dev ${result.maxEdgeDeviation.toFixed(6)}`,
        );
      }

      // Re-fetch state — anything could have changed during the await.
      const curveLatestState = get();
      const curveLatestObj = curveLatestState.sceneObjects.get(objectId);
      if (
        !curveLatestObj ||
        !isCurveObject(curveLatestObj) ||
        !curveLatestObj.curveData
      )
        return;
      if (curveLatestObj.curveData.points.length !== n) return;

      // Write back: convert each particle back to object-local space, copy
      // the local-space delta to the handles, and recompute auto/vector/aligned
      // handles via enforceHandleConstraints.
      const newPoints: CurvePoint[] = curveData.points.map((pt, i) => {
        tmp
          .set(
            resolvedPositions[i * 3],
            resolvedPositions[i * 3 + 1],
            resolvedPositions[i * 3 + 2],
          )
          .applyMatrix4(inverseWorld);
        const newCo: [number, number, number] = [tmp.x, tmp.y, tmp.z];
        const dx = newCo[0] - pt.co[0];
        const dy = newCo[1] - pt.co[1];
        const dz = newCo[2] - pt.co[2];
        return {
          ...pt,
          co: newCo,
          handleLeft: [
            pt.handleLeft[0] + dx,
            pt.handleLeft[1] + dy,
            pt.handleLeft[2] + dz,
          ],
          handleRight: [
            pt.handleRight[0] + dx,
            pt.handleRight[1] + dy,
            pt.handleRight[2] + dz,
          ],
        };
      });

      const allIndices = new Array(n);
      for (let i = 0; i < n; i++) allIndices[i] = i;
      enforceHandleConstraintsBatch(newPoints, allIndices, curveData.cyclic);

      const updated: CurveData = { ...curveData, points: newPoints };

      const nextObjects = regenerateCurveGeometryInMap(
        curveLatestState.sceneObjects,
        objectId,
        updated,
      );
      const curveLatestEm =
        curveLatestState.editMode?.objectId === objectId
          ? curveLatestState.editMode
          : null;
      if (curveLatestEm?.isCurve) {
        set({
          sceneObjects: nextObjects,
          editMode: {
            ...curveLatestEm,
            curveEditVertices: buildCurveEditVertices(updated),
          },
        });
      } else {
        set({ sceneObjects: nextObjects });
      }
      return;
    }

    // ====================================================================
    // Mesh dispatch — routes to the wire-chain path (skin-modifier chains)
    // when any wire edges exist, or to the cloth path (bedsheets, table-
    // cloths) when only faces exist. Mixed meshes keep wire-chain semantics
    // for backward compatibility — the existing path already pins face-
    // participating verts, which is the right default for a skin armature
    // with decorative geometry.
    // ====================================================================
    if (!obj.vertices || obj.vertices.length === 0 || !obj.topology) return;
    const sourceVertices = baselineVertices ?? obj.vertices;
    const topology = obj.topology;
    const edgeFlags = topology.edgeFlags;
    const topologyEdges = topology.edges;
    const totalEdges = edgeFlags.length;

    // Collect wire-edge indices.
    const wireEdgeList: number[] = [];
    for (let e = 0; e < totalEdges; e++) {
      if (edgeFlags[e] & EDGE_WIRE) wireEdgeList.push(e);
    }

    // ====================================================================
    // Cloth dispatch — purely faced mesh (no wire edges). Runs full cloth
    // simulation: structural distance constraints on every mesh edge,
    // bending constraints across triangle neighbour pairs, and optional
    // particle–triangle self-collision. Pinning rules mirror the wire
    // path but drop the face-autopin (everything is a face in cloth).
    // ====================================================================
    if (wireEdgeList.length === 0) {
      if (topology.offsets.length <= 1) return; // no faces, no wires, nothing to sim
      const vertexCount = sourceVertices.length / 3;

      // Build particles in world space.
      const clothPositions = new Float64Array(vertexCount * 3);
      const clothTmp = new THREE.Vector3();
      for (let i = 0; i < vertexCount; i++) {
        clothTmp
          .set(
            sourceVertices[i * 3],
            sourceVertices[i * 3 + 1],
            sourceVertices[i * 3 + 2],
          )
          .applyMatrix4(worldMatrix);
        clothPositions[i * 3] = clothTmp.x;
        clothPositions[i * 3 + 1] = clothTmp.y;
        clothPositions[i * 3 + 2] = clothTmp.z;
      }

      // Derive structural edges, bending pairs, and triangles.
      const cloth = buildClothTopology(topology, obj.indices);

      // Pinning: explicit pin group > edit-mode selection > object-mode no pins.
      const clothPinned = new Uint8Array(vertexCount);
      const clothPinGroup = pinGroupId
        ? obj.vertexGroups?.find((g) => g.id === pinGroupId)
        : undefined;
      const useClothPinGroup =
        clothPinGroup != null && clothPinGroup.indices.size > 0;
      if (useClothPinGroup) {
        for (const vi of clothPinGroup.indices) {
          if (vi >= 0 && vi < vertexCount) clothPinned[vi] = 1;
        }
      } else if (em && !em.isCurve) {
        for (const vi of em.selectedVertices) {
          if (vi >= 0 && vi < vertexCount) clothPinned[vi] = 1;
        }
      }
      // Object mode with no pin group: no auto-pins. Cloth drops freely
      // and settles against whatever lies below.

      // Self-collision thickness: the option value is a world-unit length
      // (0 disables). The menu converts its percentage slider into this
      // absolute measure using the mesh bounding box.
      const selfCollisionThickness = Math.max(
        0,
        options?.selfCollisionThickness ?? 0,
      );
      const clothBendingCompliance =
        options?.bendingCompliance ?? DEFAULT_SETTLE_PARAMS.bendingCompliance;

      const clothCollisionMeshes = buildCollisionMeshes(
        new Set([objectId]),
        state.sceneObjects,
        state.collections,
      );
      if (!skipUndo) state.pushUndo();
      let clothResult: SettleOutput | undefined;
      try {
        clothResult = await settleParticlesAsync(
          {
            positions: clothPositions,
            edges: cloth.structuralEdges,
            pinned: clothPinned,
            collisionMeshes: clothCollisionMeshes,
            bendingEdges: cloth.bendingPairs,
            triangles: cloth.triangles,
            recordTrajectory: shouldBake
              ? { stride: recordStride, maxFrames: GRAVITY_BAKE_MAX_FRAMES }
              : undefined,
            params: {
              compliance,
              bendingCompliance: clothBendingCompliance,
              tautness,
              maxSubsteps,
              selfCollisionThickness,
              settleExtensionSubsteps: shouldBake
                ? GRAVITY_BAKE_SETTLE_EXTENSION
                : 0,
            },
          },
          { signal, onProgress, recordStartSubstep, progressStride },
        );
      } catch (err) {
        disposeCollisionMeshes(clothCollisionMeshes);
        if (err instanceof GravitySimCancelledError) return;
        throw err;
      }
      disposeCollisionMeshes(clothCollisionMeshes);
      const resolvedClothPositions = clothResult.positions;

      if (process.env.NODE_ENV !== "production") {
        console.log(
          `simulateGravity (cloth): ${clothResult.substepsRun} substeps, ` +
            `${cloth.structuralEdges.length / 2} structural + ` +
            `${cloth.bendingPairs.length / 2} bending edges, ` +
            `max edge dev ${clothResult.maxEdgeDeviation.toFixed(6)}` +
            (clothResult.trajectory
              ? `, baked ${clothResult.trajectory.frameCount} frames`
              : ""),
        );
      }

      // Re-fetch state — the await may have spanned an outside mutation.
      const clothLatestState = get();
      const clothLatestObj = clothLatestState.sceneObjects.get(objectId);
      if (
        !clothLatestObj ||
        !clothLatestObj.vertices ||
        clothLatestObj.vertices.length !== sourceVertices.length ||
        !clothLatestObj.topology
      )
        return;

      // Write back — only free verts; pinned ones stayed at their anchors
      // and writing them back still round-trips through the matrix inverse
      // (avoid the drift).
      const clothNewVerts = new Float32Array(sourceVertices);
      for (let i = 0; i < vertexCount; i++) {
        if (clothPinned[i]) continue;
        clothTmp
          .set(
            resolvedClothPositions[i * 3],
            resolvedClothPositions[i * 3 + 1],
            resolvedClothPositions[i * 3 + 2],
          )
          .applyMatrix4(inverseWorld);
        clothNewVerts[i * 3] = clothTmp.x;
        clothNewVerts[i * 3 + 1] = clothTmp.y;
        clothNewVerts[i * 3 + 2] = clothTmp.z;
      }

      // Convert the recorded trajectory (world-space Float64) into local-
      // space Float32 frames the menu can interpolate and commit directly.
      // Pinned verts are kept at the baseline, identical to the final-
      // frame treatment above — matches the bake's frame 0 and avoids
      // drift when scrubbing back to t=0.
      if (shouldBake && clothResult.trajectory) {
        onBakeComplete!(
          bakeFramesToLocalSpace(
            objectId,
            clothResult.trajectory.frames,
            clothResult.trajectory.frameCount,
            vertexCount,
            clothPinned,
            sourceVertices,
            inverseWorld,
          ),
        );
      }

      // Recompute loop normals against the deformed verts — `patchSceneObject`
      // is a dumb shallow merge, so without this the renderer keeps the
      // pre-sim normals and shading looks wrong until the user toggles it.
      const clothLoopNormals = recomputeLoopNormals(
        clothNewVerts,
        clothLatestObj.topology,
        resolveAutoSmoothAngle(
          clothLatestObj.shading,
          clothLatestObj.autoSmoothAngle,
        ),
      );

      set({
        sceneObjects: patchSceneObject(
          clothLatestState.sceneObjects,
          objectId,
          {
            vertices: clothNewVerts,
            loopNormals: clothLoopNormals,
          },
        ),
      });
      return;
    }

    const vertexCount = sourceVertices.length / 3;

    // Per-vertex wire valence + face-membership flags.
    const wireValence = new Uint32Array(vertexCount);
    for (const e of wireEdgeList) {
      wireValence[topologyEdges[e * 2]]++;
      wireValence[topologyEdges[e * 2 + 1]]++;
    }
    const inFace = new Uint8Array(vertexCount);
    for (let c = 0; c < topology.corners.length; c++) {
      inFace[topology.corners[c]] = 1;
    }

    // Build particle world positions.
    const positions = new Float64Array(vertexCount * 3);
    const tmp = new THREE.Vector3();
    for (let i = 0; i < vertexCount; i++) {
      tmp
        .set(
          sourceVertices[i * 3],
          sourceVertices[i * 3 + 1],
          sourceVertices[i * 3 + 2],
        )
        .applyMatrix4(worldMatrix);
      positions[i * 3] = tmp.x;
      positions[i * 3 + 1] = tmp.y;
      positions[i * 3 + 2] = tmp.z;
    }

    // Build edge buffer (only wire edges).
    const edges = new Uint32Array(wireEdgeList.length * 2);
    for (let i = 0; i < wireEdgeList.length; i++) {
      const e = wireEdgeList[i];
      edges[i * 2] = topologyEdges[e * 2];
      edges[i * 2 + 1] = topologyEdges[e * 2 + 1];
    }

    // Per-vertex pinning. A vertex is auto-pinned when it participates in
    // any face (would deform face geometry) or has no wire incidence (no
    // simulation work for it). The remaining "free" set is the wire chain
    // proper.
    const pinned = new Uint8Array(vertexCount);
    for (let i = 0; i < vertexCount; i++) {
      if (inFace[i] || wireValence[i] === 0) pinned[i] = 1;
    }

    // Resolve the user pin layer. A valid pin group (non-empty, found on
    // this object) replaces the default endpoint / selection rules
    // entirely. The structural auto-pins above still apply on top.
    const pinGroup = pinGroupId
      ? obj.vertexGroups?.find((g) => g.id === pinGroupId)
      : undefined;
    const usePinGroup = pinGroup != null && pinGroup.indices.size > 0;

    if (usePinGroup) {
      for (const vi of pinGroup.indices) {
        if (vi >= 0 && vi < vertexCount) pinned[vi] = 1;
      }
    } else if (em && !em.isCurve) {
      // Edit mode: also pin every selected vertex (user-chosen anchors).
      for (const vi of em.selectedVertices) {
        if (vi >= 0 && vi < vertexCount) pinned[vi] = 1;
      }
    } else if (!em) {
      // Object mode: pin chain endpoints (valence-1 wire vertices).
      for (let i = 0; i < vertexCount; i++) {
        if (wireValence[i] === 1) pinned[i] = 1;
      }
    }

    // Bail if nothing to simulate.
    let anyFree = false;
    for (let i = 0; i < vertexCount; i++) {
      if (!pinned[i]) {
        anyFree = true;
        break;
      }
    }
    if (!anyFree) return;

    const collisionMeshes = buildCollisionMeshes(
      new Set([objectId]),
      state.sceneObjects,
      state.collections,
    );
    if (!skipUndo) state.pushUndo();
    let result: SettleOutput | undefined;
    try {
      result = await settleParticlesAsync(
        {
          positions,
          edges,
          pinned,
          collisionMeshes,
          recordTrajectory: shouldBake
            ? { stride: recordStride, maxFrames: GRAVITY_BAKE_MAX_FRAMES }
            : undefined,
          params: {
            compliance,
            tautness,
            maxSubsteps,
            settleExtensionSubsteps: shouldBake
              ? GRAVITY_BAKE_SETTLE_EXTENSION
              : 0,
          },
        },
        { signal, onProgress, recordStartSubstep, progressStride },
      );
    } catch (err) {
      disposeCollisionMeshes(collisionMeshes);
      if (err instanceof GravitySimCancelledError) return;
      throw err;
    }
    disposeCollisionMeshes(collisionMeshes);
    const resolvedPositions = result.positions;

    if (process.env.NODE_ENV !== "production") {
      console.log(
        `simulateGravity (mesh wire): ${result.substepsRun} substeps, max edge dev ${result.maxEdgeDeviation.toFixed(6)}` +
          (result.trajectory
            ? `, baked ${result.trajectory.frameCount} frames`
            : ""),
      );
    }

    // Re-fetch state — the await may have spanned an outside mutation.
    const wireLatestState = get();
    const wireLatestObj = wireLatestState.sceneObjects.get(objectId);
    if (
      !wireLatestObj ||
      !wireLatestObj.vertices ||
      wireLatestObj.vertices.length !== sourceVertices.length ||
      !wireLatestObj.topology
    )
      return;

    // Write back: only update free vertices; pinned ones stayed at their
    // anchors and writing them back would still be a no-op, but skipping
    // avoids float-roundtrip drift.
    const newVerts = new Float32Array(sourceVertices);
    for (let i = 0; i < vertexCount; i++) {
      if (pinned[i]) continue;
      tmp
        .set(
          resolvedPositions[i * 3],
          resolvedPositions[i * 3 + 1],
          resolvedPositions[i * 3 + 2],
        )
        .applyMatrix4(inverseWorld);
      newVerts[i * 3] = tmp.x;
      newVerts[i * 3 + 1] = tmp.y;
      newVerts[i * 3 + 2] = tmp.z;
    }

    if (shouldBake && result.trajectory) {
      onBakeComplete!(
        bakeFramesToLocalSpace(
          objectId,
          result.trajectory.frames,
          result.trajectory.frameCount,
          vertexCount,
          pinned,
          sourceVertices,
          inverseWorld,
        ),
      );
    }

    // Recompute loop normals so shading stays consistent with the drape.
    const wireLoopNormals = recomputeLoopNormals(
      newVerts,
      wireLatestObj.topology,
      resolveAutoSmoothAngle(
        wireLatestObj.shading,
        wireLatestObj.autoSmoothAngle,
      ),
    );

    set({
      sceneObjects: patchSceneObject(wireLatestState.sceneObjects, objectId, {
        vertices: newVerts,
        loopNormals: wireLoopNormals,
      }),
    });
  },

  applyGravityFrame: (objectId, vertices) => {
    const state = get();
    const obj = state.sceneObjects.get(objectId);
    if (!obj || !obj.topology) return;
    // The caller supplies a full object-local vertex buffer (from the
    // menu's interpolation between baked frames). We own the normal
    // recompute: the scrub loop would otherwise trigger a cache miss on
    // every frame.
    const loopNormals = recomputeLoopNormals(
      vertices,
      obj.topology,
      resolveAutoSmoothAngle(obj.shading, obj.autoSmoothAngle),
    );
    set({
      sceneObjects: patchSceneObject(state.sceneObjects, objectId, {
        vertices,
        loopNormals,
      }),
    });
  },

  resetPose: (objectId) => {
    get().pushUndo();
    const poses = new Map(get().mannequinBonePoses);
    // Set an empty map (not delete) so the renderer actively restores rest poses
    poses.set(objectId, new Map());
    set({ mannequinBonePoses: poses });
  },

  toggleBoneLock: (objectId, boneName) => {
    const locked = new Map(get().lockedBones);
    const bones = new Set<string>(locked.get(objectId) ?? new Set<string>());
    if (bones.has(boneName)) {
      bones.delete(boneName);
    } else {
      bones.add(boneName);
    }
    locked.set(objectId, bones);
    set({ lockedBones: locked });
  },

  toggleBonePin: (objectId, boneName) => {
    const pinned = new Map(get().pinnedBones);
    const bones = new Map<string, [number, number, number]>(
      pinned.get(objectId) ?? new Map(),
    );
    if (bones.has(boneName)) {
      bones.delete(boneName);
    } else {
      // Capture current world position of the bone as the pin target
      const boneRef = getBone(objectId, boneName);
      if (!boneRef) return;
      const wp = new THREE.Vector3();
      boneRef.getWorldPosition(wp);
      bones.set(boneName, [wp.x, wp.y, wp.z]);
    }
    pinned.set(objectId, bones);
    set({ pinnedBones: pinned });
  },

  setConstraintWeight: (objectId, weight) => {
    const weights = new Map(get().constraintWeights);
    weights.set(objectId, Math.max(0, Math.min(1, weight)));
    set({ constraintWeights: weights });
  },

  setBoneVolume: (objectId, boneName, scale) => {
    const volumes = new Map(get().boneVolumes);
    const bones = new Map(volumes.get(objectId) ?? new Map());
    bones.set(boneName, scale);
    volumes.set(objectId, bones);
    set({ boneVolumes: volumes });
  },

  setShowTextures: (show) => set({ showTextures: show }),
  setTextureMapsEnabled: (enabled) => set({ textureMapsEnabled: enabled }),
  setSettingsOpen: (open) =>
    set({
      settingsOpen: open,
      // Closing the modal clears any pending deep-link signal so re-opening
      // via the regular menu lands on the default tab, not whatever was
      // last requested via openSettings.
      settingsInitialTab: open ? get().settingsInitialTab : null,
    }),
  openSettings: (initialTab) =>
    set({ settingsOpen: true, settingsInitialTab: initialTab ?? null }),
  setRenderMethodModalOpen: (open) => set({ renderMethodModalOpen: open }),
  setGeneratorAdjusting: (adjusting) => set({ generatorAdjusting: adjusting }),
  setObjectXray: (xray) => set({ objectXray: xray }),
  setGridLogDist: (v) => {
    // Only update if it's changed enough to matter (0.01 precision)
    // to avoid excessive re-renders of components that depend on it
    // (though most will use useStore(s => s.gridLogDist))
    if (Math.abs(get().gridLogDist - v) > 0.005) {
      set({ gridLogDist: v });
    }
  },
  setShowGrid: (show) => set({ showGrid: show }),
  setShowAxisIndicator: (show) => set({ showAxisIndicator: show }),
  setShowFrustum: (show) => set({ showFrustum: show }),
  setRenderWidth: (px) => {
    const w = clampRenderDim(px);
    if (w !== get().renderWidth) set({ renderWidth: w });
  },
  setRenderHeight: (px) => {
    const h = clampRenderDim(px);
    if (h !== get().renderHeight) set({ renderHeight: h });
  },
  setRenderDimensions: (width, height) => {
    const w = clampRenderDim(width);
    const h = clampRenderDim(height);
    const prev = get();
    if (w !== prev.renderWidth || h !== prev.renderHeight) {
      set({ renderWidth: w, renderHeight: h });
    }
  },
  setRenderTransparentBackground: (transparent) =>
    set({ renderTransparentBackground: transparent }),
  setInkingEnabled: (enabled) => set({ inkingEnabled: enabled }),
  setInkingThickness: (thickness) =>
    set({ inkingThickness: Math.max(0.5, Math.min(15, thickness)) }),
  setInkingThicknessMin: (thickness) =>
    set({ inkingThicknessMin: Math.max(0, Math.min(15, thickness)) }),
  setInkingShadowResponse: (response) =>
    set({ inkingShadowResponse: Math.max(0, Math.min(1, response)) }),
  setInkingShadowExposure: (exposure) =>
    set({ inkingShadowExposure: Math.max(0, Math.min(1, exposure)) }),
  setInkingOpacity: (opacity) =>
    set({ inkingOpacity: Math.max(0, Math.min(1, opacity)) }),
  setInkingColor: (color) => set({ inkingColor: color }),
  setInkingIncludePrimitives: (included) =>
    set({ inkingIncludePrimitives: included }),
  setInkingIncludeMannequins: (included) =>
    set({ inkingIncludeMannequins: included }),
  setInkingIncludeBlendObjects: (included) =>
    set({ inkingIncludeBlendObjects: included }),
  setInkingLineWidthVariation: (variation) =>
    set({ inkingLineWidthVariation: Math.max(0, Math.min(1, variation)) }),
  setInkingDetail: (detail) =>
    set({ inkingDetail: Math.max(0, Math.min(1, detail)) }),
  setInkingHatchingEnabled: (enabled) =>
    set({ inkingHatchingEnabled: enabled }),
  setInkingHatchingDirection: (direction) =>
    set({ inkingHatchingDirection: Math.max(0, Math.min(180, direction)) }),
  setInkingHatchingExposure: (exposure) =>
    set({ inkingHatchingExposure: Math.max(0, Math.min(2, exposure)) }),
  setInkingHatchingDensity: (density) =>
    set({ inkingHatchingDensity: Math.max(0, Math.min(1.25, density)) }),
  setInkingHatchingJitter: (jitter) =>
    set({ inkingHatchingJitter: Math.max(0, Math.min(1, jitter)) }),
  setInkingDistanceNear: (distance) =>
    set({ inkingDistanceNear: Math.max(0, Math.min(1000, distance)) }),
  setInkingDistanceFar: (distance) =>
    set({ inkingDistanceFar: Math.max(0.1, Math.min(1000, distance)) }),
  setInkingAutoGenerate: (enabled) => set({ inkingAutoGenerate: enabled }),
  setInkingFeatureEnabled: (feature, enabled) => {
    const im = get().inkingMode;
    if (!im) return;
    set({
      inkingMode: { ...im, features: { ...im.features, [feature]: enabled } },
    });
  },
  setInkingCreaseAngle: (angle) => {
    const im = get().inkingMode;
    if (!im) return;
    set({
      inkingMode: { ...im, creaseAngle: Math.max(0, Math.min(180, angle)) },
    });
  },
  setInkingTaperStrength: (strength) => {
    const im = get().inkingMode;
    if (!im) return;
    set({
      inkingMode: { ...im, taperStrength: Math.max(0, Math.min(1, strength)) },
    });
  },
  setInkingSimplificationTolerance: (tolerance) => {
    const im = get().inkingMode;
    if (!im) return;
    set({
      inkingMode: {
        ...im,
        simplificationTolerance: Math.max(0.1, Math.min(10, tolerance)),
      },
    });
  },
  setInkingCornerAngle: (angle) => {
    const im = get().inkingMode;
    if (!im) return;
    set({
      inkingMode: { ...im, cornerAngle: Math.max(10, Math.min(170, angle)) },
    });
  },

  // -- Inking editable canvas --
  setInkingEditSubMode: (sub) => {
    const im = get().inkingMode;
    if (!im) return;
    // Reset the view transform whenever the user re-enters edit mode so they
    // start from a predictable framing rather than a stale zoom/pan. The
    // camera frame overlay (the vignette + border) is dismissed in edit mode
    // so the page itself serves as the only visible boundary; we restore it
    // when dropping back to the preview sub-mode.
    const nextPan = sub === "edit" ? ([0, 0] as [number, number]) : im.pan;
    const nextZoom = sub === "edit" ? 1 : im.zoom;
    const nextRotation = sub === "edit" ? 0 : im.rotation;
    set({
      inkingMode: {
        ...im,
        editSubMode: sub,
        pan: nextPan,
        zoom: nextZoom,
        rotation: nextRotation,
      },
      showFrustum: sub !== "edit",
    });
  },
  setInkingTool: (tool) => {
    const im = get().inkingMode;
    if (!im) return;
    set({ inkingMode: { ...im, tool } });
  },
  setInkingBrushSize: (size) => {
    const im = get().inkingMode;
    if (!im) return;
    set({ inkingMode: { ...im, size: Math.max(1, Math.min(200, size)) } });
  },
  setInkingBrushOpacity: (opacity) => {
    const im = get().inkingMode;
    if (!im) return;
    set({ inkingMode: { ...im, opacity: Math.max(0, Math.min(1, opacity)) } });
  },
  setInkingBrushHardness: (hardness) => {
    const im = get().inkingMode;
    if (!im) return;
    set({
      inkingMode: { ...im, hardness: Math.max(0, Math.min(1, hardness)) },
    });
  },
  setInkingBrushColor: (hex) => {
    const im = get().inkingMode;
    if (!im) return;
    set({ inkingMode: { ...im, color: hex } });
  },
  setInkingPressureMode: (mode) => {
    const im = get().inkingMode;
    if (!im) return;
    set({ inkingMode: { ...im, pressureMode: mode } });
  },
  setInkingShowReference: (show) => {
    const im = get().inkingMode;
    if (!im) return;
    set({ inkingMode: { ...im, showReference: show } });
  },
  setInkingPan: (pan) => {
    const im = get().inkingMode;
    if (!im) return;
    set({ inkingMode: { ...im, pan } });
  },
  setInkingZoom: (zoom) => {
    const im = get().inkingMode;
    if (!im) return;
    // Clamp to a sensible range — 0.1x to 16x page scale.
    set({ inkingMode: { ...im, zoom: Math.max(0.1, Math.min(16, zoom)) } });
  },
  setInkingRotation: (rotationDeg) => {
    const im = get().inkingMode;
    if (!im) return;
    // Normalise to (-180, 180] for predictable serialisation; visually
    // equivalent, just avoids unbounded accumulation from repeated pinches.
    let r = rotationDeg % 360;
    if (r > 180) r -= 360;
    if (r <= -180) r += 360;
    set({ inkingMode: { ...im, rotation: r } });
  },
  setInkingRasterBuffer: (buffer, width, height) => {
    const im = get().inkingMode;
    if (!im) return;
    set({
      inkingMode: {
        ...im,
        rasterBuffer: buffer,
        rasterWidth: width,
        rasterHeight: height,
        rasterSeq: im.rasterSeq + 1,
        rasterDirtyFromBake: false,
      },
    });
  },
  endInkingStroke: () => {
    const im = get().inkingMode;
    if (!im) return;
    set({
      inkingMode: {
        ...im,
        rasterSeq: im.rasterSeq + 1,
        rasterDirtyFromBake: true,
      },
    });
  },
  resetInkingView: () => {
    const im = get().inkingMode;
    if (!im) return;
    set({ inkingMode: { ...im, pan: [0, 0], zoom: 1, rotation: 0 } });
  },

  // -- Saved inking views (raster + linked camera bookmark) --
  saveCurrentInkingView: (name) => {
    const s = get();
    const im = s.inkingMode;
    if (!im?.rasterBuffer || im.rasterWidth === 0 || im.rasterHeight === 0)
      return null;

    const w = im.rasterWidth;
    const h = im.rasterHeight;
    let rasterPng: string;
    let thumbnail: string;
    try {
      rasterPng = rasterToPng(im.rasterBuffer, w, h);
      thumbnail = rasterToThumbnailPng(im.rasterBuffer, w, h, 160);
    } catch (err) {
      console.error("[Inking] Failed to encode saved view PNG:", err);
      return null;
    }

    // Auto-create a matching camera bookmark so the view also shows up as a
    // frustum gizmo in other modes — users asked for this linkage explicitly.
    const finalName = name?.trim() || nextUniqueInkingName(s.savedInkingViews);
    const now = Date.now();
    const bookmark: CameraBookmark = {
      id: `cam_${++bookmarkCounter}_${now}`,
      name: finalName,
      position: [...s.cameraPosition] as [number, number, number],
      target: [...s.cameraTarget] as [number, number, number],
      focalLength: s.focalLength,
    };

    const view: SavedInkingView = {
      id: `ink_${++savedInkingCounter}_${now}`,
      name: finalName,
      createdAt: now,
      rasterPng,
      rasterWidth: w,
      rasterHeight: h,
      thumbnail,
      cameraPosition: [...bookmark.position] as [number, number, number],
      cameraTarget: [...bookmark.target] as [number, number, number],
      focalLength: bookmark.focalLength,
      cameraBookmarkId: bookmark.id,
    };

    set({
      cameraBookmarks: [...s.cameraBookmarks, bookmark],
      savedInkingViews: [...s.savedInkingViews, view],
    });
    return view.id;
  },

  loadSavedInkingView: async (id) => {
    const view = get().savedInkingViews.find((v) => v.id === id);
    if (!view) return;

    // Take an undo snapshot before replacing in-progress work so the user can
    // cmd+Z back to whatever raster / camera state they had before the load.
    get().pushUndo();

    // Kick off the camera transition immediately so the user sees the
    // view animate even while the raster is still decoding.
    startCameraTransition(
      view.cameraPosition,
      view.cameraTarget,
      view.focalLength,
    );

    let decoded: { data: Uint8ClampedArray; width: number; height: number };
    try {
      decoded = await pngToRaster(view.rasterPng);
    } catch (err) {
      console.error("[Inking] Failed to decode saved view:", err);
      return;
    }

    const post = get();
    if (!post.inkingMode) return; // User left inking mode while decoding.
    // Integrity check — the PNG dimensions should match the stored metadata.
    // A mismatch means a corrupted or mis-edited scene file; log and bail
    // rather than feeding bad bytes into setInkingRasterBuffer.
    if (
      decoded.width !== view.rasterWidth ||
      decoded.height !== view.rasterHeight
    ) {
      console.error(
        `[Inking] Saved view ${view.id} dimensions mismatch: ` +
          `PNG is ${decoded.width}×${decoded.height}, metadata says ` +
          `${view.rasterWidth}×${view.rasterHeight}`,
      );
      return;
    }
    // Dropping back to view sub-mode gives the user an unambiguous "the saved
    // view is loaded" state; they can re-enter edit to keep refining. The
    // page transform is reset so loading a view always lands on a predictable
    // zoom/pan/rotation.
    const nextInking = {
      ...post.inkingMode,
      editSubMode: "view" as const,
      rasterBuffer: decoded.data,
      rasterWidth: decoded.width,
      rasterHeight: decoded.height,
      rasterSeq: post.inkingMode.rasterSeq + 1,
      rasterDirtyFromBake: false,
      baked: true,
      pan: [0, 0] as [number, number],
      zoom: 1,
      rotation: 0,
    };
    set({
      inkingMode: nextInking,
      showFrustum: true,
      canUndo: true,
    });
  },

  deleteSavedInkingView: (id) => {
    const s = get();
    const view = s.savedInkingViews.find((v) => v.id === id);
    set({
      savedInkingViews: s.savedInkingViews.filter((v) => v.id !== id),
      cameraBookmarks: view?.cameraBookmarkId
        ? s.cameraBookmarks.filter((b) => b.id !== view.cameraBookmarkId)
        : s.cameraBookmarks,
    });
  },

  renameSavedInkingView: (id, name) => {
    const s = get();
    const trimmed = name.trim();
    if (!trimmed) return;
    set({
      savedInkingViews: s.savedInkingViews.map((v) =>
        v.id === id ? { ...v, name: trimmed } : v,
      ),
      // Keep the linked camera bookmark's name in sync for a single source of truth.
      cameraBookmarks: s.cameraBookmarks.map((b) => {
        const view = s.savedInkingViews.find((v) => v.id === id);
        return view && b.id === view.cameraBookmarkId
          ? { ...b, name: trimmed }
          : b;
      }),
    });
  },

  setRenderExportMode: (mode) => set({ renderExportMode: mode }),
  setRenderGridOverride: (mode) => set({ renderGridOverride: mode }),
  setCameraProjection: (proj) => {
    const cur = get();
    const live = window.__poseref_cameraController?.getLiveState();
    const currentProjection = live?.projection ?? cur.cameraProjection;
    const switching = currentProjection !== proj;
    const patch: Partial<AppState> = { cameraProjection: proj };
    if (switching && proj === "orthographic") {
      const [px, py, pz] = live?.position ?? cur.cameraPosition;
      const [tx, ty, tz] = live?.target ?? cur.cameraTarget;
      const dist = Math.max(0.01, Math.hypot(px - tx, py - ty, pz - tz));
      patch.orthographicHalfHeight = lockedOrthographicHalfHeight(
        dist,
        cur.focalLength,
        currentProjection,
        cur.orthographicHalfHeight,
        false,
      );
    }
    if (cur.viewLock && switching) patch.viewLock = null;
    window.__poseref_cameraController?.applyState({
      projection: proj,
      focalLength: cur.focalLength,
      orthographicHalfHeight:
        patch.orthographicHalfHeight ?? cur.orthographicHalfHeight,
      up: proj === "perspective" ? null : (live?.up ?? cur.cameraUp),
    });
    set(patch);
  },
  enterViewLock: (axis, sign, distance, position, target, up) => {
    const s = get();
    // Preserve the pre-lock projection / focal length / ortho scale snapshot
    // across subsequent view changes (e.g. Front → Right) so exiting always
    // returns to whatever the user was using before they first entered a
    // view. The smooth transition to the axis-aligned orientation is kicked
    // off by the caller via `snapToAxisView` to keep this action free of
    // viewport/animation dependencies.
    const alreadyLocked = s.viewLock != null;
    const prevProjection = alreadyLocked
      ? s.viewLock!.prevProjection
      : s.cameraProjection;
    const prevFocalLength = alreadyLocked
      ? s.viewLock!.prevFocalLength
      : s.focalLength;
    const prevOrthographicHalfHeight = alreadyLocked
      ? s.viewLock!.prevOrthographicHalfHeight
      : s.cameraProjection === "orthographic"
        ? s.orthographicHalfHeight
        : null;
    // When transitioning between axis views we keep the user's ortho zoom —
    // switching Front→Top in Blender preserves your numpad zoom. Only seed
    // a fresh half-height on the first entry from perspective, where we
    // want the apparent vertical extent at the pivot to match the
    // perspective frame at the moment of snapping.
    const halfHeight = lockedOrthographicHalfHeight(
      distance,
      s.focalLength,
      s.cameraProjection,
      s.orthographicHalfHeight,
      alreadyLocked,
    );
    const patch: Partial<AppState> = {
      viewLock: {
        axis,
        sign,
        prevProjection,
        prevFocalLength,
        prevOrthographicHalfHeight,
      },
      cameraProjection: "orthographic",
      orthographicHalfHeight: halfHeight,
    };
    if (position) patch.cameraPosition = position;
    if (target) patch.cameraTarget = target;
    if (up !== undefined) patch.cameraUp = up;
    set(patch);
  },
  exitViewLock: (reason) => {
    const lock = get().viewLock;
    if (!lock) return;
    if (reason === "projection") {
      set({ viewLock: null });
      return;
    }
    // Both "manual" and "rotation" restore the pre-lock projection and focal
    // length. Camera position is left alone here — "manual" exits are
    // performed by `exitAxisView` in cameraViews.ts, which writes a
    // position in the same commit; "rotation" exits are continuous
    // user-driven orbits.
    //
    // When returning to perspective we clear `cameraUp`, reverting to
    // world +Z up. Axis-view locks like Top (+Y up) and Bottom (−Y up)
    // would otherwise leave the camera in a non-vertical frame — any
    // subsequent orbit would tilt the horizon. Resetting here ensures
    // roll is always perfectly vertical in perspective.
    const patch: Partial<AppState> = {
      viewLock: null,
      cameraProjection: lock.prevProjection,
      focalLength: lock.prevFocalLength,
    };
    if (lock.prevOrthographicHalfHeight != null) {
      patch.orthographicHalfHeight = lock.prevOrthographicHalfHeight;
    }
    if (lock.prevProjection === "perspective") {
      patch.cameraUp = null;
    }
    set(patch);
  },
  setOrthographicHalfHeight: (halfHeight) =>
    set({ orthographicHalfHeight: Math.max(0.01, Math.min(500, halfHeight)) }),

  updateGroupTransform: (entryId, transform) => {
    const { blendFiles } = get();
    const entry = blendFiles.get(entryId);
    if (!entry) return;
    const newFiles = new Map(blendFiles);
    newFiles.set(entryId, { ...entry, groupTransform: { ...transform } });
    set({ blendFiles: newFiles });
  },

  updateCollectionTransform: (collectionId, transform) => {
    const collections = new Map(get().collections);
    const collection = collections.get(collectionId);
    if (!collection) return;
    collections.set(collectionId, {
      ...collection,
      transform: cloneGroupTransform(transform),
    });
    set({ collections });
  },

  setUiScale: (scale) => {
    set({ uiScale: scale });
    localStorage.setItem("poseref-ui-scale", String(scale));
    document.documentElement.style.zoom = String(scale);
  },
  setOrbitSensitivity: (v) => {
    const clamped = Math.max(0.25, Math.min(3, v));
    set({ orbitSensitivity: clamped });
    try {
      localStorage.setItem("poseref-orbit-sensitivity", String(clamped));
    } catch {
      /* ignore */
    }
  },
  setPanSensitivity: (v) => {
    const clamped = Math.max(0.25, Math.min(3, v));
    set({ panSensitivity: clamped });
    try {
      localStorage.setItem("poseref-pan-sensitivity", String(clamped));
    } catch {
      /* ignore */
    }
  },
  setDollySensitivity: (v) => {
    const clamped = Math.max(0.25, Math.min(3, v));
    set({ dollySensitivity: clamped });
    try {
      localStorage.setItem("poseref-dolly-sensitivity", String(clamped));
    } catch {
      /* ignore */
    }
  },
  setInvertOrbit: (v) => {
    set({ invertOrbit: v });
    try {
      localStorage.setItem("poseref-invert-orbit", String(v));
    } catch {
      /* ignore */
    }
  },
  setInvertPan: (v) => {
    set({ invertPan: v });
    try {
      localStorage.setItem("poseref-invert-pan", String(v));
    } catch {
      /* ignore */
    }
  },
  setInvertZoom: (v) => {
    set({ invertZoom: v });
    try {
      localStorage.setItem("poseref-invert-zoom", String(v));
    } catch {
      /* ignore */
    }
  },
  setPrecisionLevel: (v) => set({ precisionLevel: v }),
  setUndoMemoryBudget: (bytes) => {
    const clamped = Math.max(
      UNDO_BUDGET_MIN_BYTES,
      Math.min(UNDO_BUDGET_MAX_BYTES, bytes),
    );
    set({ undoMemoryBudgetBytes: clamped });
    undoManager.setMaxBytes(clamped);
    persistUndoBudget(clamped);
  },
  setMemoryLimit: (bytes) => {
    const clamped = bytes === 0 ? 0 : clampMemoryLimit(bytes);
    set({ memoryLimitBytes: clamped });
    setMemoryGovernorLimit(clamped);
    try {
      localStorage.setItem("poseref-memory-limit-bytes", String(clamped));
    } catch {
      /* ignore */
    }
  },
  setDebugEnabled: (enabled) => {
    set({ debugEnabled: enabled });
    setIKLogEnabled(enabled);
    setDebugLoggerEnabled(enabled);
    try {
      localStorage.setItem("poseref-debug-enabled", String(enabled));
    } catch {
      /* ignore */
    }
  },
  setDebugMaterial: (config) => set({ debugMaterial: config }),
  toggleDebugPanel: (id) => {
    const prev = get().debugPanelOpen;
    const next = { ...prev, [id]: !prev[id] };
    set({ debugPanelOpen: next });
    try {
      localStorage.setItem("poseref-debug-panel-open", JSON.stringify(next));
    } catch {
      /* ignore */
    }
  },
  setActiveTopologyViz: (mode) => set({ activeTopologyViz: mode }),
  setPrimitiveSymmetryDebugAxis: (axis) =>
    set({ primitiveSymmetryDebugAxis: axis }),
  toggleDebugShapeKeyLayer: (layer) => {
    const prev = get().debugDisabledShapeKeyLayers;
    const next = new Set(prev);
    if (next.has(layer)) next.delete(layer);
    else next.add(layer);
    set({ debugDisabledShapeKeyLayers: next });
  },
  toggleShadingDebugFlag: (flag) => {
    const prev = get().shadingDebugFlags;
    set({ shadingDebugFlags: { ...prev, [flag]: !prev[flag] } });
  },
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),

  pushUndo: () => {
    undoManager.push(captureSnapshot(get()));
    set({ canUndo: undoManager.canUndo, canRedo: undoManager.canRedo });
  },

  undo: () => {
    // Cancel any active command before undoing — prevents stale command
    // state from persisting over reverted geometry.
    if (get().commandBuffer) {
      get().cancelCommand();
      return; // cancel already pops its own undo entry
    }
    // Surface tool modal: undo cancels the in-progress interaction without
    // touching the undo stack — no object exists until the corresponding
    // confirm action runs. Surface Poly is the lone exception: it owns a
    // per-modal segment-level undo stack (via `trySurfacePolyUndo`); when
    // that stack is non-empty the pop replaces a global undo, otherwise
    // we cancel the entire modal here.
    {
      const tool = get().surfaceTool;
      if (tool && tool.stage !== null) {
        if (tool.kind === "poly" && trySurfacePolyUndo()) return;
        set({ surfaceTool: null, activeOperator: null });
        return;
      }
    }
    // Selection deltas are self-contained (carry both before+after), so
    // peek and recycle the entry between stacks rather than capturing a
    // full snapshot — that would defeat the lightweight-undo purpose.
    const peek = undoManager.peekUndo();
    if (peek && isSelectionDelta(peek)) {
      undoManager.undo(peek);
      const patch = applySelectionDeltaState(get(), peek.before);
      set({
        ...patch,
        lastOperation: null,
        canUndo: undoManager.canUndo,
        canRedo: undoManager.canRedo,
      });
      return;
    }
    if (peek && isVertexPositionDelta(peek)) {
      undoManager.undo(peek);
      const patch = applyVertexPositionDeltaUndo(get(), peek);
      set({
        ...patch,
        lastOperation: null,
        canUndo: undoManager.canUndo,
        canRedo: undoManager.canRedo,
      });
      return;
    }
    if (peek && isObjectGeometrySnapshotDelta(peek)) {
      undoManager.undo(peek);
      const patch = applyObjectGeometrySnapshotUndo(get(), peek);
      set({
        ...patch,
        lastOperation: null,
        canUndo: undoManager.canUndo,
        canRedo: undoManager.canRedo,
      });
      return;
    }
    const entry = undoManager.undo(captureSnapshot(get()));
    if (!entry) return;
    if (isSculptDelta(entry)) {
      const patch = applySculptDeltaUndo(get(), entry);
      set({
        ...patch,
        lastOperation: null,
        canUndo: undoManager.canUndo,
        canRedo: undoManager.canRedo,
      });
      return;
    }
    if (isSelectionDelta(entry)) return; // unreachable: handled by peek path above
    if (isVertexPositionDelta(entry)) return; // unreachable: handled by peek path above
    if (isObjectGeometrySnapshotDelta(entry)) return; // unreachable: handled by peek path above
    const patch = applySnapshot(get(), entry);
    set({
      ...patch,
      lastOperation: null,
      canUndo: undoManager.canUndo,
      canRedo: undoManager.canRedo,
    });
    healSketchModeActiveLayer(get, set);
  },

  redo: () => {
    // Cancel any active command before redoing. Unlike undo (where cancel
    // IS the undo — it pops its own entry and we return), redo needs to
    // proceed after clearing the stale command to apply the next entry.
    if (get().commandBuffer) {
      get().cancelCommand();
    }
    // Surface Poly modal: route to the handler-local segment replay first.
    // If the redo stack is empty, fall through to the global redo path so
    // a redo while modal still recovers the most recent global entry.
    {
      const tool = get().surfaceTool;
      if (tool?.kind === "poly" && tool.stage !== null) {
        if (trySurfacePolyRedo()) return;
      }
    }
    // Mirror of undo: selection deltas recycle between stacks without
    // capturing a snapshot.
    const peekRedo = undoManager.peekRedo();
    if (peekRedo && isSelectionDelta(peekRedo)) {
      undoManager.redo(peekRedo);
      const patch = applySelectionDeltaState(get(), peekRedo.after);
      set({
        ...patch,
        canUndo: undoManager.canUndo,
        canRedo: undoManager.canRedo,
      });
      return;
    }
    if (peekRedo && isVertexPositionDelta(peekRedo)) {
      undoManager.redo(peekRedo);
      const patch = applyVertexPositionDeltaRedo(get(), peekRedo);
      set({
        ...patch,
        lastOperation: null,
        canUndo: undoManager.canUndo,
        canRedo: undoManager.canRedo,
      });
      return;
    }
    if (peekRedo && isObjectGeometrySnapshotDelta(peekRedo)) {
      undoManager.redo(peekRedo);
      const patch = applyObjectGeometrySnapshotRedo(get(), peekRedo);
      set({
        ...patch,
        lastOperation: null,
        canUndo: undoManager.canUndo,
        canRedo: undoManager.canRedo,
      });
      return;
    }
    const entry = undoManager.redo(captureSnapshot(get()));
    if (!entry) return;
    if (isSculptDelta(entry)) {
      const patch = applySculptDeltaRedo(get(), entry);
      set({
        ...patch,
        canUndo: undoManager.canUndo,
        canRedo: undoManager.canRedo,
      });
      return;
    }
    if (isSelectionDelta(entry)) return; // unreachable: handled by peek path above
    if (isVertexPositionDelta(entry)) return; // unreachable: handled by peek path above
    if (isObjectGeometrySnapshotDelta(entry)) return; // unreachable: handled by peek path above
    const patch = applySnapshot(get(), entry);
    set({
      ...patch,
      lastOperation: null,
      canUndo: undoManager.canUndo,
      canRedo: undoManager.canRedo,
    });
    healSketchModeActiveLayer(get, set);
  },

  addCameraBookmark: (name, position, target) => {
    const bookmark: CameraBookmark = {
      id: `cam_${++bookmarkCounter}_${Date.now()}`,
      name,
      position: [...position] as [number, number, number],
      target: [...target] as [number, number, number],
      focalLength: get().focalLength,
    };
    set({ cameraBookmarks: [...get().cameraBookmarks, bookmark] });
  },

  removeCameraBookmark: (id) => {
    set({ cameraBookmarks: get().cameraBookmarks.filter((b) => b.id !== id) });
  },

  renameCameraBookmark: (id, name) => {
    set({
      cameraBookmarks: get().cameraBookmarks.map((b) =>
        b.id === id ? { ...b, name } : b,
      ),
    });
  },

  toggleFavoriteShapeKey: (setRef, keyName) => {
    const key = `${setRef}::${keyName}`;
    const next = new Set(get().favoriteShapeKeys);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    set({ favoriteShapeKeys: next });
  },

  toggleFavoriteOperator: (operatorId) => {
    const next = new Set(get().favoriteOperators);
    if (next.has(operatorId)) {
      next.delete(operatorId);
    } else {
      next.add(operatorId);
    }
    set({ favoriteOperators: next });
    try {
      localStorage.setItem(
        "poseref-favorite-operators",
        JSON.stringify([...next]),
      );
    } catch {
      /* ignore */
    }
  },

  setOperatorSearchOpen: (open) => {
    set({ operatorSearchOpen: open });
  },

  setActiveOperator: (id) => {
    set({ activeOperator: id });
    const em = get().editMode;
    if (id === "extrude") {
      if (em) set({ editMode: { ...em, extrudePending: true } });
    } else {
      if (em?.extrudePending) {
        set({ editMode: { ...em, extrudePending: false } });
      }
    }
  },

  armToolOperator: (id) => {
    const prev = get().activeOperator;
    set({ activeOperator: id });
    const em = get().editMode;
    if (em?.extrudePending) {
      set({ editMode: { ...em, extrudePending: false } });
    }
    // Leaving any surface tool — drop the polymorphic surfaceTool slot so
    // the handler / gizmo can never read stale draft fields once disarmed.
    // External disarm paths (palette toggle, F3 search re-arm, keymap)
    // share this branch with the in-confirm cleanup in `runSurfaceConfirm`.
    if (
      prev !== id &&
      (prev === "surface_box" ||
        prev === "surface_cylinder" ||
        prev === "surface_poly" ||
        prev === "surface_inset") &&
      get().surfaceTool !== null
    ) {
      set({ surfaceTool: null });
    }
  },

  toggleChromeHidden: () => {
    set((s) => ({ chromeHidden: !s.chromeHidden }));
  },
  setAddObjectOpen: (open) => {
    set({ addObjectOpen: open });
  },
  setAddObjectFocusSearch: (focus) => {
    set({ addObjectFocusSearch: focus });
  },
  setDeleteMenuOpen: (open) => {
    set({ deleteMenuOpen: open });
  },

  setRenderMethodPieOpen: (open) => {
    set({ renderMethodPieOpen: open });
  },

  setSnapPieOpen: (open) => {
    set({ snapPieOpen: open });
  },

  setViewPieOpen: (open) => {
    set({ viewPieOpen: open });
  },

  setExtrudePieOpen: (open) => {
    set({ extrudePieOpen: open });
  },

  setMergeMenuOpen: (open) => {
    set({ mergeMenuOpen: open });
  },

  setRelaxMenuOpen: (open) => {
    set({ relaxMenuOpen: open });
  },

  setGravityMenuOpen: (open) => {
    set({ gravityMenuOpen: open });
  },

  setSmoothMenuOpen: (open) => {
    set({ smoothMenuOpen: open });
  },

  setObjectDeleteConfirmOpen: (open) => {
    set({ objectDeleteConfirmOpen: open });
  },

  setMeshMenuOpen: (open) => {
    set({ meshMenuOpen: open });
  },

  setMeshMenuSection: (section) => {
    set({ meshMenuSection: section });
  },

  setOperatorAnchorPoint: (point) => {
    set({ operatorAnchorPoint: point });
  },

  setPanelLastSelected: (panelKey, optionId) => {
    set((s) => ({
      panelLastSelected: { ...s.panelLastSelected, [panelKey]: optionId },
    }));
  },

  setRelaxFalloff: (falloff) => {
    set({ relaxFalloff: Math.max(0, Math.min(2, falloff)) });
  },

  relaxBonesFrom: (originalPoses, factor) => {
    const state = get();
    const pm = state.poseMode;
    if (!pm?.objectId || !pm.selectedBones.size) return;

    const poses = new Map(state.mannequinBonePoses);
    const bones = new Map(poses.get(pm.objectId) ?? new Map());
    const falloff = state.relaxFalloff;

    // Collect bones to relax with their effective factor based on falloff distance.
    // Distance 0 = selected bone (full factor), distance N = attenuated by 1/(N+1).
    const boneFactor = new Map<string, number>();

    for (const boneName of pm.selectedBones) {
      boneFactor.set(boneName, factor);

      if (falloff > 0) {
        const boneRef = getBone(pm.objectId, boneName);
        if (!boneRef) continue;

        // Walk up the hierarchy (parents)
        let parent = boneRef.parent;
        for (let d = 1; d <= falloff; d++) {
          if (!parent || !(parent instanceof THREE.Bone)) break;
          const attenuation = factor / (d + 1);
          const existing = boneFactor.get(parent.name) ?? 0;
          if (attenuation > existing) boneFactor.set(parent.name, attenuation);
          parent = parent.parent;
        }

        // Walk down the hierarchy (children) — breadth-first
        const queue: Array<{ bone: THREE.Bone; depth: number }> = [];
        for (const child of boneRef.children) {
          if (child instanceof THREE.Bone)
            queue.push({ bone: child, depth: 1 });
        }
        while (queue.length > 0) {
          const { bone: child, depth } = queue.shift()!;
          if (depth > falloff) continue;
          const attenuation = factor / (depth + 1);
          const existing = boneFactor.get(child.name) ?? 0;
          if (attenuation > existing) boneFactor.set(child.name, attenuation);
          for (const grandChild of child.children) {
            if (grandChild instanceof THREE.Bone) {
              queue.push({ bone: grandChild, depth: depth + 1 });
            }
          }
        }
      }
    }

    for (const [boneName, effectiveFactor] of boneFactor) {
      const restArr = getRestPose(pm.objectId, boneName);
      if (!restArr) continue;

      let origArr = originalPoses.get(boneName);
      if (!origArr) {
        const liveBone = getBone(pm.objectId, boneName);
        if (liveBone) {
          const q = liveBone.quaternion;
          origArr = [q.x, q.y, q.z, q.w];
        }
      }
      if (!origArr) continue;

      const rest = new THREE.Quaternion(
        restArr[0],
        restArr[1],
        restArr[2],
        restArr[3],
      );
      const orig = new THREE.Quaternion(
        origArr[0],
        origArr[1],
        origArr[2],
        origArr[3],
      );
      orig.slerp(rest, effectiveFactor);
      bones.set(boneName, [orig.x, orig.y, orig.z, orig.w]);
    }

    poses.set(pm.objectId, bones);
    set({ mannequinBonePoses: poses });
  },

  setModifierEnabled: (objectName, modifierIndex, enabled) => {
    const overrides = new Map(get().modifierOverrides);
    const objOverrides = [...(overrides.get(objectName) ?? [])];
    while (objOverrides.length <= modifierIndex) objOverrides.push({});
    objOverrides[modifierIndex] = { ...objOverrides[modifierIndex], enabled };
    overrides.set(objectName, objOverrides);
    set({ modifierOverrides: overrides });
  },

  setModifierProperty: (objectName, modifierIndex, property, value) => {
    const overrides = new Map(get().modifierOverrides);
    const objOverrides = [...(overrides.get(objectName) ?? [])];
    while (objOverrides.length <= modifierIndex) objOverrides.push({});
    const existing = objOverrides[modifierIndex];
    objOverrides[modifierIndex] = {
      ...existing,
      props: { ...(existing.props ?? {}), [property]: value },
    };
    overrides.set(objectName, objOverrides);
    set({ modifierOverrides: overrides });
  },

  setArrayOffsetObject: (objectName, modifierIndex, targetName) => {
    // Store only the name. The offsetMatrix is resolved live in BlendMeshEntity
    // so that moving the offset object immediately refreshes the array.
    const overrides = new Map(get().modifierOverrides);
    const objOverrides = [...(overrides.get(objectName) ?? [])];
    while (objOverrides.length <= modifierIndex) objOverrides.push({});
    const existing = objOverrides[modifierIndex];
    objOverrides[modifierIndex] = {
      ...existing,
      props: {
        ...(existing.props ?? {}),
        offsetObjectName: targetName,
        offsetMatrix: null,
      },
    };
    overrides.set(objectName, objOverrides);
    set({ modifierOverrides: overrides });
  },

  addModifier: (objectName, kind) => {
    const overrides = new Map(get().modifierOverrides);
    const objOverrides = [...(overrides.get(objectName) ?? [])];
    // Count existing modifiers of this kind for naming
    const found = findEntryByObjectName(get().blendFiles, objectName);
    const base = found
      ? found[1].file.objects.find((o) => o.name === objectName)
      : undefined;
    const baseLen = base?.modifiers.length ?? 0;
    const allMods = [
      ...(base?.modifiers ?? []),
      ...objOverrides
        .slice(baseLen)
        .filter((o) => o.runtimeModifier)
        .map((o) => o.runtimeModifier!),
    ];
    const count = allMods.filter((m) => m.kind === kind).length;
    const mod = MODIFIER_DEFAULTS[kind]();
    if (count > 0)
      mod.name = `${mod.name}.${String(count + 1).padStart(3, "0")}`;
    objOverrides.push({ enabled: true, runtimeModifier: mod, userAdded: true });
    overrides.set(objectName, objOverrides);
    set({ modifierOverrides: overrides });
  },

  removeModifier: (objectName, modifierIndex) => {
    const overrides = new Map(get().modifierOverrides);
    const objOverrides = [...(overrides.get(objectName) ?? [])];
    // Only allow removal of user-added modifiers
    if (!objOverrides[modifierIndex]?.userAdded) return;
    objOverrides.splice(modifierIndex, 1);
    overrides.set(objectName, objOverrides);
    set({ modifierOverrides: overrides });
  },

  moveModifier: (objectName, fromIndex, direction) => {
    const state = get();
    const foundEntry = findEntryByObjectName(state.blendFiles, objectName);
    const base = foundEntry
      ? foundEntry[1].file.objects.find((o) => o.name === objectName)
      : undefined;
    const baseModifiers = base?.modifiers ?? [];
    const overrides = new Map(state.modifierOverrides);
    const objOverrides = [...(overrides.get(objectName) ?? [])];

    // Build merged modifier list with userAdded tracking
    const maxLen = Math.max(baseModifiers.length, objOverrides.length);
    const entries: { mod: BlendModifier; userAdded: boolean }[] = [];
    for (let i = 0; i < maxLen; i++) {
      const baseMod = baseModifiers[i];
      const override = objOverrides[i];
      let mod = override?.runtimeModifier ?? baseMod;
      if (!mod) continue;
      if (override) {
        if (override.enabled !== undefined || override.props) {
          mod = { ...mod, ...(override.props ?? {}) } as typeof mod;
          if (override.enabled !== undefined)
            mod = { ...mod, enabled: override.enabled };
        }
      }
      entries.push({ mod, userAdded: !!override?.userAdded });
    }

    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= entries.length) return;

    // Swap
    [entries[fromIndex], entries[toIndex]] = [
      entries[toIndex],
      entries[fromIndex],
    ];

    // Materialize all as explicit overrides to preserve the new order
    const newOverrides = entries.map(({ mod, userAdded }) => ({
      enabled: mod.enabled,
      runtimeModifier: mod,
      userAdded,
    }));
    overrides.set(objectName, newOverrides);
    set({ modifierOverrides: overrides });
  },

  toggleModifierCollapsed: (objectId, modifierIndex) => {
    // Clone Map + inner Set so zustand selectors subscribed to either level
    // see a referential change and re-run.
    const next = new Map(get().modifierUICollapsed);
    const prevSet = next.get(objectId);
    const nextSet = new Set(prevSet ?? []);
    if (nextSet.has(modifierIndex)) nextSet.delete(modifierIndex);
    else nextSet.add(modifierIndex);
    next.set(objectId, nextSet);
    set({ modifierUICollapsed: next });
  },

  clearModifierCollapsed: (objectId) => {
    const prev = get().modifierUICollapsed;
    if (!prev.has(objectId)) return;
    const next = new Map(prev);
    next.delete(objectId);
    set({ modifierUICollapsed: next });
  },

  // ---------------------------------------------------------------------------
  // Modeling actions
  // ---------------------------------------------------------------------------

  addPrimitive: (kind) => {
    // Route primitives through the generator registry when one exists for
    // this kind — that gives the user a re-editable sidebar section. The
    // empty/curve/skin_armature kinds fall through to the legacy path
    // below because they are not covered by the v1 generator set.
    if (isKnownGeneratorId(kind)) {
      get().addGenerator(kind);
      return;
    }

    // Switch to object mode if not already there
    if (get().workspaceMode !== "object") {
      get().setWorkspaceMode("object");
    }
    // Mirror exitEditMode: cancel any in-flight command before introducing a
    // new object. Shared cleanup path with enterPoseMode and exitEditMode.
    if (get().commandBuffer) get().cancelCommand();
    const counter =
      [...get().sceneObjects.values()].filter((o) => o.objectKind === kind)
        .length + 1;
    const DISPLAY_NAME: Partial<
      Record<import("../modeling/types.ts").PrimitiveKind, string>
    > = {
      skin_armature: "Skin Armature",
    };
    const displayName =
      DISPLAY_NAME[kind as import("../modeling/types.ts").PrimitiveKind] ??
      (kind === "empty"
        ? "Empty"
        : kind.charAt(0).toUpperCase() + kind.slice(1));
    const spawnPos = cursorSpawnPosition(get().cursor3D);

    // Empties carry no geometry — they are transform-only helper objects.
    if (kind === "empty") {
      const emptyMatId = get().createMaterial();
      const emptyObj: SceneObject = {
        id: createSceneObjectId(),
        name: `${displayName}.${String(counter).padStart(3, "0")}`,
        objectKind: "empty",
        position: spawnPos,
        rotation: [0, 0, 0],
        scale: [1, 1, 1],
        vertices: new Float32Array(),
        indices: EMPTY_RENDER_TRIANGLE_INDICES,
        loopNormals: asLoopNormals(new Float32Array()),
        topology: {
          offsets: new Uint32Array([0]),
          corners: new Uint32Array(),
          edges: new Uint32Array(0),
          cornerEdges: new Uint32Array(0),
          edgeFlags: new Uint8Array(0),
        },
        materialSlots: createDefaultMaterialSlots(emptyMatId),
        shading: "flat",
      };
      const emptyAdd = addSceneObject(get().sceneObjects, emptyObj);
      if (!emptyAdd.admitted) return;
      clearLiveEditPreview();
      set({
        sceneObjects: emptyAdd.map,
        selectedObjectTargets: [{ kind: "sceneObject", id: emptyObj.id }],
        mode: "transform" as InteractionMode,
        editMode: null,
        activeEditSession: null,
      });
      return;
    }

    const geo = generatePrimitive(kind);
    // Blender-style per-primitive shading defaults:
    //   cube/plane/circle → flat (hard edges only, or no faces)
    //   cylinder/cone     → autoSmooth (mix of hard cap edges + smooth sides)
    //   sphere/icosphere/torus/curve/skin_armature → smooth (fully curved surfaces)
    const shading: import("../modeling/types.ts").ShadingMode =
      kind === "cube" || kind === "plane" || kind === "circle"
        ? "flat"
        : kind === "cylinder" || kind === "cone"
          ? "autoSmooth"
          : "smooth";
    const primMatId = get().createMaterial();
    const obj: SceneObject = {
      id: createSceneObjectId(),
      name: `${displayName}.${String(counter).padStart(3, "0")}`,
      objectKind: kind,
      position: spawnPos,
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      ...(() => {
        const loopNormals = recomputeLoopNormals(
          geo.vertices,
          geo.topology,
          resolveAutoSmoothAngle(shading),
        );
        return {
          vertices: geo.vertices,
          indices: geo.indices,
          loopNormals,
          topology: geo.topology,
        };
      })(),
      uvs: geo.uvs,
      materialSlots: createDefaultMaterialSlots(primMatId),
      shading,
      ...(kind === "curve" ? { curveData: createDefaultCurveData() } : {}),
    };

    // Skin Armature ships with a pre-seeded Skin modifier so the user sees a
    // tube immediately — no need to manually add the modifier.
    const modifierOverrides = new Map(get().modifierOverrides);
    if (kind === "skin_armature") {
      modifierOverrides.set(obj.id, [
        {
          enabled: true,
          runtimeModifier: MODIFIER_DEFAULTS.skin(),
          userAdded: true,
        },
      ]);
    }

    const primAdd = addSceneObject(get().sceneObjects, obj);
    if (!primAdd.admitted) return;
    clearLiveEditPreview();
    set({
      sceneObjects: primAdd.map,
      selectedObjectTargets: [{ kind: "sceneObject", id: obj.id }],
      mode: "transform" as InteractionMode,
      editMode: null,
      activeEditSession: null,
      modifierOverrides,
    });
  },
  updateCurveData: (id, patch) => {
    const o = get().sceneObjects.get(id);
    if (!o || !o.curveData) return;
    const updated = { ...o.curveData, ...patch };
    // If bevel was partially patched, merge it
    if (patch.bevel && o.curveData.bevel) {
      updated.bevel = { ...o.curveData.bevel, ...patch.bevel };
    }
    set({
      sceneObjects: regenerateCurveGeometryInMap(
        get().sceneObjects,
        id,
        updated,
      ),
    });
  },

  convertCurveToMesh: (id) => {
    const o = get().sceneObjects.get(id);
    if (!o || !o.curveData) return;
    const { curveData, ...rest } = o;
    // Bevel-disabled curves carry empty geometry; bake the spline into a
    // wire-edge polyline so the resulting mesh actually contains something.
    const baked = curveData.bevel.enabled
      ? null
      : generateCurvePolylineGeometry(curveData);
    const next = new Map(get().sceneObjects);
    next.set(id, {
      ...rest,
      objectKind: "imported" as const,
      ...(baked
        ? {
            vertices: baked.vertices,
            indices: baked.indices,
            loopNormals: baked.loopNormals,
            topology: baked.topology,
            uvs: baked.uvs,
          }
        : {}),
    });
    set({ sceneObjects: next });
  },

  removeSceneObject: (id) => {
    get().pushUndo();
    const objects = removeFromSceneMap(get().sceneObjects, id);
    const editMode = get().editMode?.objectId === id ? null : get().editMode;
    const poseMode = get().poseMode?.objectId === id ? null : get().poseMode;
    const morphs = new Map(get().mannequinMorphs);
    morphs.delete(id);
    const bonePoses = new Map(get().mannequinBonePoses);
    bonePoses.delete(id);
    const locked = new Map(get().lockedBones);
    locked.delete(id);
    const volumes = new Map(get().boneVolumes);
    volumes.delete(id);
    // Prune from all collections. Removing a member from a
    // generator-backed collection additionally bakes the collection —
    // the generator can no longer reproduce the current membership, and
    // silently re-adding a deleted child on the next param change would
    // surprise the user.
    const collections = new Map(get().collections);
    const owningCollectionIds: string[] = [];
    for (const [cid, coll] of collections) {
      if (coll.objectIds.has(id)) owningCollectionIds.push(cid);
    }
    for (const cid of owningCollectionIds) {
      removeCollectionMembers(collections, cid, [id]);
    }
    // If we cleared editMode or poseMode for the deleted object, sync workspace
    const wm = get().workspaceMode;
    const wsModePatch: Record<string, unknown> = {};
    if (!editMode && wm === "edit") wsModePatch.workspaceMode = "object";
    if (!poseMode && wm === "pose") wsModePatch.workspaceMode = "object";
    const prunedMats = pruneUnusedMaterials(get().materials, objects);
    // If the deleted object owns the active "object-create" redo-panel
    // session, dismiss the panel — the replay strategy already no-ops on
    // missing object, but proactively clearing avoids a stale panel frame.
    const lastOp = get().lastOperation;
    const lastOpPatch =
      lastOp?.capture.kind === "object-create" && lastOp.capture.objectId === id
        ? { lastOperation: null }
        : {};
    // Drop projected sketch strokes attached to the removed object.
    const sketch = pruneSceneSketch(get().sketch, {
      removedObjectIds: new Set([id]),
    });
    set({
      sceneObjects: objects,
      ...(prunedMats ? { materials: prunedMats } : {}),
      selectedObjectTargets: filterExistingSelectionTargets(
        objects,
        get().blendFiles,
        collections,
        get().selectedObjectTargets,
      ),
      editMode,
      poseMode,
      mannequinMorphs: morphs,
      mannequinBonePoses: bonePoses,
      lockedBones: locked,
      boneVolumes: volumes,
      collections,
      sketch,
      ...wsModePatch,
      ...lastOpPatch,
    });
  },

  updateSceneObjectTransform: (id, position, rotation, scale) => {
    // World-space generators (e.g. cable) own their geometry in absolute
    // coordinates and must stay pinned to the origin. Dropping the write
    // rather than patching here prevents the gizmo / drag paths from
    // desynchronising the transform from the endpoint-anchored geometry.
    const existing = get().sceneObjects.get(id);
    if (existing?.generator?.worldSpace) return;
    set({
      sceneObjects: patchSceneObject(get().sceneObjects, id, {
        position,
        rotation,
        scale,
      }),
    });
    // A generator may depend on this object via an `objectRef` param
    // (e.g. a cable whose endpoints are empties). Re-run every dependent
    // generator so the live geometry tracks the moved source. The scan
    // is O(generator-owners × refs) — trivially cheap at realistic
    // scene sizes.
    get().rebuildDependentGenerators(id);
  },

  setAllowNonPenDrawingInput: (enabled) => {
    set({ allowNonPenDrawingInput: enabled });
  },

  // --- Generator-mode operations (parametric object recipes) ---
  ...createGeneratorOps(set, get),

  // --- Edit-mode operations (selection, deletion, topology, normals) ---
  ...createEditOps(set, get),

  // --- Sculpt-mode operations (brush displacement, spatial queries) ---
  ...createSculptOps(set, get),

  // --- Sketch-mode operations (stroke capture and layer compositing) ---
  ...createSceneSketchOps(set, get),

  // --- Surface tool operations (Box, Cylinder, Poly, Inset confirms) ---
  ...createSurfaceOps(set, get, {
    pushUndo: () => get().pushUndo(),
    createMaterial: () => get().createMaterial(),
  }),

  createMaterial: (properties) => {
    const id = createMaterialId();
    const mats = new Map(get().materials);
    const name = nextMaterialName(mats);
    const block: MaterialDataBlock = {
      id,
      name,
      properties: { ...DEFAULT_MATERIAL, ...properties },
    };
    mats.set(id, block);
    set({ materials: mats });
    return id;
  },

  deleteMaterial: (materialId) => {
    const mats = new Map(get().materials);
    if (!mats.has(materialId)) return;
    mats.delete(materialId);

    // Reassign orphaned slots to the first remaining material, or create
    // one only if the registry is now completely empty.
    let sceneObjects = get().sceneObjects;
    let fallbackId: string | null = null;
    const resolveFallback = (): string => {
      if (fallbackId) return fallbackId;
      const first = mats.values().next().value;
      if (first) {
        fallbackId = first.id;
      } else {
        const fbId = createMaterialId();
        const fbName = nextMaterialName(mats);
        mats.set(fbId, {
          id: fbId,
          name: fbName,
          properties: { ...DEFAULT_MATERIAL },
        });
        fallbackId = fbId;
      }
      return fallbackId;
    };
    for (const obj of sceneObjects.values()) {
      let dirty = false;
      const nextSlots = obj.materialSlots.map((slot) => {
        if (slot.materialId !== materialId) return slot;
        dirty = true;
        return { ...slot, materialId: resolveFallback() };
      });
      if (dirty) {
        sceneObjects = patchSceneObject(sceneObjects, obj.id, {
          materialSlots: nextSlots,
        });
      }
    }
    set({ materials: mats, sceneObjects });
  },

  renameMaterial: (materialId, name) => {
    const block = get().materials.get(materialId);
    if (!block) return;
    const mats = new Map(get().materials);
    mats.set(materialId, { ...block, name });
    set({ materials: mats });
  },

  updateMaterial: (materialId, patch) => {
    const state = get();
    const block = state.materials.get(materialId);
    if (!block) return;
    set(propagateMaterialEdit(state, block, patch));
  },

  setMaterialFakeUser: (materialId, fakeUser) => {
    const block = get().materials.get(materialId);
    if (!block || block.fakeUser === fakeUser) return;
    const mats = new Map(get().materials);
    mats.set(materialId, { ...block, fakeUser });
    // Clearing the flag makes this material eligible for pruning if it
    // currently has no users.
    if (!fakeUser) {
      const pruned = pruneUnusedMaterials(mats, get().sceneObjects);
      set({ materials: pruned ?? mats });
    } else {
      set({ materials: mats });
    }
  },

  setMaterialSlotMaterial: (objectId, slotId, materialId) => {
    const state = get();
    const obj = state.sceneObjects.get(objectId);
    if (!obj || !state.materials.has(materialId)) return;
    const idx = obj.materialSlots.findIndex((s) => s.id === slotId);
    if (idx < 0) return;
    if (obj.materialSlots[idx].materialId === materialId) return;
    const nextSlots = obj.materialSlots.slice();
    nextSlots[idx] = { ...nextSlots[idx], materialId };
    const nextSceneObjects = patchSceneObject(state.sceneObjects, objectId, {
      materialSlots: nextSlots,
    });
    const prunedMats = pruneUnusedMaterials(state.materials, nextSceneObjects);
    set({
      sceneObjects: nextSceneObjects,
      ...(prunedMats ? { materials: prunedMats } : {}),
    });
  },

  makeUniqueMaterialForSlot: (objectId, slotId) => {
    const obj = get().sceneObjects.get(objectId);
    if (!obj) return "";
    const slot = obj.materialSlots.find((s) => s.id === slotId);
    if (!slot) return "";
    const block = get().materials.get(slot.materialId);
    if (!block) return slot.materialId;

    // Count users across all slots on all objects.
    let userCount = 0;
    for (const o of get().sceneObjects.values()) {
      for (const s of o.materialSlots) {
        if (s.materialId === slot.materialId) userCount++;
      }
    }
    if (userCount <= 1) return slot.materialId;

    // Clone into a new block. The new block intentionally drops libraryId —
    // "make unique" means break all links, including the library link.
    const newId = createMaterialId();
    const mats = new Map(get().materials);
    const newName = nextMaterialName(mats, block.name);
    mats.set(newId, {
      id: newId,
      name: newName,
      properties: { ...block.properties },
    });
    const nextSlots = obj.materialSlots.slice();
    const idx = nextSlots.findIndex((s) => s.id === slotId);
    nextSlots[idx] = { ...nextSlots[idx], materialId: newId };
    set({
      materials: mats,
      sceneObjects: patchSceneObject(get().sceneObjects, objectId, {
        materialSlots: nextSlots,
      }),
    });
    return newId;
  },

  // --- Material library (cross-scene, cloud-backed via libraryStore) ---

  exportMaterialToLibrary: async (materialId) => {
    const state = get();
    const block = state.materials.get(materialId);
    if (!block) return null;

    const item = await exportMaterialToCloudLibrary(
      block.name,
      block.properties,
    );
    if (!item) return null;
    return item.id;
  },

  importMaterialFromLibrary: (libraryId) => {
    // The library item metadata lives in libraryStore; the actual
    // SceneMaterial comes from the cache (lazy-fetched from R2).
    const libItem = useLibraryStore
      .getState()
      .items.find((i) => i.id === libraryId);
    if (!libItem || libItem.type !== "material") return "";
    const properties = getCachedLibraryMaterial(libraryId);
    if (!properties) return "";

    const newId = createMaterialId();
    const mats = new Map(get().materials);
    const name = nextMaterialName(mats, libItem.name);
    mats.set(newId, { id: newId, name, properties: { ...properties } });
    set({ materials: mats });
    return newId;
  },

  setSceneObjectShading: (id, shading) => {
    const obj = get().sceneObjects.get(id);
    if (!obj) return;
    const angle = resolveAutoSmoothAngle(shading, obj.autoSmoothAngle);
    const loopNormals = recomputeLoopNormals(obj.vertices, obj.topology, angle);
    const patch: Partial<SceneObject> = { shading, loopNormals };
    set({ sceneObjects: patchSceneObject(get().sceneObjects, id, patch) });
  },

  setSceneObjectAutoSmoothAngle: (id, angle) => {
    const obj = get().sceneObjects.get(id);
    if (!obj) return;
    const loopNormals = recomputeLoopNormals(
      obj.vertices,
      obj.topology,
      resolveAutoSmoothAngle("autoSmooth", angle),
    );
    const patch: Partial<SceneObject> = { autoSmoothAngle: angle, loopNormals };
    set({ sceneObjects: patchSceneObject(get().sceneObjects, id, patch) });
  },

  setPivotReference: (ref) => {
    set({ pivotReference: ref });
  },
  setTransformOrientation: (o) => {
    set({ transformOrientation: o });
  },
  setProportionalEnabled: (enabled) => set({ proportionalEnabled: enabled }),
  setProportionalRadius: (radius) => set({ proportionalRadius: radius }),
  setProportionalFalloff: (falloff) => set({ proportionalFalloff: falloff }),
  setBrushSelectRadius: (radius) => set({ brushSelectRadius: radius }),
  setSnapEnabled: (enabled) => set({ snapEnabled: enabled }),
  setSnapTarget: (target) => set({ snapTarget: target }),
  setSnapWithRotation: (enabled) => set({ snapWithRotation: enabled }),
  setSurfaceProjectIndividual: (enabled) =>
    set({ surfaceProjectIndividual: enabled }),

  // ---------------------------------------------------------------------------
  // 3D Cursor
  // ---------------------------------------------------------------------------

  setCursor3DPosition: (position) => {
    set({
      cursor3D: {
        ...get().cursor3D,
        position: [position[0], position[1], position[2]],
      },
    });
  },
  setCursor3DSelected: (selected) => {
    const c = get().cursor3D;
    if (c.selected === selected) return;
    // Cursor selection is mutually exclusive with object / edit / pose selection.
    // Clearing other selection sets centralizes the invariant here so every
    // downstream consumer (gizmo, sidebar, command dispatch) can treat
    // cursor3D.selected as the single source of truth.
    if (selected) {
      const patch: Partial<AppState> = {
        cursor3D: { ...c, selected: true },
      };
      if (get().selectedObjectTargets.length > 0)
        patch.selectedObjectTargets = [];
      const pm = get().poseMode;
      if (pm && (pm.selectedBones.size > 0 || pm.activeBone !== null)) {
        patch.poseMode = { ...pm, selectedBones: new Set(), activeBone: null };
      }
      // Leave editMode untouched — selecting the cursor while editing should
      // not tear down the edit session. Both states coexist: the cursor is
      // the active manipulation target; resume vertex editing by tapping
      // anywhere in the mesh.
      if (get().mode === "navigate")
        patch.mode = "transform" as InteractionMode;
      set(patch);
    } else {
      set({ cursor3D: { ...c, selected: false } });
    }
  },

  snapCursorTo: (kind) => {
    const state = get();
    const cursor = state.cursor3D;
    let next: [number, number, number] | null = null;

    switch (kind) {
      case "worldOrigin": {
        next = [0, 0, 0];
        break;
      }
      case "grid": {
        next = snapCursorToGridPoint(cursor.position);
        break;
      }
      case "selection": {
        next = getSelectionWorldCenter(state);
        break;
      }
      case "selectionActive": {
        // First item in selection
        const pm = state.poseMode;
        if (pm?.activeBone) {
          const bone = getBone(pm.objectId, pm.activeBone);
          if (bone) {
            const world = new THREE.Vector3();
            bone.getWorldPosition(world);
            next = [world.x, world.y, world.z];
            break;
          }
        }
        const em = state.editMode;
        if (em && hasVertexSelection(em)) {
          const obj = state.sceneObjects.get(em.objectId);
          if (obj) {
            const first = [...em.selectedVertices][0];
            const lx = obj.vertices[first * 3];
            const ly = obj.vertices[first * 3 + 1];
            const lz = obj.vertices[first * 3 + 2];
            const mat = new THREE.Matrix4().compose(
              new THREE.Vector3(...obj.position),
              new THREE.Quaternion().setFromEuler(
                new THREE.Euler(...obj.rotation),
              ),
              new THREE.Vector3(...obj.scale),
            );
            const world = new THREE.Vector3(lx, ly, lz).applyMatrix4(mat);
            next = [world.x, world.y, world.z];
            break;
          }
        }
        const firstTarget = state.selectedObjectTargets[0];
        if (firstTarget) {
          next = selectionTargetWorldPosition(state, firstTarget);
        }
        break;
      }
      case "selectionFace": {
        const em = state.editMode;
        if (em) {
          const obj = state.sceneObjects.get(em.objectId);
          if (obj)
            next = editFaceSelectionWorldMedian(
              obj,
              em,
              getCollectionForObject(state.collections, em.objectId)?.transform,
            );
        }
        break;
      }
      case "activeBone": {
        const pm = state.poseMode;
        if (pm?.activeBone) {
          const bone = getBone(pm.objectId, pm.activeBone);
          if (bone) {
            const world = new THREE.Vector3();
            bone.getWorldPosition(world);
            next = [world.x, world.y, world.z];
          }
        }
        break;
      }
    }

    if (!next) return;
    get().pushUndo();
    set({ cursor3D: { ...get().cursor3D, position: next } });
  },

  snapSelectionToCursor: (kind) => {
    const state = get();
    const cursor = state.cursor3D.position;

    // Edit mode: write to vertices of the edited object.
    const em = state.editMode;
    if (em) {
      const obj = state.sceneObjects.get(em.objectId);
      if (!obj) return;
      get().pushUndo();
      let updated: SceneObject;
      const collectionTransform = getCollectionForObject(
        state.collections,
        obj.id,
      )?.transform;
      if (kind === "selection" && em.granularity === "vertex") {
        updated = snapVerticesToCursor(obj, em, cursor, collectionTransform);
      } else {
        // Edge / face / offset → preserve shape, translate median to cursor.
        updated = snapSelectionMedianToCursor(
          obj,
          em,
          cursor,
          collectionTransform,
        );
      }
      if (updated !== obj) {
        set({
          sceneObjects: patchSceneObject(get().sceneObjects, obj.id, {
            vertices: updated.vertices,
            loopNormals: updated.loopNormals,
          }),
        });
      }
      return;
    }

    // Pose mode is intentionally not wired here — Blender itself doesn't
    // snap bones to the cursor, and doing so would bypass the IK solver.

    // Object mode: either move selected elements to the cursor, or move their
    // origins onto the cursor while preserving world-space geometry.
    if (state.selectedObjectTargets.length === 0) return;
    if (kind === "origin") {
      const selectedLeafIds = selectionToLeafObjectIds(
        state.blendFiles,
        state.collections,
        state.selectedObjectTargets,
      );
      if (selectedLeafIds.size === 0) return;

      get().pushUndo();
      let nextSceneObjects = state.sceneObjects;
      let changed = false;
      for (const id of selectedLeafIds) {
        const obj = nextSceneObjects.get(id);
        if (!obj) continue;
        const patch = originToPositionPatch(obj, cursor);
        if (!patch) continue;
        nextSceneObjects = patchSceneObject(nextSceneObjects, id, patch);
        changed = true;
      }
      if (changed) set({ sceneObjects: nextSceneObjects });
      return;
    }

    get().pushUndo();
    const nextSceneObjects = new Map(state.sceneObjects);
    const nextCollections = new Map(state.collections);
    const nextBlendFiles = new Map(state.blendFiles);

    const currentPositions = state.selectedObjectTargets
      .map((target) => {
        const position = selectionTargetWorldPosition(state, target);
        return position ? { target, position } : null;
      })
      .filter(
        (
          entry,
        ): entry is {
          target: ObjectSelectionTarget;
          position: [number, number, number];
        } => entry != null,
      );

    if (currentPositions.length === 0) return;

    const median =
      kind === "offset"
        ? medianPoint(currentPositions.map((entry) => entry.position))
        : null;
    const dx = median ? cursor[0] - median[0] : 0;
    const dy = median ? cursor[1] - median[1] : 0;
    const dz = median ? cursor[2] - median[2] : 0;

    for (const entry of currentPositions) {
      const nextPosition: [number, number, number] =
        kind === "offset"
          ? [
              entry.position[0] + dx,
              entry.position[1] + dy,
              entry.position[2] + dz,
            ]
          : [cursor[0], cursor[1], cursor[2]];

      if (entry.target.kind === "sceneObject") {
        const obj = nextSceneObjects.get(entry.target.id);
        if (!obj) continue;
        const worldTransform = sceneObjectWorldTransform(
          { collections: nextCollections },
          obj,
        );
        const updatedObjects = patchSceneObjectWorldTransform(
          { sceneObjects: nextSceneObjects, collections: nextCollections },
          entry.target.id,
          {
            position: nextPosition,
            rotation: worldTransform.rotation,
            scale: worldTransform.scale,
          },
        );
        nextSceneObjects.clear();
        for (const [id, updated] of updatedObjects)
          nextSceneObjects.set(id, updated);
        continue;
      }

      if (entry.target.kind === "collection") {
        const collection = nextCollections.get(entry.target.id);
        if (!collection) continue;
        nextCollections.set(entry.target.id, {
          ...collection,
          transform: {
            ...collection.transform,
            position: nextPosition,
          },
        });
        continue;
      }

      const blendEntry = nextBlendFiles.get(entry.target.id);
      if (!blendEntry) continue;
      nextBlendFiles.set(entry.target.id, {
        ...blendEntry,
        groupTransform: {
          ...blendEntry.groupTransform,
          position: nextPosition,
        },
      });
    }

    set({
      sceneObjects: nextSceneObjects,
      collections: nextCollections,
      blendFiles: nextBlendFiles,
    });
  },

  // --- Object-mode operations (transform, origin, clipboard, library) ---
  ...createObjectOps(set, get),

  selectAll: () => {
    const state = get();
    const inEditMode = state.mode === "edit" && state.editMode != null;

    get().pushUndo();

    if (inEditMode) {
      const em = state.editMode!;
      const obj = state.sceneObjects.get(em.objectId);
      if (!obj) return;

      const topology = obj.topology;
      const vertexCount = obj.vertices.length / 3;
      const selectedVertices = new Set<number>();
      const selectedEdges = new Set<number>();
      const selectedFaces = new Set<number>();

      if (em.granularity === "face") {
        const faceCount = topologyFaceCount(topology);
        for (let f = 0; f < faceCount; f++) {
          if (em.hiddenFaces.has(f)) continue;
          selectedFaces.add(f);
          for (const vi of topologyFaceVerts(topology, f)) {
            if (!em.hiddenVertices.has(vi)) selectedVertices.add(vi);
          }
        }
      } else if (em.granularity === "edge") {
        const edgeCount = topology.edges.length / 2;
        const faceCount = topologyFaceCount(topology);
        const edgeFaceCounts = new Uint32Array(edgeCount);
        const hiddenEdgeFaceCounts = new Uint32Array(edgeCount);
        for (let f = 0; f < faceCount; f++) {
          const start = topology.offsets[f];
          const end = topology.offsets[f + 1];
          for (let c = start; c < end; c++) {
            const edgeIndex = topology.cornerEdges[c];
            edgeFaceCounts[edgeIndex]++;
            if (em.hiddenFaces.has(f)) hiddenEdgeFaceCounts[edgeIndex]++;
          }
        }
        for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex++) {
          const a = topology.edges[edgeIndex * 2];
          const b = topology.edges[edgeIndex * 2 + 1];
          if (em.hiddenVertices.has(a) || em.hiddenVertices.has(b)) continue;
          if (
            edgeFaceCounts[edgeIndex] > 0 &&
            edgeFaceCounts[edgeIndex] === hiddenEdgeFaceCounts[edgeIndex]
          ) {
            continue;
          }
          selectedEdges.add(edgeIndex);
          selectedVertices.add(a);
          selectedVertices.add(b);
        }
      } else {
        for (let i = 0; i < vertexCount; i++) {
          if (!em.hiddenVertices.has(i)) selectedVertices.add(i);
        }
      }

      set({
        editMode: {
          ...em,
          selectedVertices,
          selectedEdges,
          selectedFaces,
        },
      });
    } else {
      // Select all visible objects via ECS
      const registry = getRegistry();
      const visibleIds = new Set<string>();
      for (const [, entity] of registry) {
        const vis = entity.components.visibility;
        if (!vis || vis.visible) {
          visibleIds.add(entityIdToStoreKey(entity.id));
        }
      }
      if (visibleIds.size > 0) {
        set({
          ...withSelectedTargets(
            [...visibleIds].map((id) =>
              resolveStoreKeySelectionTarget(
                state.blendFiles,
                state.collections,
                id,
              ),
            ),
          ),
          mode: "transform" as InteractionMode,
        });
      }
    }
  },

  invertSelection: () => {
    const state = get();
    const inEditMode = state.mode === "edit" && state.editMode != null;

    get().pushUndo();

    if (inEditMode) {
      const em = state.editMode!;
      const obj = state.sceneObjects.get(em.objectId);
      if (!obj) return;
      const sel = em.selectedVertices;
      const inverted = new Set<number>();

      if (em.granularity === "face" && obj.topology) {
        // Invert the face set directly, then derive vertices from it
        const faceCount = topologyFaceCount(obj.topology);
        const invertedFaces = new Set<number>();
        for (let f = 0; f < faceCount; f++) {
          if (!em.selectedFaces.has(f) && !em.hiddenFaces.has(f))
            invertedFaces.add(f);
        }
        for (const f of invertedFaces) {
          const fv = topologyFaceVerts(obj.topology, f);
          for (const v of fv) inverted.add(v);
        }
        set({
          editMode: {
            ...em,
            selectedVertices: inverted,
            selectedEdges: new Set(),
            selectedFaces: invertedFaces,
          },
        });
      } else if (em.granularity === "edge" && obj.topology) {
        const { edges } = obj.topology;
        const edgeCount = edges.length / 2;
        const invertedEdges = new Set<number>();
        for (let i = 0; i < edgeCount; i++) {
          const a = edges[i * 2],
            b = edges[i * 2 + 1];
          if (em.hiddenVertices.has(a) || em.hiddenVertices.has(b)) continue;
          if (!em.selectedEdges.has(i)) {
            invertedEdges.add(i);
            inverted.add(a);
            inverted.add(b);
          }
        }
        set({
          editMode: {
            ...em,
            selectedVertices: inverted,
            selectedEdges: invertedEdges,
            selectedFaces: new Set(),
          },
        });
      } else {
        const vertexCount = obj.vertices.length / 3;
        for (let i = 0; i < vertexCount; i++) {
          if (!sel.has(i) && !em.hiddenVertices.has(i)) inverted.add(i);
        }
        set({
          editMode: {
            ...em,
            selectedVertices: inverted,
            selectedEdges: new Set(),
            selectedFaces: new Set(),
          },
        });
      }
    } else {
      const registry = getRegistry();
      const prevLeafIds = selectionToLeafObjectIds(
        state.blendFiles,
        state.collections,
        state.selectedObjectTargets,
      );
      const inverted = new Set<string>();
      for (const [, entity] of registry) {
        const vis = entity.components.visibility;
        if (!vis || vis.visible) {
          const key = entityIdToStoreKey(entity.id);
          if (!prevLeafIds.has(key)) inverted.add(key);
        }
      }
      const patch: Partial<AppState> = {
        ...withSelectedTargets(
          [...inverted].map((id) =>
            resolveStoreKeySelectionTarget(
              state.blendFiles,
              state.collections,
              id,
            ),
          ),
        ),
      };
      if (
        patch.selectedObjectTargets &&
        patch.selectedObjectTargets.length > 0
      ) {
        patch.mode = "transform" as InteractionMode;
      } else if (state.mode === "transform") {
        patch.mode = "navigate" as InteractionMode;
      }
      set(patch);
    }
  },

  checkerDeselect: () => {
    const state = get();
    if (state.mode !== "edit" || state.editMode == null) return;

    const em = state.editMode;
    const obj = state.sceneObjects.get(em.objectId);
    if (!obj) return;

    const next = checkerDeselectEditSelection(
      obj.topology,
      obj.vertices.length / 3,
      em,
    );
    if (!next) return;

    get().pushUndo();
    set({ editMode: { ...em, ...next } });
  },

  clearSelection: () => {
    const state = get();
    const inEditMode = state.mode === "edit" && state.editMode != null;

    let patch: Partial<AppState>;
    if (inEditMode) {
      const em = state.editMode!;
      patch = {
        editMode: {
          ...em,
          selectedVertices: new Set(),
          selectedEdges: new Set(),
          selectedFaces: new Set(),
        },
      };
      // Capture current selection for recall only if there is something to save.
      if (hasAnySelection(em)) {
        patch.lastEditModeSelection = captureEditSelectionForRecall(em);
      }
    } else {
      patch = {
        selectedObjectTargets: [],
      };
      if (state.selectedObjectTargets.length > 0) {
        patch.lastObjectModeSelection = selectionToLeafObjectIds(
          state.blendFiles,
          state.collections,
          state.selectedObjectTargets,
        );
      }
      if (state.mode === "transform")
        patch.mode = "navigate" as InteractionMode;
    }

    const before = captureSelectionDeltaState(state);
    const after = captureSelectionDeltaState({
      ...state,
      ...patch,
    } as AppState);
    pushSelectionUndo(before, after);
    set({ canUndo: undoManager.canUndo, canRedo: undoManager.canRedo });
    set(patch);
  },

  recallSelection: () => {
    const state = get();
    const inEditMode = state.mode === "edit" && state.editMode != null;

    if (inEditMode) {
      const last = state.lastEditModeSelection;
      const em = state.editMode!;
      if (!last || last.objectId !== em.objectId) return;
      get().pushUndo();
      set({
        editMode: {
          ...em,
          selectedVertices: new Set(last.selectedVertices),
          selectedEdges: new Set(last.selectedEdges),
          selectedFaces: new Set(last.selectedFaces),
          granularity: last.granularity,
        },
      });
    } else {
      const last = state.lastObjectModeSelection;
      if (!last || last.size === 0) return;
      get().pushUndo();
      set({
        ...withSelectedTargets(
          [...last].map((id) =>
            resolveStoreKeySelectionTarget(
              state.blendFiles,
              state.collections,
              id,
            ),
          ),
        ),
        mode: "transform" as InteractionMode,
      });
    }
  },

  randomSelect: (coverage: number) => {
    get().pushUndo();
    const state = get();
    const ratio = Math.max(0, Math.min(1, coverage));
    const inEditMode = state.mode === "edit" && state.editMode != null;

    if (inEditMode) {
      const em = state.editMode!;
      const obj = state.sceneObjects.get(em.objectId);
      if (!obj) return;
      const checkerVerts = new Set<number>();
      const checkerEdges = new Set<number>();
      const checkerFaces = new Set<number>();

      if (em.granularity === "face" && obj.topology) {
        const faceCount = obj.topology.offsets.length - 1;
        for (let f = 0; f < faceCount; f++) {
          if (Math.random() < ratio) {
            checkerFaces.add(f);
            const fv = topologyFaceVerts(obj.topology, f);
            for (const v of fv) checkerVerts.add(v);
          }
        }
      } else if (em.granularity === "edge" && obj.topology) {
        const { edges } = obj.topology;
        const edgeCount = edges.length / 2;
        for (let i = 0; i < edgeCount; i++) {
          if (Math.random() < ratio) {
            checkerEdges.add(i);
            checkerVerts.add(edges[i * 2]);
            checkerVerts.add(edges[i * 2 + 1]);
          }
        }
      } else {
        const vertexCount = obj.vertices.length / 3;
        for (let i = 0; i < vertexCount; i++) {
          if (Math.random() < ratio) checkerVerts.add(i);
        }
      }
      set({
        editMode: {
          ...em,
          selectedVertices: checkerVerts,
          selectedEdges: checkerEdges,
          selectedFaces: checkerFaces,
        },
      });
    } else {
      const registry = getRegistry();
      const visibleIds: string[] = [];
      for (const [, entity] of registry) {
        const vis = entity.components.visibility;
        if (!vis || vis.visible) visibleIds.push(entityIdToStoreKey(entity.id));
      }
      const selected = new Set<string>();
      for (const id of visibleIds) {
        if (Math.random() < ratio) selected.add(id);
      }
      const patch: Partial<AppState> = {
        ...withSelectedTargets(
          [...selected].map((id) =>
            resolveStoreKeySelectionTarget(
              state.blendFiles,
              state.collections,
              id,
            ),
          ),
        ),
      };
      if (patch.selectedObjectTargets && patch.selectedObjectTargets.length > 0)
        patch.mode = "transform" as InteractionMode;
      else if (state.mode === "transform")
        patch.mode = "navigate" as InteractionMode;
      set(patch);
    }
  },

  selectMore: () => {
    const state = get();
    if (state.mode !== "edit" || !state.editMode) return;
    const em = state.editMode;
    const obj = state.sceneObjects.get(em.objectId);
    if (!obj?.topology) return;
    const next = growEditSelection(obj.topology, obj.vertices.length / 3, em);
    if (!next) return;
    get().pushUndo();
    set({ editMode: { ...em, ...next } });
  },

  selectLess: () => {
    const state = get();
    if (state.mode !== "edit" || !state.editMode) return;
    const em = state.editMode;
    const obj = state.sceneObjects.get(em.objectId);
    if (!obj?.topology) return;
    const next = shrinkEditSelection(obj.topology, obj.vertices.length / 3, em);
    if (!next) return;
    get().pushUndo();
    set({ editMode: { ...em, ...next } });
  },

  focusSelection: () => {
    const state = get();
    if (state.selectedObjectTargets.length === 0) return;
    const leafIds = selectionToLeafObjectIds(
      state.blendFiles,
      state.collections,
      state.selectedObjectTargets,
    );
    if (leafIds.size === 0) return;

    // Compute center from actual geometry bounding boxes (post-modifier),
    // falling back to object position when no mesh is available.
    const computeCenter = window.__poseref_computeSelectionCenter;
    let center: [number, number, number] | null = null;
    if (computeCenter) {
      center = computeCenter(leafIds);
    }

    if (!center) {
      // Fallback: average object positions via ECS
      center = computeSelectedCentroid(leafIds, getRegistry());
      if (!center) return;
    }

    window.__poseref_cameraController?.applyState({ target: center });
    // Shadow store is updated via the coordinator, which reads the just-
    // applied live target and writes position/target/up atomically.
    flushCameraSync();
  },

  // --- Command buffer operations (G/R/S numeric input, live transform preview) ---
  ...createCommandOps(set, get),

  // --- Scene file operations (new, save, load, import) ---
  ...createSceneFileOps(set, get),
}));

// ---------------------------------------------------------------------------
// Session restore — recover last state on page load
// ---------------------------------------------------------------------------

// Track whether initialization is still in progress.  While true the
// subscriber skips dirty-marking so that hydration / session-restore
// don't immediately flag the scene as modified.
let _initializing = true;

// Session data is saved to localStorage as a backup but is NOT
// auto-restored. The cloud library is the source of truth for scenes.
// To recover from a crash, the user can manually restore from localStorage
// via the hamburger menu.
queueMicrotask(() => {
  _initializing = false;
  useStore.setState({ sceneDirty: false });
});

// Auto-save scene state on changes (debounced)
// ---------------------------------------------------------------------------
// Autosave scheduler — unified 1 s debounce.
//
// Two independent triggers feed the same debounced timer, so bursts of
// activity still collapse into a single IndexedDB write:
//
//   1. State-change subscription (below) — reacts to any mutation of a
//      persisted field. Covers passive changes such as drag-commit writes
//      into `sceneObjects` and gizmo-driven vertex updates.
//
//   2. `undoManager.setPushListener(scheduleAutosave)` — fires the instant
//      a snapshot-history-affecting command begins (operator entry,
//      transform start, mesh op). This protects against crashes landing
//      mid-operator: without this, a long-running op with no frame-by-frame
//      state churn could execute to completion with zero autosaves queued.
//
// Both the snapshot capture and the heavy encode/JSON/IDB work run off the
// main thread via the persistence worker; only the thin scheduling and
// field-equality check happen here.
// ---------------------------------------------------------------------------

let saveTimer: ReturnType<typeof setTimeout> | null = null;

function writeAutosave(): void {
  const current = useStore.getState();
  const firstEntry = [...current.blendFiles.values()][0];
  if (firstEntry) saveScene(firstEntry.fileName, current);
  saveSessionState(current, getCurrentLibraryItemId());
}

/**
 * Schedule a debounced autosave. Called from the state-change subscriber
 * and from `undoManager`'s push listener. Safe to call as often as desired;
 * the timer is reset on each call and only fires once per idle window.
 */
function scheduleAutosave(): void {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    saveTimer = null;
    writeAutosave();
  }, 1000);
}

/**
 * Flush any pending debounced save. Fires async IndexedDB writes without
 * awaiting — in a beforeunload handler we can't block on a promise, but
 * IndexedDB transactions started before unload will typically complete.
 */
export function flushPendingSave(): void {
  if (!saveTimer) return;
  clearTimeout(saveTimer);
  saveTimer = null;
  writeAutosave();
}

// Ensure pending IndexedDB save is flushed when the page unloads.
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", flushPendingSave);
}

// Command-begin trigger. Every snapshot-history-affecting op routes
// through `undoManager.push()` — hooking that chokepoint captures all
// operator entries, transform starts, and selection deltas without
// touching any individual op call site.
undoManager.setPushListener(scheduleAutosave);

useStore.subscribe((state, prev) => {
  // Dirty tracking for .pr save — only properties that are actually
  // persisted in the scene file format.  Non-persisted UI state
  // (showFrustum, favoriteOperators, canUndo/canRedo) is excluded to
  // avoid phantom dirty flags.
  const stateChanged =
    state.shapeKeyWeights !== prev.shapeKeyWeights ||
    state.visibility !== prev.visibility ||
    state.renderVisibility !== prev.renderVisibility ||
    state.blendFiles !== prev.blendFiles ||
    state.focalLength !== prev.focalLength ||
    state.cameraBookmarks !== prev.cameraBookmarks ||
    state.sceneObjects !== prev.sceneObjects ||
    state.materials !== prev.materials ||
    state.referenceLayers !== prev.referenceLayers ||
    state.renderConfigs !== prev.renderConfigs ||
    state.showTextures !== prev.showTextures ||
    state.textureMapsEnabled !== prev.textureMapsEnabled ||
    state.objectXray !== prev.objectXray ||
    state.showGrid !== prev.showGrid ||
    state.shadingOverrides !== prev.shadingOverrides ||
    state.favoriteShapeKeys !== prev.favoriteShapeKeys ||
    state.modifierOverrides !== prev.modifierOverrides ||
    state.pivotReference !== prev.pivotReference ||
    state.proportionalEnabled !== prev.proportionalEnabled ||
    state.proportionalRadius !== prev.proportionalRadius ||
    state.proportionalFalloff !== prev.proportionalFalloff ||
    state.boneVolumes !== prev.boneVolumes ||
    state.mannequinBonePoses !== prev.mannequinBonePoses ||
    state.mannequinMorphs !== prev.mannequinMorphs ||
    state.renderWidth !== prev.renderWidth ||
    state.renderHeight !== prev.renderHeight;

  if (stateChanged) {
    // Mark scene as dirty for .pr save tracking.
    // Skip during initialization, active scene loads (which create new
    // Map/Set references without content changes), and when sceneDirty
    // was just cleared (save/load/newScene transition).
    if (
      !_initializing &&
      !isDirtyTrackingSuppressed() &&
      !state.sceneDirty &&
      !prev.sceneDirty
    ) {
      useStore.setState({ sceneDirty: true });
    }

    // Debounced auto-save to IndexedDB (backup only — not the source of truth).
    // sceneDirty is NOT cleared here; it's only cleared by explicit file saves.
    scheduleAutosave();
  }
});
